/**
 * Accounts screen — the AVOS Books financial workspace.
 *
 * An eight-tab console surface that overlays financials on the existing AVOS
 * reservations register and rolls the portfolio's books up into an owner-ready
 * picture. Nothing here creates bookings; invoices, payments, expenses, cash
 * movements, vouchers, sales-team payroll and owner statements are financial
 * overlays on data that already exists.
 *
 * Tabs, in order:
 *   Overview · Stays & Invoices · Expenses · Cash & Banks · Sales / Vouchers ·
 *   Sales team · Owner Statements · Accounts / Ledger
 *
 * A property selector in the header scopes each tab to a single property (or
 * the whole portfolio). Each tab lazy-loads its own data in an effect keyed on
 * (activeTab, propertyId, refreshToken, period).
 *
 * No permanent data-entry forms live on list/reporting pages: every add/create/
 * edit action sits behind a "+ Add" CTA that opens a right-side drawer (long
 * forms) or a modal (short forms).
 *
 * Web boundary: all backend access goes through the fetch-based `../services.js`
 * layer (no backend runtime import). Money arrives as integer-minor STRINGS and
 * is shown via `formatMoney(Number(x), currency)`; decimal inputs become minor
 * units through the shared `parseMoneyToMinor`.
 *
 * SSR-safe: the initial render is a pure loading state; every network call runs
 * inside an effect or an event handler, and there is no `window`/`document`/
 * `Date` read at module or render top-level (the `formatDate` helper only runs
 * on already-string dates during render and constructs no clock value; the
 * period selector and voucher checker read `new Date()` only inside handlers).
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactElement,
} from "react";
import {
  UserMessage,
  toUserMessage,
  type UserMessageModel,
} from "../../components/UserMessage.js";
import { MoneyValue } from "../components/MoneyValue.js";
import { RevenueChannelBars } from "../charts/RevenueChannelBars.js";
import { RevenueStreamDonut } from "../charts/RevenueStreamDonut.js";
import { IncomeExpenseTrend, type TrendBucket } from "../charts/IncomeExpenseTrend.js";
import {
  apiAccountsAddPayroll,
  apiAccountsAdjustment,
  apiAccountsBankAccountDetail,
  apiAccountsBankAccounts,
  apiAccountsBankTransfer,
  apiAccountsBankWithdrawal,
  apiAccountsCashBanksOverview,
  apiAccountsCashCount,
  apiAccountsCashDeposit,
  apiAccountsCashInHand,
  apiAccountsCashbook,
  apiAccountsChart,
  apiAccountsCheckVoucher,
  apiAccountsCreateBankAccount,
  apiAccountsCreateExpense,
  apiAccountsExpenses,
  apiAccountsMovements,
  apiAccountsOpeningBalance,
  apiAccountsReverseMovement,
  apiAccountsUpdateBankAccount,
  apiAccountsInvoiceDelete,
  apiAccountsInvoiceDetail,
  apiAccountsInvoiceEmail,
  apiAccountsInvoiceLifecycle,
  apiAccountsInvoicePdf,
  apiAccountsInvoiceWhatsApp,
  apiAccountsOverview,
  apiAccountsOwnerStatements,
  apiAccountsPayroll,
  apiAccountsProperties,
  apiAccountsRecordPayment,
  apiAccountsSetPayrollActive,
  apiAccountsStaffSummary,
  apiAccountsPayrollPreview,
  apiAccountsRunPayroll,
  apiAccountsPayrollRuns,
  apiAccountsPayrollRunLines,
  apiAccountsPayPayroll,
  apiAccountsPayslips,
  apiAccountsPayslipDetail,
  apiAccountsPayslipPdf,
  apiAccountsPayslipEmail,
  apiAccountsPayslipWhatsApp,
  apiAccountsSettings,
  apiAccountsStays,
  apiAccountsUpdateSettings,
  apiAccountsUpsertInvoice,
  apiAccountsUpsertVoucher,
  apiAccountsSetVoucherActive,
  apiAccountsArchiveVoucher,
  apiAccountsVoucherRedemptions,
  apiAccountsVouchers,
  asError,
  formatMoney,
  parseMoneyToMinor,
  type AccountsBankAccountDetail,
  type AccountsBankAccountRow,
  type AccountsBankTxnRow,
  type AccountsCashBanksOverview,
  type AccountsCashInHandRow,
  type AccountsCashbookRow,
  type AccountsChartGroup,
  type AccountsExpenseRow,
  type AccountsMovementRow,
  type AccountsInvoiceDetail,
  type AccountsInvoicePayment,
  type AccountsOverviewData,
  type AccountsOwnerStatementRow,
  type AccountsPayrollRow,
  type AccountsPropertySettings,
  type AccountsStaffSummary,
  type AccountsPayrollLine,
  type AccountsPayrollRun,
  type AccountsPayslipRow,
  type AccountsPayslipDetail,
  type AccountsEmploymentType,
  type AccountsCommissionType,
  type AccountsStaffPaymentMethod,
  type AccountsStayRow,
  type AccountsStream,
  type AccountsVoucherRow,
  type AccountsVoucherRedemptionRow,
  type Property,
} from "../services.js";

/** Fallback currency when a row somehow omits one. */
const FALLBACK_CURRENCY = "INR";

// ===========================================================================
// Tabs
// ===========================================================================

/** The Accounts tabs, in order. */
const TABS = [
  { key: "overview", label: "Overview" },
  { key: "stays", label: "Stays & Invoices" },
  { key: "expenses", label: "Expenses" },
  { key: "cash", label: "Cash & Banks" },
  { key: "vouchers", label: "Offers & Vouchers" },
  { key: "salesteam", label: "Staff & Payroll" },
  { key: "owners", label: "Owner Statements" },
  { key: "ledger", label: "Accounts / Ledger" },
] as const;

type TabKey = (typeof TABS)[number]["key"];

// ===========================================================================
// Small pure helpers (SSR-safe: no clock/DOM reads)
// ===========================================================================

/**
 * Format an ISO date `YYYY-MM-DD` (or a fuller ISO timestamp) compactly for
 * display. Runs only during render from already-string values; it never
 * constructs a "now" value at module or render top-level. When the string is
 * empty/null we render an em dash.
 */
function formatDate(iso: string | null): string {
  if (iso === null || iso.trim() === "") {
    return "—";
  }
  // Reuse only the date portion; keep it purely string-derived so there is no
  // locale/clock divergence between server render and client hydration.
  const datePart = iso.slice(0, 10);
  const [y, m, d] = datePart.split("-");
  if (y === undefined || m === undefined || d === undefined) {
    return iso;
  }
  const MONTHS = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  const monthIndex = Number(m) - 1;
  const monthLabel = MONTHS[monthIndex] ?? m;
  return `${Number(d)} ${monthLabel} ${y}`;
}

/** Map a stay payment status to the shared status-pill modifier class. */
function paymentStatusPill(status: AccountsStayRow["paymentStatus"]): string {
  switch (status) {
    case "settled":
      return "avos-status avos-status--ok";
    case "part":
      return "avos-status avos-status--attention";
    case "clearing":
      return "avos-status avos-status--pending";
    case "due":
    default:
      return "avos-status avos-status--critical";
  }
}

/** Human label for a payment status. */
function paymentStatusLabel(status: AccountsStayRow["paymentStatus"]): string {
  switch (status) {
    case "settled":
      return "Settled";
    case "part":
      return "Part paid";
    case "clearing":
      return "Clearing";
    case "due":
    default:
      return "Due";
  }
}

/** Human label for a revenue stream. */
const STREAM_LABEL: Record<AccountsStream, string> = {
  rooms: "Rooms",
  dining: "Dining",
  photo: "Photo",
  scooty: "Scooty",
  other: "Other",
};

/** Human label for a cash-movement kind. */
const CASH_KIND_LABEL: Record<string, string> = {
  opening: "Opening float / adjustment",
  topup: "Top-up from bank",
  deposit: "Deposit into bank",
  count: "Counted cash in box",
  adjustment: "Adjustment",
};

/** AVOS Books expense categories (fixed chart). */
const EXPENSE_CATEGORIES = [
  "Salaries & wages",
  "Housekeeping & cleaning",
  "Food & beverage costs",
  "Electricity & water",
  "Repairs & maintenance",
  "Marketing & advertising",
  "Guest amenities & consumables",
  "Internet & telephone",
  "Software subscriptions",
  "Professional fees",
  "Rates licences & permits",
  "Insurance",
  "Staff welfare",
  "Bank & gateway charges",
  "Miscellaneous",
] as const;

/** GST rate options (percent). */
const GST_RATES = [0, 5, 12, 18, 28] as const;

/** Payment/expense methods. */
const METHODS = [
  { value: "cash", label: "Cash" },
  { value: "pos", label: "Card / POS" },
  { value: "gateway", label: "Online gateway" },
  { value: "bank", label: "Bank transfer" },
] as const;

type Method = (typeof METHODS)[number]["value"];

/** Look up a method's display label (used for the "paid through" account). */
function methodLabel(value: string): string {
  return METHODS.find((m) => m.value === value)?.label ?? value;
}

/** Build a UserMessage model for a simple client-side validation failure. */
function validationMessage(what: string, why: string, next: string): UserMessageModel {
  return { what, why, next };
}

/** A tab-level status/error note. */
interface Note {
  readonly severity: "error" | "status";
  readonly model: UserMessageModel;
}

// ===========================================================================
// Overview tab
// ===========================================================================

/** The preset period keys for the Overview date filter (short + friendly). */
const PERIOD_PRESETS = [
  { key: "this-month", label: "This month" },
  { key: "last-month", label: "Last month" },
  { key: "this-quarter", label: "This quarter" },
  { key: "last-quarter", label: "Last quarter" },
  { key: "this-year", label: "This year" },
  { key: "all-time", label: "All time" },
  { key: "custom", label: "Custom range" },
] as const;

type PresetKey = (typeof PERIOD_PRESETS)[number]["key"];

/**
 * The resolved period filter state: a preset key plus the actual from/to ISO
 * dates and the dropdown label. The label stays simple (the preset name, or
 * just "Custom range") — the resolved dates are never shown beside the trigger.
 */
interface PeriodState {
  readonly preset: PresetKey;
  readonly from: string | undefined;
  readonly to: string | undefined;
  /** The dropdown trigger label — always a preset name (e.g. "This month" / "Custom range"). */
  readonly label: string;
}

/** ISO-format a date component triple. */
function isoDate(y: number, m: number, d: number): string {
  return `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

/** Last day of a month (month is 0-indexed). */
function lastDay(y: number, m: number): number {
  return new Date(y, m + 1, 0).getDate();
}

/** Format an ISO date compactly for display in the trigger/hint. */
function shortDate(iso: string): string {
  const [y, m, d] = iso.split("-");
  if (y === undefined || m === undefined || d === undefined) return iso;
  const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${Number(d)} ${MONTHS[Number(m) - 1] ?? m} ${y}`;
}

/**
 * Resolve a preset key to a PeriodState. Reads `new Date()`, so it MUST only
 * be called inside an effect or event handler — never at module or render
 * top-level (SSR-safe by construction). The label is always the preset name;
 * "custom" carries the chosen dates but keeps the "Custom range" label.
 */
function resolvePreset(preset: PresetKey, customFrom?: string, customTo?: string): PeriodState {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const day = now.getDate();
  const today = isoDate(year, month, day);

  const range = (from: string, to: string, label: string): PeriodState => ({
    preset,
    from,
    to,
    label,
  });

  switch (preset) {
    case "this-month":
      return range(isoDate(year, month, 1), isoDate(year, month, lastDay(year, month)), "This month");
    case "last-month": {
      const pm = new Date(year, month - 1, 1);
      const py = pm.getFullYear();
      const pmm = pm.getMonth();
      return range(isoDate(py, pmm, 1), isoDate(py, pmm, lastDay(py, pmm)), "Last month");
    }
    case "this-quarter": {
      const q = Math.floor(month / 3);
      const qStart = q * 3;
      return range(isoDate(year, qStart, 1), isoDate(year, qStart + 2, lastDay(year, qStart + 2)), "This quarter");
    }
    case "last-quarter": {
      const q = Math.floor(month / 3);
      const lq = q === 0 ? 3 : q - 1;
      const lqYear = q === 0 ? year - 1 : year;
      const lqStart = lq * 3;
      return range(isoDate(lqYear, lqStart, 1), isoDate(lqYear, lqStart + 2, lastDay(lqYear, lqStart + 2)), "Last quarter");
    }
    case "this-year":
      return range(isoDate(year, 0, 1), isoDate(year, 11, 31), "This year");
    case "all-time":
      return { preset, from: undefined, to: undefined, label: "All time" };
    case "custom": {
      const f = customFrom ?? today;
      const t = customTo ?? today;
      return { preset, from: f, to: t, label: "Custom range" };
    }
    default:
      return range(isoDate(year, month, 1), isoDate(year, month, lastDay(year, month)), "This month");
  }
}

/** Human label + pill class for a recent-activity kind. */
const ACTIVITY_KIND: Record<string, { label: string; pill: string }> = {
  stay: { label: "Stay", pill: "avos-status avos-status--ok" },
  expense: { label: "Expense", pill: "avos-status avos-status--critical" },
  cash: { label: "Cash", pill: "avos-status avos-status--pending" },
};

/**
 * Bucket the real recent-activity feed into an income-vs-expenses trend, PURE.
 * Positive amounts are income, negative are expenses. For a single-month period
 * we bucket by ISO week-of-month (Week 1..5); otherwise by calendar month. All
 * money stays integer-minor until the ÷100 at the end (major units for the
 * chart). No new data is fabricated — this only re-shapes activity already
 * returned by the overview service.
 */
function buildTrendBuckets(
  activity: readonly { readonly date: string; readonly amountMinor: string }[],
  monthly: boolean,
): TrendBucket[] {
  const byBucket = new Map<string, { order: number; income: number; expense: number }>();
  const ensure = (key: string, order: number): { order: number; income: number; expense: number } => {
    let b = byBucket.get(key);
    if (b === undefined) {
      b = { order, income: 0, expense: 0 };
      byBucket.set(key, b);
    }
    return b;
  };

  const MONTHS = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];

  for (const a of activity) {
    const iso = (a.date ?? "").slice(0, 10);
    const [y, m, d] = iso.split("-");
    if (y === undefined || m === undefined || d === undefined) continue;
    const amt = Number(a.amountMinor);
    let key: string;
    let order: number;
    if (monthly) {
      order = Number(y) * 12 + (Number(m) - 1);
      key = `${MONTHS[Number(m) - 1] ?? m} ${y.slice(2)}`;
    } else {
      const week = Math.min(5, Math.floor((Number(d) - 1) / 7) + 1);
      order = week;
      key = `Week ${week}`;
    }
    const bucket = ensure(key, order);
    if (amt >= 0) bucket.income += amt;
    else bucket.expense += -amt;
  }

  return [...byBucket.entries()]
    .sort((a, b) => a[1].order - b[1].order)
    .map(([label, b]) => ({
      label,
      income: Math.round(b.income) / 100,
      expense: Math.round(b.expense) / 100,
    }));
}

/** Weekday headers (Mon-first) for the range calendar. */
const CAL_WEEKDAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"] as const;
const CAL_MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
] as const;

/** A single calendar cell: an ISO day or a blank leading/trailing pad. */
interface CalCell {
  readonly iso: string | null;
  readonly day: number | null;
}

/** Build the Mon-first grid of a month (0-indexed) as 6 rows × 7 cells. */
function monthGrid(year: number, month: number): CalCell[] {
  const first = new Date(year, month, 1);
  // JS getDay: 0=Sun..6=Sat → shift to Mon-first (Mon=0..Sun=6).
  const lead = (first.getDay() + 6) % 7;
  const days = lastDay(year, month);
  const cells: CalCell[] = [];
  for (let i = 0; i < lead; i += 1) cells.push({ iso: null, day: null });
  for (let d = 1; d <= days; d += 1) cells.push({ iso: isoDate(year, month, d), day: d });
  while (cells.length % 7 !== 0) cells.push({ iso: null, day: null });
  return cells;
}

/** One rendered month within the range calendar. */
function CalMonth({
  year,
  month,
  from,
  to,
  onPick,
}: {
  readonly year: number;
  readonly month: number;
  readonly from: string | null;
  readonly to: string | null;
  readonly onPick: (iso: string) => void;
}): ReactElement {
  const cells = monthGrid(year, month);
  return (
    <div className="accounts-cal__month">
      <p className="accounts-cal__monthname">{`${CAL_MONTHS[month]} ${year}`}</p>
      <div className="accounts-cal__grid" role="grid">
        {CAL_WEEKDAYS.map((w) => (
          <span key={w} className="accounts-cal__wd" aria-hidden="true">{w}</span>
        ))}
        {cells.map((c, i) => {
          if (c.iso === null) {
            return <span key={`pad-${i}`} className="accounts-cal__pad" />;
          }
          const isStart = from !== null && c.iso === from;
          const isEnd = to !== null && c.iso === to;
          const inRange =
            from !== null && to !== null && c.iso > from && c.iso < to;
          const cls = [
            "accounts-cal__day",
            isStart ? "accounts-cal__day--start" : "",
            isEnd ? "accounts-cal__day--end" : "",
            inRange ? "accounts-cal__day--inrange" : "",
          ]
            .filter(Boolean)
            .join(" ");
          return (
            <button
              key={c.iso}
              type="button"
              className={cls}
              aria-pressed={isStart || isEnd}
              aria-label={shortDate(c.iso)}
              onClick={() => onPick(c.iso as string)}
            >
              {c.day}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * PeriodFilter — the Overview date filter: a compact dropdown trigger (with a
 * calendar glyph) showing only the preset name, a short preset menu, and a
 * two-month visual range calendar for Custom range that only applies on
 * "Apply". One shared source of truth lives in the parent; this is purely the
 * control. Keyboard accessible (Escape closes, focusable options + day cells).
 * The resolved dates are never shown beside the trigger.
 */
function PeriodFilter({
  period,
  onPresetChange,
  onCustomApply,
}: {
  readonly period: PeriodState;
  readonly onPresetChange: (preset: PresetKey) => void;
  readonly onCustomApply: (from: string, to: string) => void;
}): ReactElement {
  const [open, setOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  // The in-progress custom selection (null until the user starts picking).
  const [pickFrom, setPickFrom] = useState<string | null>(null);
  const [pickTo, setPickTo] = useState<string | null>(null);
  const [customError, setCustomError] = useState<string | null>(null);
  // The left month shown in the two-month calendar (0-indexed month + year).
  const [calYear, setCalYear] = useState<number>(2000);
  const [calMonth, setCalMonth] = useState<number>(0);
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Close the menu on an outside click or Escape (SSR-safe: listener attached
  // in an effect, only on the client).
  useEffect(() => {
    if (!open && !customOpen) return undefined;
    const onDocClick = (e: MouseEvent): void => {
      if (rootRef.current !== null && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
        setCustomOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        setOpen(false);
        setCustomOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, customOpen]);

  const openCustom = (): void => {
    // Seed from the active range (retain a previously-applied custom range) or
    // fall back to the current month; anchor the calendar to the start month.
    const from = period.from ?? null;
    const to = period.to ?? null;
    setPickFrom(from);
    setPickTo(to);
    setCustomError(null);
    const anchor = from !== null ? new Date(`${from}T00:00:00`) : new Date();
    setCalYear(anchor.getFullYear());
    setCalMonth(anchor.getMonth());
    setOpen(false);
    setCustomOpen(true);
  };

  const choosePreset = (preset: PresetKey): void => {
    if (preset === "custom") {
      openCustom();
      return;
    }
    setOpen(false);
    setCustomOpen(false);
    onPresetChange(preset);
  };

  // Click semantics: first click (or a click after a complete range) sets the
  // start and clears the end; the next click sets the end (swapping if earlier).
  const pickDay = (iso: string): void => {
    setCustomError(null);
    if (pickFrom === null || (pickFrom !== null && pickTo !== null)) {
      setPickFrom(iso);
      setPickTo(null);
      return;
    }
    if (iso < pickFrom) {
      setPickTo(pickFrom);
      setPickFrom(iso);
    } else {
      setPickTo(iso);
    }
  };

  const stepMonth = (delta: number): void => {
    const d = new Date(calYear, calMonth + delta, 1);
    setCalYear(d.getFullYear());
    setCalMonth(d.getMonth());
  };

  const applyCustom = (): void => {
    if (pickFrom === null || pickTo === null) {
      setCustomError("Select a start date and an end date.");
      return;
    }
    setCustomError(null);
    setCustomOpen(false);
    onCustomApply(pickFrom, pickTo);
  };

  // The right month is one after the left.
  const rightDate = new Date(calYear, calMonth + 1, 1);

  return (
    <div className="accounts-periodfilter" ref={rootRef}>
      <span className="accounts-periodfilter__caption" id="accounts-period-label">
        Period
      </span>
      <div className="accounts-periodfilter__control">
        <button
          type="button"
          className="accounts-periodfilter__trigger"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-labelledby="accounts-period-label"
          onClick={() => {
            setCustomOpen(false);
            setOpen((v) => !v);
          }}
        >
          <span className="accounts-periodfilter__icon" aria-hidden="true">🗓</span>
          <span className="accounts-periodfilter__value">{period.label}</span>
          <span className="accounts-periodfilter__chevron" aria-hidden="true">▾</span>
        </button>

        {open ? (
          <ul className="accounts-periodfilter__menu" role="listbox" aria-labelledby="accounts-period-label">
            {PERIOD_PRESETS.map((p) => (
              <li key={p.key} role="option" aria-selected={period.preset === p.key}>
                <button
                  type="button"
                  className={
                    period.preset === p.key
                      ? "accounts-periodfilter__option accounts-periodfilter__option--active"
                      : "accounts-periodfilter__option"
                  }
                  onClick={() => choosePreset(p.key)}
                >
                  {p.label}
                  {period.preset === p.key ? (
                    <span className="accounts-periodfilter__tick" aria-hidden="true">✓</span>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        ) : null}

        {customOpen ? (
          <div className="accounts-periodfilter__panel accounts-cal" role="dialog" aria-label="Custom date range">
            <div className="accounts-cal__nav">
              <button
                type="button"
                className="console-btn console-btn--ghost accounts-cal__navbtn"
                aria-label="Previous month"
                onClick={() => stepMonth(-1)}
              >
                ‹
              </button>
              <button
                type="button"
                className="console-btn console-btn--ghost accounts-cal__navbtn"
                aria-label="Next month"
                onClick={() => stepMonth(1)}
              >
                ›
              </button>
            </div>
            <div className="accounts-cal__months">
              <CalMonth
                year={calYear}
                month={calMonth}
                from={pickFrom}
                to={pickTo}
                onPick={pickDay}
              />
              <CalMonth
                year={rightDate.getFullYear()}
                month={rightDate.getMonth()}
                from={pickFrom}
                to={pickTo}
                onPick={pickDay}
              />
            </div>
            <div className="accounts-cal__fields">
              <span className="accounts-cal__field">
                <span className="accounts-cal__field-label">Start</span>
                <span className="accounts-cal__field-value">
                  {pickFrom !== null ? shortDate(pickFrom) : "—"}
                </span>
              </span>
              <span className="accounts-cal__field">
                <span className="accounts-cal__field-label">End</span>
                <span className="accounts-cal__field-value">
                  {pickTo !== null ? shortDate(pickTo) : "—"}
                </span>
              </span>
            </div>
            {customError !== null ? (
              <p className="accounts-periodfilter__error" role="alert">{customError}</p>
            ) : null}
            <div className="accounts-periodfilter__panel-actions">
              <button
                type="button"
                className="console-btn console-btn--ghost"
                onClick={() => setCustomOpen(false)}
              >
                Cancel
              </button>
              <button type="button" className="console-btn" onClick={applyCustom}>
                Apply
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function OverviewTab({
  data,
  loading,
  period,
  onPresetChange,
  onCustomApply,
  onViewAllActivity,
}: {
  readonly data: AccountsOverviewData | null;
  readonly loading: boolean;
  readonly period: PeriodState;
  readonly onPresetChange: (preset: PresetKey) => void;
  readonly onCustomApply: (from: string, to: string) => void;
  readonly onViewAllActivity: () => void;
}): ReactElement {
  const periodSelector = (
    <PeriodFilter
      period={period}
      onPresetChange={onPresetChange}
      onCustomApply={onCustomApply}
    />
  );

  if (loading) {
    return (
      <>
        {periodSelector}
        <p className="console-loading" role="status" aria-live="polite">
          Loading overview…
        </p>
      </>
    );
  }
  if (data === null) {
    return (
      <>
        {periodSelector}
        <p className="console-empty">
          No financial figures yet. They appear here as invoices, expenses and
          cash movements are recorded.
        </p>
      </>
    );
  }
  const cur = data.currency || FALLBACK_CURRENCY;
  const money = (minor: string): string => formatMoney(Number(minor), cur);
  const operatingNegative = Number(data.operatingResultMinor) < 0;

  /** Render a KPI money value via the shared global MoneyValue treatment. */
  const kpiValue = (minor: string): ReactElement => (
    <MoneyValue minor={Number(minor)} currency={cur} />
  );

  // Channels sorted highest→lowest for the bar chart (real data only).
  const channelRows = [...data.byChannel]
    .map((r) => ({ channel: r.channel || "Direct", netMinor: r.netMinor }))
    .sort((a, b) => Number(b.netMinor) - Number(a.netMinor));
  // Streams humanised for the donut (real data only).
  const streamRows = data.byStream.map((r) => ({
    label: STREAM_LABEL[r.stream as AccountsStream] ?? r.stream,
    netMinor: r.netMinor,
  }));
  // Income-vs-expenses buckets from the real activity feed. Bucket by month for
  // long spans (quarter / FY / YTD / all-time), else by week within the period.
  const spanDays =
    period.from !== undefined && period.to !== undefined
      ? (Date.parse(period.to) - Date.parse(period.from)) / 86_400_000
      : Infinity;
  const trendMonthly = !Number.isFinite(spanDays) || spanDays > 62;
  const trendBuckets = buildTrendBuckets(data.recentActivity, trendMonthly);

  // GST position, laid out as a compact three-value comparison.
  const outputGst = Number(data.gstCollectedMinor);
  const itc = Number(data.itcMinor);
  const netGst = Number(data.netGstPayableMinor);
  const gstMax = Math.max(outputGst, itc, Math.abs(netGst), 1);

  return (
    <>
      {periodSelector}

      {/* ---- Five primary KPIs (owner-first) ---- */}
      <div className="console-card-grid accounts-kpis">
        <div
          className={
            operatingNegative
              ? "console-card accounts-kpi accounts-kpi--hero accounts-kpi--loss"
              : "console-card console-card--gold accounts-kpi accounts-kpi--hero"
          }
        >
          <p className="console-card__label">Operating result</p>
          {kpiValue(data.operatingResultMinor)}
          <p className="console-card__hint">Revenue after commissions and expenses</p>
        </div>
        <div className="console-card accounts-kpi">
          <p className="console-card__label">Revenue</p>
          {kpiValue(data.revenueExGstMinor)}
          <p className="console-card__hint">Revenue for selected period</p>
        </div>
        <div className="console-card accounts-kpi">
          <p className="console-card__label">Expenses</p>
          {kpiValue(data.expensesMinor)}
          <p className="console-card__hint">Operating expenses</p>
        </div>
        <div className="console-card accounts-kpi">
          <p className="console-card__label">Outstanding receivables</p>
          {kpiValue(data.receivablesMinor)}
          <p className="console-card__hint">Money still to be received</p>
        </div>
        <div className="console-card accounts-kpi">
          <p className="console-card__label">Cash in hand</p>
          {kpiValue(data.cashInHandMinor)}
          <p className="console-card__hint">Available cash balance</p>
        </div>
      </div>

      {/* ---- Section 1: Revenue by channel + Revenue by stream ---- */}
      <div className="accounts-dash-row">
        <section className="console-panel accounts-dash-panel" aria-label="Revenue by channel">
          <h2 className="console-panel__title">Revenue by channel</h2>
          <p className="recon-caption">Where your bookings are coming from</p>
          <RevenueChannelBars rows={channelRows} />
        </section>
        <section className="console-panel accounts-dash-panel" aria-label="Revenue by stream">
          <h2 className="console-panel__title">Revenue by stream</h2>
          <p className="recon-caption">What your guests are spending on</p>
          <RevenueStreamDonut rows={streamRows} totalLabel={money(data.revenueExGstMinor)} />
        </section>
      </div>

      {/* ---- Section 2: Income vs Expenses + GST position ---- */}
      <div className="accounts-dash-row">
        <section className="console-panel accounts-dash-panel" aria-label="Income versus expenses">
          <h2 className="console-panel__title">Income vs expenses</h2>
          <p className="recon-caption">Is more money coming in than going out?</p>
          <IncomeExpenseTrend buckets={trendBuckets} />
        </section>
        <section className="console-panel accounts-dash-panel" aria-label="GST position">
          <h2 className="console-panel__title">GST position</h2>
          {outputGst === 0 && itc === 0 ? (
            <p className="console-empty">No GST activity for this period.</p>
          ) : (
            <>
              <div className="accounts-gst">
                <div className="accounts-gst__row">
                  <span className="accounts-gst__label">Output GST</span>
                  <span className="accounts-gst__bar-track">
                    <span
                      className="accounts-gst__bar accounts-gst__bar--out"
                      style={{ width: `${(outputGst / gstMax) * 100}%` }}
                    />
                  </span>
                  <span className="accounts-gst__amt">{money(data.gstCollectedMinor)}</span>
                </div>
                <div className="accounts-gst__op" aria-hidden="true">minus</div>
                <div className="accounts-gst__row">
                  <span className="accounts-gst__label">Input tax credit</span>
                  <span className="accounts-gst__bar-track">
                    <span
                      className="accounts-gst__bar accounts-gst__bar--itc"
                      style={{ width: `${(itc / gstMax) * 100}%` }}
                    />
                  </span>
                  <span className="accounts-gst__amt">{money(data.itcMinor)}</span>
                </div>
                <div className="accounts-gst__op" aria-hidden="true">equals</div>
                <div className="accounts-gst__row accounts-gst__row--net">
                  <span className="accounts-gst__label">Net GST payable</span>
                  <span className="accounts-gst__bar-track">
                    <span
                      className="accounts-gst__bar accounts-gst__bar--net"
                      style={{ width: `${(Math.abs(netGst) / gstMax) * 100}%` }}
                    />
                  </span>
                  <span className="accounts-gst__amt accounts-gst__amt--net">
                    {money(data.netGstPayableMinor)}
                  </span>
                </div>
              </div>
              <p className="recon-caption">
                Rooms at 5% carry no ITC; mixed 5% / 18% periods may require Rule
                42 apportionment.
              </p>
            </>
          )}
        </section>
      </div>

      {/* ---- Section 3: Recent activity ---- */}
      <section className="console-panel" aria-label="Recent activity">
        <div className="accounts-panel-head">
          <h2 className="console-panel__title">Recent activity</h2>
          <button
            type="button"
            className="console-btn console-btn--ghost"
            onClick={onViewAllActivity}
          >
            View all activity
          </button>
        </div>
        {data.recentActivity.length === 0 ? (
          <p className="console-empty">No financial activity yet.</p>
        ) : (
          <ul className="accounts-activity">
            {data.recentActivity.map((a, index) => {
              const amt = Number(a.amountMinor);
              const rowCur = a.currency || cur;
              const kind = ACTIVITY_KIND[a.kind] ?? {
                label: a.kind,
                pill: "avos-status avos-status--pending",
              };
              return (
                <li className="accounts-activity__item" key={`${a.date}-${index}`}>
                  <span className="accounts-activity__date">{formatDate(a.date)}</span>
                  <span className={`${kind.pill} accounts-activity__kind`}>{kind.label}</span>
                  <span className="accounts-activity__desc">{a.description}</span>
                  <span
                    className={
                      amt < 0
                        ? "accounts-activity__amt accounts-ledger-neg"
                        : "accounts-activity__amt accounts-activity__amt--in"
                    }
                  >
                    {amt < 0 ? "− " : "+ "}
                    {formatMoney(Math.abs(amt), rowCur)}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </>
  );
}

// ===========================================================================
// Stays & Invoices — invoice drawer
// ===========================================================================

/** An editable invoice line in the drawer (decimal strings for inputs). */
interface DraftLine {
  readonly stream: AccountsStream;
  readonly rate: string;
  readonly qty: string;
  readonly description: string;
}

/** The stream options for the line editor. */
const STREAM_OPTIONS: readonly AccountsStream[] = [
  "rooms",
  "dining",
  "photo",
  "scooty",
  "other",
];

function InvoiceDrawer({
  stay,
  people,
  onClose,
  onSaved,
}: {
  readonly stay: AccountsStayRow;
  readonly people: readonly AccountsPayrollRow[];
  readonly onClose: () => void;
  readonly onSaved: (message: string) => void;
}): ReactElement {
  const cur = stay.currency || FALLBACK_CURRENCY;
  const [channel, setChannel] = useState<string>(stay.channel ?? "");
  const [channelIsOta, setChannelIsOta] = useState<boolean>(
    stay.channel !== null && stay.channel.trim() !== "" && stay.commissionMinor !== "0",
  );
  const [commissionPct, setCommissionPct] = useState<string>("");
  const [collection, setCollection] = useState<"guest" | "ota">(stay.collection);
  const [otaSettled, setOtaSettled] = useState<boolean>(false);
  const [discount, setDiscount] = useState<string>("");
  const [voucherCode, setVoucherCode] = useState<string>("");
  const [salespersonId, setSalespersonId] = useState<string>(stay.salespersonId ?? "");
  const [advance, setAdvance] = useState<string>(
    stay.advanceMinor !== "0" && stay.advanceMinor !== ""
      ? (Number(stay.advanceMinor) / 100).toString()
      : "",
  );
  const [receivedOn, setReceivedOn] = useState<string>(
    stay.receivedOn !== null ? stay.receivedOn.slice(0, 10) : "",
  );
  const [lines, setLines] = useState<readonly DraftLine[]>(() => {
    // Prefill from the stay's saved lines when present; otherwise a single
    // Rooms line prefilled from the stay's base amount (minor→major).
    if (stay.lines.length > 0) {
      return stay.lines.map((l) => ({
        stream: (STREAM_OPTIONS.includes(l.stream as AccountsStream)
          ? (l.stream as AccountsStream)
          : "other"),
        rate: (Number(l.rateMinor) / 100).toString(),
        qty: l.qty === "" ? "1" : l.qty,
        description: l.description ?? "",
      }));
    }
    const baseMajor = (Number(stay.baseMinor) / 100).toString();
    return [{ stream: "rooms", rate: baseMajor, qty: "1", description: "" }];
  });
  const [saving, setSaving] = useState(false);
  const [payAmount, setPayAmount] = useState<string>("");
  const [payMethod, setPayMethod] = useState<Method>("cash");
  const [paying, setPaying] = useState(false);
  const [note, setNote] = useState<Note | null>(null);

  const updateLine = useCallback(
    (index: number, patch: Partial<DraftLine>): void => {
      setLines((prev) =>
        prev.map((line, i) => (i === index ? { ...line, ...patch } : line)),
      );
    },
    [],
  );

  const addLine = useCallback((): void => {
    setLines((prev) => [
      ...prev,
      { stream: "other", rate: "", qty: "1", description: "" },
    ]);
  }, []);

  const removeLine = useCallback((index: number): void => {
    setLines((prev) =>
      prev.length <= 1 ? prev : prev.filter((_, i) => i !== index),
    );
  }, []);

  const handleSave = useCallback(
    async (e: FormEvent): Promise<void> => {
      e.preventDefault();
      setNote(null);
      // Validate + convert every line.
      const wireLines: {
        stream: AccountsStream;
        rateMinor: number;
        qty: number;
        description?: string;
      }[] = [];
      for (const line of lines) {
        const rateMinor = parseMoneyToMinor(line.rate);
        if (rateMinor === null) {
          setNote({
            severity: "error",
            model: validationMessage(
              "Line rate is invalid",
              `The ${STREAM_LABEL[line.stream]} line has a rate that is not a valid amount.`,
              "Enter a non-negative decimal amount for each line.",
            ),
          });
          return;
        }
        const qty = Number(line.qty);
        if (!Number.isFinite(qty) || qty <= 0) {
          setNote({
            severity: "error",
            model: validationMessage(
              "Line quantity is invalid",
              `The ${STREAM_LABEL[line.stream]} line has a quantity that is not a positive number.`,
              "Enter a quantity of at least 1.",
            ),
          });
          return;
        }
        wireLines.push({
          stream: line.stream,
          rateMinor,
          qty,
          ...(line.description.trim() !== ""
            ? { description: line.description.trim() }
            : {}),
        });
      }

      let discountMinor: number | undefined;
      if (discount.trim() !== "") {
        const d = parseMoneyToMinor(discount);
        if (d === null) {
          setNote({
            severity: "error",
            model: validationMessage(
              "Discount is invalid",
              "The discount is not a valid amount.",
              "Leave it blank or enter a non-negative decimal amount.",
            ),
          });
          return;
        }
        discountMinor = d;
      }

      let advanceMinor: number | undefined;
      if (advance.trim() !== "") {
        const a = parseMoneyToMinor(advance);
        if (a === null || a < 0) {
          setNote({
            severity: "error",
            model: validationMessage(
              "Advance is invalid",
              "The advance is not a valid amount.",
              "Leave it blank or enter a non-negative decimal amount.",
            ),
          });
          return;
        }
        advanceMinor = a;
      }

      let commissionPctNum: number | undefined;
      if (commissionPct.trim() !== "") {
        const c = Number(commissionPct);
        if (!Number.isFinite(c) || c < 0) {
          setNote({
            severity: "error",
            model: validationMessage(
              "Commission % is invalid",
              "The commission percentage is not a valid non-negative number.",
              "Leave it blank or enter a percentage such as 15.",
            ),
          });
          return;
        }
        commissionPctNum = c;
      }

      setSaving(true);
      try {
        const result = await apiAccountsUpsertInvoice({
          reservationId: stay.reservationId,
          propertyId: stay.propertyId,
          ...(channel.trim() === "" ? {} : { channel: channel.trim() }),
          channelIsOta,
          ...(commissionPctNum !== undefined ? { commissionPct: commissionPctNum } : {}),
          collection,
          ...(collection === "ota" ? { otaSettled } : {}),
          ...(discountMinor !== undefined ? { discountMinor } : {}),
          ...(voucherCode.trim() !== "" ? { voucherCode: voucherCode.trim() } : {}),
          salespersonId: salespersonId.trim() === "" ? null : salespersonId,
          ...(advanceMinor !== undefined ? { advanceMinor } : {}),
          receivedOn: receivedOn.trim() === "" ? null : receivedOn,
          currency: cur,
          lines: wireLines,
        });
        const err = asError(result);
        if (err !== null) {
          setNote({ severity: "error", model: toUserMessage(err) });
          return;
        }
        onSaved("Invoice saved.");
      } catch (caught) {
        setNote({
          severity: "error",
          model: validationMessage(
            "Could not save the invoice",
            caught instanceof Error ? caught.message : "The request failed.",
            "Check your connection and try again.",
          ),
        });
      } finally {
        setSaving(false);
      }
    },
    [
      lines,
      discount,
      advance,
      commissionPct,
      stay.reservationId,
      stay.propertyId,
      channel,
      channelIsOta,
      collection,
      otaSettled,
      voucherCode,
      salespersonId,
      receivedOn,
      cur,
      onSaved,
    ],
  );

  const handlePayment = useCallback(
    async (e: FormEvent): Promise<void> => {
      e.preventDefault();
      setNote(null);
      if (!stay.hasInvoice) {
        setNote({
          severity: "error",
          model: validationMessage(
            "No invoice to pay yet",
            "This stay has no saved invoice, so there is nothing to record a payment against.",
            "Save an invoice first, then record the payment.",
          ),
        });
        return;
      }
      const amountMinor = parseMoneyToMinor(payAmount);
      if (amountMinor === null || amountMinor <= 0) {
        setNote({
          severity: "error",
          model: validationMessage(
            "Payment amount is invalid",
            "The payment amount is not a positive amount.",
            "Enter an amount greater than zero.",
          ),
        });
        return;
      }
      if (stay.invoiceId === null) {
        setNote({
          severity: "error",
          model: validationMessage(
            "No invoice to pay yet",
            "This stay has no saved invoice id.",
            "Save an invoice first, then record the payment.",
          ),
        });
        return;
      }
      setPaying(true);
      try {
        const result = await apiAccountsRecordPayment({
          invoiceId: stay.invoiceId,
          amountMinor,
          method: payMethod,
        });
        const err = asError(result);
        if (err !== null) {
          setNote({ severity: "error", model: toUserMessage(err) });
          return;
        }
        onSaved("Payment recorded.");
      } catch (caught) {
        setNote({
          severity: "error",
          model: validationMessage(
            "Could not record the payment",
            caught instanceof Error ? caught.message : "The request failed.",
            "Check your connection and try again.",
          ),
        });
      } finally {
        setPaying(false);
      }
    },
    [stay.hasInvoice, stay.invoiceId, payAmount, payMethod, onSaved],
  );

  return (
    <div
      className="prop-drawer-scrim"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="prop-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Edit invoice"
      >
        <header className="prop-drawer__header">
          <div>
            <p className="prop-drawer__eyebrow">Invoice</p>
            <h2 className="prop-drawer__title">
              {stay.guestName ?? "Guest"} · {formatDate(stay.checkIn)}
            </h2>
          </div>
          <button
            type="button"
            className="console-btn console-btn--ghost prop-drawer__close"
            aria-label="Close"
            onClick={onClose}
          >
            ✕
          </button>
        </header>

        <div className="prop-drawer__body">
          {note !== null ? (
            <UserMessage message={note.model} severity={note.severity} />
          ) : null}

          {stay.salespersonName !== null ? (
            <p className="recon-caption">
              Lead converted by <strong>{stay.salespersonName}</strong> ·
              commission{" "}
              {formatMoney(Number(stay.salespersonCommissionMinor), cur)}
            </p>
          ) : null}

          <form className="console-form" noValidate onSubmit={handleSave}>
            <p className="recon-caption">
              This overlays financials on the existing reservation. It never
              creates a booking.
            </p>

            <section className="prop-drawer__section">
              <h3 className="prop-drawer__section-title">Channel &amp; collection</h3>
              <div className="console-form__row">
                <div className="console-field">
                  <label htmlFor="inv-channel">Channel</label>
                  <input
                    id="inv-channel"
                    type="text"
                    value={channel}
                    onChange={(e) => setChannel(e.target.value)}
                    placeholder="Direct, Airbnb, Booking.com…"
                  />
                </div>
                <div className="console-field console-field--checkbox">
                  <label htmlFor="inv-isota">
                    <input
                      id="inv-isota"
                      type="checkbox"
                      checked={channelIsOta}
                      onChange={(e) => setChannelIsOta(e.target.checked)}
                    />
                    Channel is an OTA
                  </label>
                </div>
              </div>
              <div className="console-form__row">
                <div className="console-field">
                  <label htmlFor="inv-commission">Commission %</label>
                  <input
                    id="inv-commission"
                    type="number"
                    min="0"
                    step="0.01"
                    value={commissionPct}
                    onChange={(e) => setCommissionPct(e.target.value)}
                    placeholder="e.g. 15"
                  />
                </div>
                <div className="console-field">
                  <label htmlFor="inv-collection">Collection</label>
                  <select
                    id="inv-collection"
                    value={collection}
                    onChange={(e) =>
                      setCollection(e.target.value === "ota" ? "ota" : "guest")
                    }
                  >
                    <option value="guest">Collected from guest</option>
                    <option value="ota">Collected by OTA</option>
                  </select>
                </div>
              </div>
              {collection === "ota" ? (
                <div className="console-form__row">
                  <div className="console-field console-field--checkbox">
                    <label htmlFor="inv-otasettled">
                      <input
                        id="inv-otasettled"
                        type="checkbox"
                        checked={otaSettled}
                        onChange={(e) => setOtaSettled(e.target.checked)}
                      />
                      OTA has settled the payout
                    </label>
                  </div>
                </div>
              ) : null}
            </section>

            <section className="prop-drawer__section">
              <h3 className="prop-drawer__section-title">Sales &amp; receipt</h3>
              <div className="console-form__row">
                <div className="console-field">
                  <label htmlFor="inv-salesperson">
                    Salesperson (lead converted by)
                  </label>
                  <select
                    id="inv-salesperson"
                    value={salespersonId}
                    onChange={(e) => setSalespersonId(e.target.value)}
                  >
                    <option value="">None</option>
                    {people.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="console-field">
                  <label htmlFor="inv-advance">Advance ({cur})</label>
                  <input
                    id="inv-advance"
                    type="text"
                    inputMode="decimal"
                    value={advance}
                    onChange={(e) => setAdvance(e.target.value)}
                    placeholder="0.00"
                  />
                </div>
                <div className="console-field">
                  <label htmlFor="inv-receivedon">Received on</label>
                  <input
                    id="inv-receivedon"
                    type="date"
                    value={receivedOn}
                    onChange={(e) => setReceivedOn(e.target.value)}
                  />
                </div>
              </div>
            </section>

            <section className="prop-drawer__section">
              <h3 className="prop-drawer__section-title">Adjustments</h3>
              <div className="console-form__row">
                <div className="console-field">
                  <label htmlFor="inv-discount">Discount ({cur})</label>
                  <input
                    id="inv-discount"
                    type="text"
                    inputMode="decimal"
                    value={discount}
                    onChange={(e) => setDiscount(e.target.value)}
                    placeholder="0.00"
                  />
                </div>
                <div className="console-field">
                  <label htmlFor="inv-voucher">Voucher code</label>
                  <input
                    id="inv-voucher"
                    type="text"
                    value={voucherCode}
                    onChange={(e) => setVoucherCode(e.target.value)}
                    placeholder="Optional"
                  />
                </div>
              </div>
            </section>

            <section className="prop-drawer__section">
              <h3 className="prop-drawer__section-title">Lines</h3>
              {lines.map((line, index) => (
                <div className="console-form__row" key={index}>
                  <div className="console-field">
                    <label htmlFor={`inv-line-stream-${index}`}>Stream</label>
                    <select
                      id={`inv-line-stream-${index}`}
                      value={line.stream}
                      onChange={(e) =>
                        updateLine(index, {
                          stream: e.target.value as AccountsStream,
                        })
                      }
                    >
                      {STREAM_OPTIONS.map((s) => (
                        <option key={s} value={s}>
                          {STREAM_LABEL[s]}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="console-field">
                    <label htmlFor={`inv-line-desc-${index}`}>Description</label>
                    <input
                      id={`inv-line-desc-${index}`}
                      type="text"
                      value={line.description}
                      onChange={(e) =>
                        updateLine(index, { description: e.target.value })
                      }
                      placeholder="Optional"
                    />
                  </div>
                  <div className="console-field">
                    <label htmlFor={`inv-line-rate-${index}`}>Rate ({cur})</label>
                    <input
                      id={`inv-line-rate-${index}`}
                      type="text"
                      inputMode="decimal"
                      value={line.rate}
                      onChange={(e) => updateLine(index, { rate: e.target.value })}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="console-field">
                    <label htmlFor={`inv-line-qty-${index}`}>Qty</label>
                    <input
                      id={`inv-line-qty-${index}`}
                      type="number"
                      min="1"
                      step="1"
                      value={line.qty}
                      onChange={(e) => updateLine(index, { qty: e.target.value })}
                    />
                  </div>
                  <div className="console-field console-field--checkbox">
                    <button
                      type="button"
                      className="console-btn console-btn--ghost"
                      disabled={lines.length <= 1}
                      onClick={() => removeLine(index)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
              <button
                type="button"
                className="console-btn console-btn--ghost"
                onClick={addLine}
              >
                + Add line
              </button>
            </section>

            <div className="prop-drawer__footer">
              <button
                type="button"
                className="console-btn console-btn--ghost"
                onClick={onClose}
              >
                Cancel
              </button>
              <button type="submit" className="console-btn" disabled={saving}>
                {saving ? "Saving…" : "Save invoice"}
              </button>
            </div>
          </form>

          {stay.hasInvoice ? (
            <form className="console-form" noValidate onSubmit={handlePayment}>
              <h3 className="prop-drawer__section-title">Record payment</h3>
              <div className="console-form__row">
                <div className="console-field">
                  <label htmlFor="inv-pay-amount">Amount ({cur})</label>
                  <input
                    id="inv-pay-amount"
                    type="text"
                    inputMode="decimal"
                    value={payAmount}
                    onChange={(e) => setPayAmount(e.target.value)}
                    placeholder="0.00"
                  />
                </div>
                <div className="console-field">
                  <label htmlFor="inv-pay-method">Method</label>
                  <select
                    id="inv-pay-method"
                    value={payMethod}
                    onChange={(e) => setPayMethod(e.target.value as Method)}
                  >
                    {METHODS.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="console-field console-field--checkbox">
                  <button type="submit" className="console-btn" disabled={paying}>
                    {paying ? "Recording…" : "Record payment"}
                  </button>
                </div>
              </div>
            </form>
          ) : (
            <p className="recon-caption">
              Save an invoice before recording a payment against this stay.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// Stays & Invoices tab
// ===========================================================================

/** Whether a stay is "cancelled" and should be excluded from active totals. */
function isCancelledStay(s: AccountsStayRow): boolean {
  return s.status.toLowerCase() === "cancelled" || s.status.toLowerCase() === "canceled";
}

/** The invoice-list status filter options (derived concept). */
type InvoiceStatusFilter = "all" | "due" | "part" | "paid" | "archived" | "void";

const INVOICE_STATUS_FILTERS: readonly { key: InvoiceStatusFilter; label: string }[] = [
  { key: "all", label: "All statuses" },
  { key: "due", label: "Due" },
  { key: "part", label: "Part paid" },
  { key: "paid", label: "Paid" },
  { key: "archived", label: "Archived" },
  { key: "void", label: "Void" },
];

/** The outstanding balance (gross − paid) as an integer-minor number. */
function balanceMinor(s: AccountsStayRow): number {
  return Number(s.grossMinor) - Number(s.paidMinor);
}

/**
 * Escape a CSV cell: wrap in quotes and double any embedded quotes so commas,
 * quotes and newlines survive. Pure + SSR-safe (no DOM).
 */
function csvCell(value: string): string {
  return `"${value.replace(/"/g, '""')}"`;
}

/** A confirm-action kind for the lifecycle/delete confirmation modal. */
type ConfirmKind = "archive" | "void" | "delete";

const CONFIRM_COPY: Record<ConfirmKind, { title: string; body: string; cta: string }> = {
  archive: {
    title: "Archive invoice?",
    body: "This invoice will be removed from active views but kept in records.",
    cta: "Archive invoice",
  },
  void: {
    title: "Void invoice?",
    body: "This action will mark the invoice as void.",
    cta: "Void invoice",
  },
  delete: {
    title: "Delete invoice?",
    body: "This action cannot be undone.",
    cta: "Delete invoice",
  },
};

/** Open branded invoice HTML in a fresh window for print / save-as-PDF. */
function openInvoiceHtml(html: string): void {
  const win = window.open("", "_blank");
  if (win === null) {
    return;
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
}

// ---------------------------------------------------------------------------
// Row overflow menu
// ---------------------------------------------------------------------------

/** One action in the row / drawer overflow menu. */
interface RowMenuAction {
  readonly key: string;
  readonly label: string;
  readonly onClick: () => void;
  readonly disabled?: boolean;
  readonly danger?: boolean;
}

function RowMenu({ actions }: { readonly actions: readonly RowMenuAction[] }): ReactElement {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e: MouseEvent): void => {
      if (rootRef.current !== null && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="accounts-rowmenu" ref={rootRef}>
      <button
        type="button"
        className="console-btn console-btn--ghost accounts-rowmenu__trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="More actions"
        onClick={() => setOpen((v) => !v)}
      >
        ⋮
      </button>
      {open ? (
        <ul className="accounts-rowmenu__menu" role="menu">
          {actions.map((a) => (
            <li key={a.key} role="none">
              <button
                type="button"
                role="menuitem"
                className={
                  a.danger
                    ? "accounts-rowmenu__item accounts-rowmenu__item--danger"
                    : "accounts-rowmenu__item"
                }
                disabled={a.disabled === true}
                onClick={() => {
                  setOpen(false);
                  a.onClick();
                }}
              >
                {a.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Send-by-email modal
// ---------------------------------------------------------------------------

function SendEmailModal({
  stay,
  onClose,
  onSent,
}: {
  readonly stay: AccountsStayRow;
  readonly onClose: () => void;
  readonly onSent: (message: string) => void;
}): ReactElement {
  const [to, setTo] = useState<string>(stay.guestEmail ?? "");
  const [subject, setSubject] = useState<string>(
    `Invoice ${stay.invoiceNo ?? ""}`.trim(),
  );
  const [message, setMessage] = useState<string>("");
  const [sending, setSending] = useState(false);
  const [note, setNote] = useState<Note | null>(null);

  const handleSend = useCallback(
    async (e: FormEvent): Promise<void> => {
      e.preventDefault();
      setNote(null);
      setSending(true);
      try {
        const trimmedTo = to.trim();
        const trimmedSubject = subject.trim();
        const trimmedMessage = message.trim();
        const result = await apiAccountsInvoiceEmail({
          reservationId: stay.reservationId,
          ...(trimmedTo !== "" ? { to: trimmedTo } : {}),
          ...(trimmedSubject !== "" ? { subject: trimmedSubject } : {}),
          ...(trimmedMessage !== "" ? { message: trimmedMessage } : {}),
        });
        const err = asError(result);
        if (err !== null) {
          setNote({ severity: "error", model: toUserMessage(err) });
          return;
        }
        const sentTo = (result as { to: string }).to;
        onSent(`Invoice emailed to ${sentTo}.`);
      } catch (caught) {
        setNote({
          severity: "error",
          model: validationMessage(
            "Could not send the email",
            caught instanceof Error ? caught.message : "The request failed.",
            "Check your connection and try again.",
          ),
        });
      } finally {
        setSending(false);
      }
    },
    [to, subject, message, stay.reservationId, onSent],
  );

  return (
    <div
      className="console-modal-scrim"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="console-modal" role="dialog" aria-modal="true" aria-label="Send invoice by email">
        <header className="console-modal__header">
          <h2 className="console-modal__title">Send invoice by email</h2>
          <button
            type="button"
            className="console-btn console-btn--ghost console-modal__close"
            aria-label="Close"
            onClick={onClose}
          >
            ✕
          </button>
        </header>
        <div className="console-modal__body">
          {note !== null ? (
            <UserMessage message={note.model} severity={note.severity} />
          ) : null}
          <form className="console-form" noValidate onSubmit={handleSend}>
            <div className="console-field">
              <label htmlFor="inv-email-to">Recipient</label>
              <input
                id="inv-email-to"
                type="email"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                placeholder="guest@example.com"
              />
            </div>
            <div className="console-field">
              <label htmlFor="inv-email-subject">Subject</label>
              <input
                id="inv-email-subject"
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>
            <div className="console-field">
              <label htmlFor="inv-email-message">Message</label>
              <textarea
                id="inv-email-message"
                rows={4}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Optional note to the guest"
              />
            </div>
            <p className="recon-caption">PDF invoice link included.</p>
            <div className="console-modal__actions">
              <button type="button" className="console-btn console-btn--ghost" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="console-btn" disabled={sending}>
                {sending ? "Sending…" : "Send"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Confirm (archive / void / delete) modal
// ---------------------------------------------------------------------------

function ConfirmActionModal({
  kind,
  busy,
  error,
  onConfirm,
  onClose,
}: {
  readonly kind: ConfirmKind;
  readonly busy: boolean;
  readonly error: UserMessageModel | null;
  readonly onConfirm: () => void;
  readonly onClose: () => void;
}): ReactElement {
  const copy = CONFIRM_COPY[kind];
  return (
    <div
      className="console-modal-scrim"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="console-modal console-modal--confirm" role="dialog" aria-modal="true" aria-label={copy.title}>
        <header className="console-modal__header">
          <h2 className="console-modal__title">{copy.title}</h2>
          <button
            type="button"
            className="console-btn console-btn--ghost console-modal__close"
            aria-label="Close"
            onClick={onClose}
          >
            ✕
          </button>
        </header>
        <div className="console-modal__body">
          {error !== null ? <UserMessage message={error} severity="error" /> : null}
          <p className="recon-caption">{copy.body}</p>
          <div className="console-modal__actions">
            <button type="button" className="console-btn console-btn--ghost" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className={kind === "delete" ? "console-btn console-btn--danger" : "console-btn"}
              disabled={busy}
              onClick={onConfirm}
            >
              {busy ? "Working…" : copy.cta}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Invoice detail drawer (read-only View)
// ---------------------------------------------------------------------------

function InvoiceDetailDrawer({
  stay,
  onClose,
  onDownload,
  onEmail,
  onWhatsApp,
  onEdit,
  onLifecycle,
}: {
  readonly stay: AccountsStayRow;
  readonly onClose: () => void;
  readonly onDownload: (stay: AccountsStayRow) => void;
  readonly onEmail: (stay: AccountsStayRow) => void;
  readonly onWhatsApp: (stay: AccountsStayRow) => void;
  readonly onEdit: (stay: AccountsStayRow) => void;
  readonly onLifecycle: (stay: AccountsStayRow, kind: ConfirmKind) => void;
}): ReactElement {
  const cur = stay.currency || FALLBACK_CURRENCY;
  const [detail, setDetail] = useState<AccountsInvoiceDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState<Note | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNote(null);
    void (async (): Promise<void> => {
      try {
        const result = await apiAccountsInvoiceDetail(stay.reservationId);
        const err = asError(result);
        if (cancelled) return;
        if (err !== null) {
          setNote({ severity: "error", model: toUserMessage(err) });
          setDetail(null);
        } else {
          setDetail(result as AccountsInvoiceDetail);
        }
      } catch (caught) {
        if (!cancelled) {
          setNote({
            severity: "error",
            model: validationMessage(
              "Could not load the invoice",
              caught instanceof Error ? caught.message : "The request failed.",
              "Check your connection and try again.",
            ),
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [stay.reservationId]);

  const money = (minor: string): string => formatMoney(Number(minor), cur);
  const view = detail?.stay ?? stay;
  const payments: readonly AccountsInvoicePayment[] = detail?.payments ?? [];
  const netMinorNum = Number(view.netMinor);
  const discountNum = Number(view.discountMinor);
  const balance = balanceMinor(view);
  const lifecycle = view.lifecycle;
  const canManage = view.invoiceId !== null;

  const statusPill =
    lifecycle === "archived" ? (
      <span className="avos-status avos-status--pending">Archived</span>
    ) : lifecycle === "void" ? (
      <span className="avos-status avos-status--critical">Void</span>
    ) : (
      <span className={paymentStatusPill(view.paymentStatus)}>
        {paymentStatusLabel(view.paymentStatus)}
      </span>
    );

  return (
    <div
      className="prop-drawer-scrim"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="prop-drawer" role="dialog" aria-modal="true" aria-label="Invoice detail">
        <header className="prop-drawer__header">
          <div>
            <p className="prop-drawer__eyebrow">Invoice</p>
            <h2 className="prop-drawer__title">
              {view.invoiceNo ?? "Not issued"}
            </h2>
          </div>
          <button
            type="button"
            className="console-btn console-btn--ghost prop-drawer__close"
            aria-label="Close"
            onClick={onClose}
          >
            ✕
          </button>
        </header>

        <div className="prop-drawer__body">
          {note !== null ? (
            <UserMessage message={note.model} severity={note.severity} />
          ) : null}

          {loading ? (
            <p className="console-loading" role="status" aria-live="polite">
              Loading invoice…
            </p>
          ) : (
            <>
              <div className="accounts-invdetail__status">{statusPill}</div>

              <section className="prop-drawer__section">
                <h3 className="prop-drawer__section-title">Guest</h3>
                <dl className="accounts-invdetail__rows">
                  <div className="accounts-invdetail__row">
                    <dt>Name</dt>
                    <dd>{view.guestName ?? "Guest"}</dd>
                  </div>
                  <div className="accounts-invdetail__row">
                    <dt>Email</dt>
                    <dd>{view.guestEmail ?? "—"}</dd>
                  </div>
                  <div className="accounts-invdetail__row">
                    <dt>Phone</dt>
                    <dd>{view.guestPhone ?? "—"}</dd>
                  </div>
                </dl>
              </section>

              <section className="prop-drawer__section">
                <h3 className="prop-drawer__section-title">Stay</h3>
                <dl className="accounts-invdetail__rows">
                  <div className="accounts-invdetail__row">
                    <dt>Property / Unit</dt>
                    <dd>
                      {view.propertyName ?? "—"}
                      {view.unitName !== null && view.unitName.trim() !== ""
                        ? ` · ${view.unitName}`
                        : ""}
                    </dd>
                  </div>
                  <div className="accounts-invdetail__row">
                    <dt>Stay dates</dt>
                    <dd>
                      {formatDate(view.checkIn)} → {formatDate(view.checkOut)}
                    </dd>
                  </div>
                  <div className="accounts-invdetail__row">
                    <dt>Source</dt>
                    <dd>{view.channel ?? "Direct"}</dd>
                  </div>
                  <div className="accounts-invdetail__row">
                    <dt>Invoice date</dt>
                    <dd>{formatDate(view.issuedOn)}</dd>
                  </div>
                </dl>
              </section>

              <section className="prop-drawer__section">
                <h3 className="prop-drawer__section-title">Line items</h3>
                <table className="console-table accounts-invdetail__lines">
                  <thead>
                    <tr>
                      <th scope="col">Stream</th>
                      <th scope="col">Description</th>
                      <th scope="col">Qty</th>
                      <th scope="col">Rate</th>
                      <th scope="col">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {view.lines.map((l, i) => {
                      const rate = Number(l.rateMinor);
                      const qty = Number(l.qty) || 0;
                      return (
                        <tr key={i}>
                          <td>{STREAM_LABEL[l.stream as AccountsStream] ?? l.stream}</td>
                          <td>{l.description ?? "—"}</td>
                          <td className="recon-num">{l.qty === "" ? "1" : l.qty}</td>
                          <td className="recon-num">{money(l.rateMinor)}</td>
                          <td className="recon-num">{formatMoney(rate * qty, cur)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </section>

              <section className="prop-drawer__section">
                <h3 className="prop-drawer__section-title">Totals</h3>
                <dl className="accounts-invdetail__rows accounts-invdetail__rows--totals">
                  <div className="accounts-invdetail__row">
                    <dt>Subtotal (net)</dt>
                    <dd className="recon-num">{money(view.netMinor)}</dd>
                  </div>
                  {discountNum > 0 ? (
                    <div className="accounts-invdetail__row">
                      <dt>Discount</dt>
                      <dd className="recon-num">−{money(view.discountMinor)}</dd>
                    </div>
                  ) : null}
                  <div className="accounts-invdetail__row">
                    <dt>GST</dt>
                    <dd className="recon-num">{money(view.gstMinor)}</dd>
                  </div>
                  <div className="accounts-invdetail__row accounts-invdetail__row--strong">
                    <dt>Gross total</dt>
                    <dd className="recon-num">{money(view.grossMinor)}</dd>
                  </div>
                  <div className="accounts-invdetail__row">
                    <dt>Paid</dt>
                    <dd className="recon-num">{money(view.paidMinor)}</dd>
                  </div>
                  <div className="accounts-invdetail__row accounts-invdetail__row--strong">
                    <dt>Balance due</dt>
                    <dd className={balance > 0 ? "recon-num accounts-ledger-neg" : "recon-num"}>
                      {formatMoney(balance, cur)}
                    </dd>
                  </div>
                </dl>
                <p className="console-sr-only">Net subtotal {netMinorNum}</p>
              </section>

              <section className="prop-drawer__section">
                <h3 className="prop-drawer__section-title">Payment history</h3>
                {payments.length === 0 ? (
                  <p className="recon-caption">No payments recorded yet.</p>
                ) : (
                  <table className="console-table accounts-invdetail__payments">
                    <thead>
                      <tr>
                        <th scope="col">Date</th>
                        <th scope="col">Method</th>
                        <th scope="col">Reference</th>
                        <th scope="col">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map((p) => (
                        <tr key={p.id}>
                          <td>{formatDate(p.receivedOn)}</td>
                          <td>{methodLabel(p.method)}</td>
                          <td>{p.reference ?? "—"}</td>
                          <td className="recon-num">{money(p.amountMinor)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>
            </>
          )}
        </div>

        <div className="prop-drawer__footer">
          <button
            type="button"
            className="console-btn console-btn--ghost"
            disabled={!canManage}
            onClick={() => onDownload(view)}
          >
            Download PDF
          </button>
          <button
            type="button"
            className="console-btn console-btn--ghost"
            disabled={!canManage}
            onClick={() => onEmail(view)}
          >
            Send Email
          </button>
          <button
            type="button"
            className="console-btn console-btn--ghost"
            disabled={!canManage}
            onClick={() => onWhatsApp(view)}
          >
            Send WhatsApp
          </button>
          <button
            type="button"
            className="console-btn console-btn--ghost"
            onClick={() => onEdit(view)}
          >
            Edit
          </button>
          {lifecycle === "void" ? null : (
            <button
              type="button"
              className="console-btn console-btn--ghost"
              disabled={!canManage}
              onClick={() => onLifecycle(view, lifecycle === "archived" ? "void" : "archive")}
            >
              {lifecycle === "archived" ? "Void" : "Archive"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stays & Invoices tab (invoice-management workspace)
// ---------------------------------------------------------------------------

const STAYS_PAGE_SIZE = 25;

function StaysTab({
  rows,
  loading,
  onInvoice,
  onRefresh,
}: {
  readonly rows: readonly AccountsStayRow[];
  readonly loading: boolean;
  readonly onInvoice: (stay: AccountsStayRow) => void;
  readonly onRefresh: (message: string) => void;
}): ReactElement {
  const [search, setSearch] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<InvoiceStatusFilter>("all");
  const [period, setPeriod] = useState<PeriodState>({
    preset: "all-time",
    from: undefined,
    to: undefined,
    label: "All time",
  });
  const [page, setPage] = useState<number>(0);
  const [note, setNote] = useState<Note | null>(null);

  // Row-scoped overlays.
  const [detailStay, setDetailStay] = useState<AccountsStayRow | null>(null);
  const [emailStay, setEmailStay] = useState<AccountsStayRow | null>(null);
  const [confirm, setConfirm] = useState<{ stay: AccountsStayRow; kind: ConfirmKind } | null>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmError, setConfirmError] = useState<UserMessageModel | null>(null);

  const handlePresetChange = useCallback((preset: PresetKey): void => {
    setPeriod(resolvePreset(preset));
    setPage(0);
  }, []);
  const handleCustomApply = useCallback((from: string, to: string): void => {
    setPeriod(resolvePreset("custom", from, to));
    setPage(0);
  }, []);

  // The date used for period filtering: the issue date when present, else check-in.
  const filterDate = useCallback((s: AccountsStayRow): string | null => {
    return s.issuedOn ?? s.checkIn;
  }, []);

  // Apply search + status + period filters over the property-scoped rows.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((s) => {
      if (q !== "") {
        const haystack = [
          s.guestName ?? "",
          s.invoiceNo ?? "",
          s.propertyName ?? "",
          s.unitName ?? "",
          s.channel ?? "",
        ]
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      switch (statusFilter) {
        case "due":
          if (s.paymentStatus !== "due") return false;
          break;
        case "part":
          if (s.paymentStatus !== "part") return false;
          break;
        case "paid":
          if (s.paymentStatus !== "settled") return false;
          break;
        case "archived":
          if (s.lifecycle !== "archived") return false;
          break;
        case "void":
          if (s.lifecycle !== "void") return false;
          break;
        case "all":
        default:
          break;
      }
      if (period.from !== undefined || period.to !== undefined) {
        const d = filterDate(s);
        const day = d === null ? "" : d.slice(0, 10);
        if (day === "") return false;
        if (period.from !== undefined && day < period.from) return false;
        if (period.to !== undefined && day > period.to) return false;
      }
      return true;
    });
  }, [rows, search, statusFilter, period.from, period.to, filterDate]);

  // KPI figures: derived from the currently-filtered rows. Active totals exclude
  // cancelled stays and archived/void invoices.
  const kpis = useMemo(() => {
    const active = filtered.filter(
      (s) => !isCancelledStay(s) && s.lifecycle !== "archived" && s.lifecycle !== "void",
    );
    let invoicedMinor = 0;
    let outstandingMinor = 0;
    let dueCount = 0;
    for (const s of active) {
      invoicedMinor += Number(s.grossMinor);
      if (s.collection === "guest" && (s.paymentStatus === "due" || s.paymentStatus === "part")) {
        outstandingMinor += Math.max(0, balanceMinor(s));
      }
      if (s.paymentStatus === "due" || s.paymentStatus === "part") {
        dueCount += 1;
      }
    }
    const currency = filtered[0]?.currency ?? active[0]?.currency ?? FALLBACK_CURRENCY;
    return {
      count: filtered.length,
      invoicedMinor,
      outstandingMinor,
      dueCount,
      currency: currency || FALLBACK_CURRENCY,
    };
  }, [filtered]);

  // Client-side pagination.
  const pageCount = Math.max(1, Math.ceil(filtered.length / STAYS_PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = filtered.slice(
    safePage * STAYS_PAGE_SIZE,
    safePage * STAYS_PAGE_SIZE + STAYS_PAGE_SIZE,
  );

  // ---- Shared action handlers (used by rows AND the detail drawer) --------
  const handleDownload = useCallback(
    async (stay: AccountsStayRow): Promise<void> => {
      setNote(null);
      try {
        const result = await apiAccountsInvoicePdf(stay.reservationId);
        const err = asError(result);
        if (err !== null) {
          setNote({ severity: "error", model: toUserMessage(err) });
          return;
        }
        openInvoiceHtml((result as { html: string }).html);
      } catch (caught) {
        setNote({
          severity: "error",
          model: validationMessage(
            "Could not open the invoice",
            caught instanceof Error ? caught.message : "The request failed.",
            "Check your connection and try again.",
          ),
        });
      }
    },
    [],
  );

  const handleWhatsApp = useCallback(
    async (stay: AccountsStayRow): Promise<void> => {
      setNote(null);
      try {
        const result = await apiAccountsInvoiceWhatsApp({ reservationId: stay.reservationId });
        const err = asError(result);
        if (err !== null) {
          setNote({ severity: "error", model: toUserMessage(err) });
          return;
        }
        window.open((result as { url: string }).url, "_blank");
      } catch (caught) {
        setNote({
          severity: "error",
          model: validationMessage(
            "Could not open WhatsApp",
            caught instanceof Error ? caught.message : "The request failed.",
            "Check the guest has a phone number on file and try again.",
          ),
        });
      }
    },
    [],
  );

  const confirmAction = useCallback(async (): Promise<void> => {
    if (confirm === null) return;
    const { stay, kind } = confirm;
    if (stay.invoiceId === null) {
      setConfirm(null);
      return;
    }
    setConfirmBusy(true);
    setConfirmError(null);
    try {
      const result =
        kind === "delete"
          ? await apiAccountsInvoiceDelete({ invoiceId: stay.invoiceId })
          : await apiAccountsInvoiceLifecycle({
              invoiceId: stay.invoiceId,
              lifecycle: kind === "archive" ? "archived" : "void",
            });
      const err = asError(result);
      if (err !== null) {
        setConfirmError(toUserMessage(err));
        return;
      }
      setConfirm(null);
      setDetailStay(null);
      const message =
        kind === "delete"
          ? "Invoice deleted."
          : kind === "archive"
            ? "Invoice archived."
            : "Invoice voided.";
      onRefresh(message);
    } catch (caught) {
      setConfirmError(
        validationMessage(
          "Could not complete the action",
          caught instanceof Error ? caught.message : "The request failed.",
          "Check your connection and try again.",
        ),
      );
    } finally {
      setConfirmBusy(false);
    }
  }, [confirm, onRefresh]);

  const handleExportCsv = useCallback((): void => {
    const header = [
      "Invoice",
      "Guest",
      "Property",
      "Unit",
      "Source",
      "Check-in",
      "Check-out",
      "Invoice date",
      "Total",
      "Paid",
      "Balance",
      "Status",
    ];
    const lines = [header.map(csvCell).join(",")];
    for (const s of filtered) {
      const cur = s.currency || FALLBACK_CURRENCY;
      const statusText =
        s.lifecycle === "archived"
          ? "Archived"
          : s.lifecycle === "void"
            ? "Void"
            : paymentStatusLabel(s.paymentStatus);
      lines.push(
        [
          s.invoiceNo ?? "Not issued",
          s.guestName ?? "Guest",
          s.propertyName ?? "",
          s.unitName ?? "",
          s.channel ?? "Direct",
          s.checkIn ?? "",
          s.checkOut ?? "",
          s.issuedOn ?? "",
          formatMoney(Number(s.grossMinor), cur),
          formatMoney(Number(s.paidMinor), cur),
          formatMoney(balanceMinor(s), cur),
          statusText,
        ]
          .map((v) => csvCell(String(v)))
          .join(","),
      );
    }
    const csv = lines.join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "invoices.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [filtered]);

  const periodSelector = (
    <PeriodFilter
      period={period}
      onPresetChange={handlePresetChange}
      onCustomApply={handleCustomApply}
    />
  );

  return (
    <>
      {/* ---- Four KPI summary cards ---- */}
      <div className="console-card-grid accounts-invkpis">
        <div className="console-card accounts-kpi">
          <p className="console-card__label">Total invoices</p>
          <p className="console-card__value">{kpis.count}</p>
          <p className="console-card__hint">In current view</p>
        </div>
        <div className="console-card console-card--gold accounts-kpi accounts-kpi--hero">
          <p className="console-card__label">Total invoiced</p>
          <MoneyValue minor={kpis.invoicedMinor} currency={kpis.currency} />
          <p className="console-card__hint">Gross across active invoices</p>
        </div>
        <div className="console-card accounts-kpi">
          <p className="console-card__label">Outstanding</p>
          <MoneyValue minor={kpis.outstandingMinor} currency={kpis.currency} />
          <p className="console-card__hint">Guest balances still to collect</p>
        </div>
        <div className="console-card accounts-kpi">
          <p className="console-card__label">Due / overdue</p>
          <p className="console-card__value">{kpis.dueCount}</p>
          <p className="console-card__hint">Invoices due or part paid</p>
        </div>
      </div>

      <section className="console-panel" aria-label="Invoices">
        <div className="accounts-panel-head">
          <h2 className="console-panel__title">Invoices</h2>
          <div className="accounts-panel-head__actions">
            <button
              type="button"
              className="console-btn console-btn--ghost"
              onClick={handleExportCsv}
              disabled={filtered.length === 0}
            >
              Download report (CSV)
            </button>
          </div>
        </div>

        {/* ---- Control row: search + status + period ---- */}
        <div className="accounts-invcontrols">
          <div className="console-field accounts-invcontrols__search">
            <label htmlFor="inv-search">Search</label>
            <input
              id="inv-search"
              type="search"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              placeholder="Guest, invoice no, property, unit or channel"
            />
          </div>
          <div className="console-field accounts-invcontrols__status">
            <label htmlFor="inv-status">Status</label>
            <select
              id="inv-status"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as InvoiceStatusFilter);
                setPage(0);
              }}
            >
              {INVOICE_STATUS_FILTERS.map((f) => (
                <option key={f.key} value={f.key}>
                  {f.label}
                </option>
              ))}
            </select>
          </div>
          <div className="accounts-invcontrols__period">{periodSelector}</div>
        </div>

        {note !== null ? (
          <UserMessage message={note.model} severity={note.severity} />
        ) : null}

        {loading ? (
          <p className="console-loading" role="status" aria-live="polite">
            Loading invoices…
          </p>
        ) : rows.length === 0 ? (
          <p className="console-empty">
            No invoices yet — invoices generated from stays and reservations will
            appear here.
          </p>
        ) : filtered.length === 0 ? (
          <p className="console-empty">No invoices match your search or filters.</p>
        ) : (
          <>
            <table className="console-table">
              <caption className="console-sr-only">Invoices</caption>
              <thead>
                <tr>
                  <th scope="col">Invoice</th>
                  <th scope="col">Guest</th>
                  <th scope="col">Property / Unit</th>
                  <th scope="col">Source</th>
                  <th scope="col">Stay dates</th>
                  <th scope="col">Invoice date</th>
                  <th scope="col">Total</th>
                  <th scope="col">Paid</th>
                  <th scope="col">Balance</th>
                  <th scope="col">Status</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((s) => {
                  const cur = s.currency || FALLBACK_CURRENCY;
                  const money = (minor: string): string => formatMoney(Number(minor), cur);
                  const balance = balanceMinor(s);
                  const canManage = s.invoiceId !== null;
                  const statusCell =
                    s.lifecycle === "archived" ? (
                      <span className="avos-status avos-status--pending">Archived</span>
                    ) : s.lifecycle === "void" ? (
                      <span className="avos-status avos-status--critical">Void</span>
                    ) : (
                      <span className={paymentStatusPill(s.paymentStatus)}>
                        {paymentStatusLabel(s.paymentStatus)}
                      </span>
                    );
                  const menuActions: readonly RowMenuAction[] = [
                    {
                      key: "view",
                      label: "View invoice",
                      onClick: () => setDetailStay(s),
                      disabled: !canManage,
                    },
                    {
                      key: "download",
                      label: "Download PDF",
                      onClick: () => void handleDownload(s),
                      disabled: !canManage,
                    },
                    {
                      key: "email",
                      label: "Send by Email",
                      onClick: () => setEmailStay(s),
                      disabled: !canManage,
                    },
                    {
                      key: "whatsapp",
                      label: "Send by WhatsApp",
                      onClick: () => void handleWhatsApp(s),
                      disabled: !canManage,
                    },
                    {
                      key: "edit",
                      label: canManage ? "Edit" : "Create invoice",
                      onClick: () => onInvoice(s),
                    },
                    {
                      key: "lifecycle",
                      label: s.lifecycle === "archived" ? "Restore" : "Archive",
                      onClick: () => {
                        if (s.lifecycle === "archived") {
                          if (s.invoiceId === null) return;
                          setNote(null);
                          void (async (): Promise<void> => {
                            const result = await apiAccountsInvoiceLifecycle({
                              invoiceId: s.invoiceId as string,
                              lifecycle: "issued",
                            });
                            const err = asError(result);
                            if (err !== null) {
                              setNote({ severity: "error", model: toUserMessage(err) });
                              return;
                            }
                            onRefresh("Invoice restored.");
                          })();
                        } else {
                          setConfirmError(null);
                          setConfirm({ stay: s, kind: "archive" });
                        }
                      },
                      disabled: !canManage,
                    },
                    {
                      key: "void",
                      label: "Void",
                      onClick: () => {
                        setConfirmError(null);
                        setConfirm({ stay: s, kind: "void" });
                      },
                      disabled: !canManage || s.lifecycle === "void",
                    },
                    {
                      key: "delete",
                      label: "Delete",
                      danger: true,
                      onClick: () => {
                        setConfirmError(null);
                        setConfirm({ stay: s, kind: "delete" });
                      },
                      disabled: !canManage,
                    },
                  ];
                  return (
                    <tr key={s.reservationId}>
                      <td>
                        <span className="bookings-guest">
                          {s.invoiceNo ?? (canManage ? "—" : "Not issued")}
                        </span>
                      </td>
                      <td>
                        <span className="bookings-guest">{s.guestName ?? "Guest"}</span>
                        {!s.hasInvoice ? (
                          <span className="prop-help">derived from reservation</span>
                        ) : null}
                      </td>
                      <td>
                        {s.propertyName ?? "—"}
                        {s.unitName !== null && s.unitName.trim() !== "" ? (
                          <span className="bookings-code">{s.unitName}</span>
                        ) : null}
                      </td>
                      <td>{s.channel ?? "Direct"}</td>
                      <td>
                        <span className="bookings-dates">
                          {formatDate(s.checkIn)} → {formatDate(s.checkOut)}
                        </span>
                      </td>
                      <td>{formatDate(s.issuedOn)}</td>
                      <td className="recon-num">{money(s.grossMinor)}</td>
                      <td className="recon-num">{money(s.paidMinor)}</td>
                      <td className={balance > 0 ? "recon-num accounts-ledger-neg" : "recon-num"}>
                        {formatMoney(balance, cur)}
                      </td>
                      <td>{statusCell}</td>
                      <td>
                        <div className="accounts-actions">
                          <button
                            type="button"
                            className="console-btn console-btn--ghost"
                            disabled={!canManage}
                            onClick={() => void handleDownload(s)}
                          >
                            Download
                          </button>
                          <RowMenu actions={menuActions} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {pageCount > 1 ? (
              <div className="accounts-pagination">
                <button
                  type="button"
                  className="console-btn console-btn--ghost"
                  disabled={safePage <= 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  ‹ Prev
                </button>
                <span className="accounts-pagination__label">
                  Page {safePage + 1} of {pageCount}
                </span>
                <button
                  type="button"
                  className="console-btn console-btn--ghost"
                  disabled={safePage >= pageCount - 1}
                  onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                >
                  Next ›
                </button>
              </div>
            ) : null}
          </>
        )}
      </section>

      {detailStay !== null ? (
        <InvoiceDetailDrawer
          stay={detailStay}
          onClose={() => setDetailStay(null)}
          onDownload={(s) => void handleDownload(s)}
          onEmail={(s) => setEmailStay(s)}
          onWhatsApp={(s) => void handleWhatsApp(s)}
          onEdit={(s) => {
            setDetailStay(null);
            onInvoice(s);
          }}
          onLifecycle={(s, kind) => {
            setConfirmError(null);
            setConfirm({ stay: s, kind });
          }}
        />
      ) : null}

      {emailStay !== null ? (
        <SendEmailModal
          stay={emailStay}
          onClose={() => setEmailStay(null)}
          onSent={(message) => {
            setEmailStay(null);
            setNote({
              severity: "status",
              model: { what: message, why: "The invoice was sent.", next: "" },
            });
          }}
        />
      ) : null}

      {confirm !== null ? (
        <ConfirmActionModal
          kind={confirm.kind}
          busy={confirmBusy}
          error={confirmError}
          onConfirm={() => void confirmAction()}
          onClose={() => {
            setConfirm(null);
            setConfirmError(null);
          }}
        />
      ) : null}
    </>
  );
}

// ===========================================================================
// Expenses tab
// ===========================================================================

function ExpenseModal({
  propertyId,
  onClose,
  onCreated,
}: {
  readonly propertyId: string | undefined;
  readonly onClose: () => void;
  readonly onCreated: (message: string) => void;
}): ReactElement {
  const [date, setDate] = useState<string>("");
  const [category, setCategory] = useState<string>(EXPENSE_CATEGORIES[0]);
  const [expNote, setExpNote] = useState<string>("");
  const [amount, setAmount] = useState<string>("");
  const [gstRate, setGstRate] = useState<number>(0);
  const [isDirect, setIsDirect] = useState<boolean>(false);
  const [claimItc, setClaimItc] = useState<boolean>(true);
  const [paidAccount, setPaidAccount] = useState<Method>("cash");
  const [method, setMethod] = useState<Method>("cash");
  const [submitting, setSubmitting] = useState(false);
  const [formNote, setFormNote] = useState<Note | null>(null);

  const handleSubmit = useCallback(
    async (e: FormEvent): Promise<void> => {
      e.preventDefault();
      setFormNote(null);
      if (date.trim() === "") {
        setFormNote({
          severity: "error",
          model: validationMessage(
            "Date is required",
            "An expense needs a date.",
            "Pick the date the expense was incurred.",
          ),
        });
        return;
      }
      const amountMinor = parseMoneyToMinor(amount);
      if (amountMinor === null || amountMinor <= 0) {
        setFormNote({
          severity: "error",
          model: validationMessage(
            "Amount is invalid",
            "The expense amount is not a positive amount.",
            "Enter an amount greater than zero.",
          ),
        });
        return;
      }
      setSubmitting(true);
      try {
        const result = await apiAccountsCreateExpense({
          ...(propertyId !== undefined ? { propertyId } : {}),
          date,
          category,
          ...(expNote.trim() !== "" ? { note: expNote.trim() } : {}),
          amountMinor,
          gstRate,
          method,
          isDirect,
          claimItc,
          paidAccount,
        });
        const err = asError(result);
        if (err !== null) {
          setFormNote({ severity: "error", model: toUserMessage(err) });
          return;
        }
        onCreated("Expense recorded.");
      } catch (caught) {
        setFormNote({
          severity: "error",
          model: validationMessage(
            "Could not record the expense",
            caught instanceof Error ? caught.message : "The request failed.",
            "Check your connection and try again.",
          ),
        });
      } finally {
        setSubmitting(false);
      }
    },
    [
      date,
      amount,
      propertyId,
      category,
      expNote,
      gstRate,
      method,
      isDirect,
      claimItc,
      paidAccount,
      onCreated,
    ],
  );

  return (
    <div
      className="console-modal-scrim"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="console-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Record an expense"
      >
        <header className="console-modal__header">
          <h2 className="console-modal__title">Record an expense</h2>
          <button
            type="button"
            className="console-btn console-btn--ghost console-modal__close"
            aria-label="Close"
            onClick={onClose}
          >
            ✕
          </button>
        </header>
        <div className="console-modal__body">
          {formNote !== null ? (
            <UserMessage message={formNote.model} severity={formNote.severity} />
          ) : null}
          <form className="console-form" noValidate onSubmit={handleSubmit}>
            <div className="console-form__row">
              <div className="console-field">
                <label htmlFor="exp-date">Date</label>
                <input
                  id="exp-date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </div>
              <div className="console-field">
                <label htmlFor="exp-category">Category</label>
                <select
                  id="exp-category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                >
                  {EXPENSE_CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="console-form__row">
              <div className="console-field">
                <label htmlFor="exp-amount">Amount (GST-inclusive)</label>
                <input
                  id="exp-amount"
                  type="text"
                  inputMode="decimal"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                />
              </div>
              <div className="console-field">
                <label htmlFor="exp-gst">GST rate</label>
                <select
                  id="exp-gst"
                  value={String(gstRate)}
                  onChange={(e) => setGstRate(Number(e.target.value))}
                >
                  {GST_RATES.map((r) => (
                    <option key={r} value={String(r)}>
                      {r}%
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="console-form__row">
              <div className="console-field">
                <label htmlFor="exp-paid">Paid through</label>
                <select
                  id="exp-paid"
                  value={paidAccount}
                  onChange={(e) => setPaidAccount(e.target.value as Method)}
                >
                  {METHODS.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="console-field">
                <label htmlFor="exp-method">Method</label>
                <select
                  id="exp-method"
                  value={method}
                  onChange={(e) => setMethod(e.target.value as Method)}
                >
                  {METHODS.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="console-form__row">
              <div className="console-field console-field--checkbox">
                <label htmlFor="exp-direct">
                  <input
                    id="exp-direct"
                    type="checkbox"
                    checked={isDirect}
                    onChange={(e) => setIsDirect(e.target.checked)}
                  />
                  Direct cost
                </label>
              </div>
              <div className="console-field console-field--checkbox">
                <label htmlFor="exp-itc">
                  <input
                    id="exp-itc"
                    type="checkbox"
                    checked={claimItc}
                    onChange={(e) => setClaimItc(e.target.checked)}
                  />
                  Claim ITC
                </label>
              </div>
            </div>
            <div className="console-form__row">
              <div className="console-field" style={{ flex: "1 1 100%" }}>
                <label htmlFor="exp-note">Note</label>
                <input
                  id="exp-note"
                  type="text"
                  value={expNote}
                  onChange={(e) => setExpNote(e.target.value)}
                  placeholder="Optional"
                />
              </div>
            </div>
            <div className="console-modal__actions">
              <button
                type="button"
                className="console-btn console-btn--ghost"
                onClick={onClose}
              >
                Cancel
              </button>
              <button type="submit" className="console-btn" disabled={submitting}>
                {submitting ? "Recording…" : "Record expense"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function ExpensesTab({
  rows,
  loading,
  propertyId,
  onCreated,
}: {
  readonly rows: readonly AccountsExpenseRow[];
  readonly loading: boolean;
  readonly propertyId: string | undefined;
  readonly onCreated: (message: string) => void;
}): ReactElement {
  const [modalOpen, setModalOpen] = useState(false);

  const totals = useMemo(() => {
    let amountTotal = 0;
    let itcTotal = 0;
    let cur = FALLBACK_CURRENCY;
    for (const r of rows) {
      amountTotal += Number(r.amountMinor);
      itcTotal += Number(r.itcMinor);
      cur = r.currency || cur;
    }
    return { amountTotal, itcTotal, cur };
  }, [rows]);

  const handleCreated = useCallback(
    (message: string): void => {
      setModalOpen(false);
      onCreated(message);
    },
    [onCreated],
  );

  return (
    <>
      <section className="console-panel" aria-label="Expenses">
        <div className="accounts-panel-head">
          <h2 className="console-panel__title">Expenses</h2>
          <button
            type="button"
            className="console-btn"
            onClick={() => setModalOpen(true)}
          >
            + Add expense
          </button>
        </div>
        {loading ? (
          <p className="console-loading" role="status" aria-live="polite">
            Loading expenses…
          </p>
        ) : rows.length === 0 ? (
          <p className="console-empty">No expenses recorded yet.</p>
        ) : (
          <table className="console-table">
            <caption className="console-sr-only">Recorded expenses</caption>
            <thead>
              <tr>
                <th scope="col">Date</th>
                <th scope="col">Category</th>
                <th scope="col">Note</th>
                <th scope="col">Type</th>
                <th scope="col">Claim ITC</th>
                <th scope="col">Paid through</th>
                <th scope="col">Amount</th>
                <th scope="col">GST rate</th>
                <th scope="col">ITC</th>
                <th scope="col">Method</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const cur = r.currency || FALLBACK_CURRENCY;
                return (
                  <tr key={r.id}>
                    <td>{formatDate(r.date)}</td>
                    <td>{r.category}</td>
                    <td>{r.note ?? "—"}</td>
                    <td>{r.isDirect ? "Direct" : "Operating"}</td>
                    <td>{r.claimItc ? "Yes" : "No"}</td>
                    <td>
                      {r.paidAccount !== null
                        ? methodLabel(r.paidAccount)
                        : "—"}
                    </td>
                    <td className="recon-num">
                      {formatMoney(Number(r.amountMinor), cur)}
                    </td>
                    <td className="recon-num">{r.gstRate}%</td>
                    <td className="recon-num">
                      {formatMoney(Number(r.itcMinor), cur)}
                    </td>
                    <td>{methodLabel(r.method)}</td>
                  </tr>
                );
              })}
              <tr className="accounts-total-row">
                <td colSpan={6}>Total</td>
                <td className="recon-num">
                  {formatMoney(totals.amountTotal, totals.cur)}
                </td>
                <td />
                <td className="recon-num">
                  {formatMoney(totals.itcTotal, totals.cur)}
                </td>
                <td />
              </tr>
            </tbody>
          </table>
        )}
      </section>

      {modalOpen ? (
        <ExpenseModal
          propertyId={propertyId}
          onClose={() => setModalOpen(false)}
          onCreated={handleCreated}
        />
      ) : null}
    </>
  );
}

// ===========================================================================
// Cash & Banks tab
// ===========================================================================
//
// A proper cash-and-bank management workspace with four internal sub-tabs:
// Overview · Cashbook · Bank Accounts · Movements. The global header property
// selector scopes everything; each sub-view loads its own data in an effect
// keyed on (activeSub, propertyId, a local refreshToken). Every add/edit action
// sits behind a CTA that opens a modal (short) or a right-side drawer (long).

/** The Cash & Banks internal sub-tabs, in order. */
const CASH_SUBS = [
  { key: "overview", label: "Overview" },
  { key: "cashbook", label: "Cashbook" },
  { key: "banks", label: "Bank Accounts" },
  { key: "movements", label: "Movements" },
] as const;

type CashSubKey = (typeof CASH_SUBS)[number]["key"];

/** The record-movement types (type-first modal). */
const MOVEMENT_TYPES = [
  { key: "deposit", label: "Cash deposit to bank" },
  { key: "withdrawal", label: "Bank withdrawal / cash top-up" },
  { key: "transfer", label: "Bank transfer" },
  { key: "opening", label: "Opening balance / float" },
  { key: "adjustment", label: "Adjustment" },
  { key: "count", label: "Cash count" },
] as const;

type MovementType = (typeof MOVEMENT_TYPES)[number]["key"];

/** Bank account types offered on the create/edit form. */
const BANK_ACCOUNT_TYPES = [
  { value: "current", label: "Current" },
  { value: "savings", label: "Savings" },
  { value: "cash", label: "Cash" },
  { value: "other", label: "Other" },
] as const;

/** Bank account purpose options (plain-language). */
const BANK_PURPOSES = [
  "Guest Collections",
  "Operating Expenses",
  "Owner Payout",
  "Petty Cash Funding",
  "General",
] as const;

/** Human label for a bank account type value. */
function bankAccountTypeLabel(value: string): string {
  return BANK_ACCOUNT_TYPES.find((t) => t.value === value)?.label ?? value;
}

/** Masked account number for display (never the full number). */
function maskedAccount(last4: string | null): string {
  return last4 !== null && last4.trim() !== "" ? `•••• ${last4}` : "—";
}

// ---------------------------------------------------------------------------
// Count Cash modal
// ---------------------------------------------------------------------------

/** The denomination notes offered in the count grid (₹ face values). */
const CASH_DENOMS = [500, 200, 100, 50, 20, 10] as const;

function CountCashModal({
  propertyId,
  properties,
  cashInHand,
  currency,
  onClose,
  onSaved,
  onRecordAdjustment,
}: {
  readonly propertyId: string | undefined;
  readonly properties: readonly Property[];
  readonly cashInHand: readonly AccountsCashInHandRow[];
  readonly currency: string;
  readonly onClose: () => void;
  readonly onSaved: (message: string) => void;
  readonly onRecordAdjustment: (propertyId: string) => void;
}): ReactElement {
  // Default the property to the scoped one, else the first counted row.
  const [prop, setProp] = useState<string>(
    propertyId ?? cashInHand[0]?.propertyId ?? "",
  );
  const [date, setDate] = useState<string>("");
  const [counts, setCounts] = useState<Record<number, string>>({});
  const [coins, setCoins] = useState<string>("");
  const [counted, setCounted] = useState<string>("");
  const [useDenoms, setUseDenoms] = useState<boolean>(false);
  const [cashNote, setCashNote] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [savedVariance, setSavedVariance] = useState<number | null>(null);
  const [formNote, setFormNote] = useState<Note | null>(null);

  // The expected cash for the chosen property (read-only, from cash-in-hand).
  const expectedRow = cashInHand.find((r) => r.propertyId === prop) ?? null;
  const expectedMinor =
    expectedRow !== null ? Number(expectedRow.expectedMinor) : 0;
  const cur = expectedRow?.currency || currency || FALLBACK_CURRENCY;

  // Sum the denomination grid (minor units) when it is used.
  const denomTotalMinor = useDenoms
    ? CASH_DENOMS.reduce((sum, face) => {
        const n = Number(counts[face] ?? "0");
        return sum + (Number.isFinite(n) && n > 0 ? face * n * 100 : 0);
      }, 0) + (parseMoneyToMinor(coins) ?? 0)
    : 0;

  // The effective counted amount: the denomination sum when the grid is used,
  // otherwise the free-entry value.
  const countedMinor = useDenoms ? denomTotalMinor : parseMoneyToMinor(counted);
  const varianceMinor =
    countedMinor !== null ? countedMinor - expectedMinor : null;

  const handleSubmit = useCallback(
    async (e: FormEvent): Promise<void> => {
      e.preventDefault();
      setFormNote(null);
      if (prop === "") {
        setFormNote({
          severity: "error",
          model: validationMessage(
            "Property is required",
            "A cash count is scoped to one property.",
            "Choose the property you counted.",
          ),
        });
        return;
      }
      if (date.trim() === "") {
        setFormNote({
          severity: "error",
          model: validationMessage(
            "Count date is required",
            "A cash count needs a date.",
            "Pick the date you counted the cash.",
          ),
        });
        return;
      }
      const finalCounted = useDenoms ? denomTotalMinor : parseMoneyToMinor(counted);
      if (finalCounted === null || finalCounted < 0) {
        setFormNote({
          severity: "error",
          model: validationMessage(
            "Counted cash is invalid",
            "The counted amount is not a valid amount.",
            "Enter the cash you counted, or use the denomination grid.",
          ),
        });
        return;
      }
      // Build the optional denominations map only when the grid is used.
      const denominations: Record<string, number> = {};
      if (useDenoms) {
        for (const face of CASH_DENOMS) {
          const n = Number(counts[face] ?? "0");
          if (Number.isFinite(n) && n > 0) denominations[String(face)] = n;
        }
        const coinMinor = parseMoneyToMinor(coins);
        if (coinMinor !== null && coinMinor > 0) denominations["coins"] = coinMinor;
      }
      setSubmitting(true);
      try {
        const result = await apiAccountsCashCount({
          propertyId: prop,
          date,
          countedMinor: finalCounted,
          ...(useDenoms && Object.keys(denominations).length > 0
            ? { denominations }
            : {}),
          ...(cashNote.trim() !== "" ? { note: cashNote.trim() } : {}),
        });
        const err = asError(result);
        if (err !== null) {
          setFormNote({ severity: "error", model: toUserMessage(err) });
          return;
        }
        const variance = finalCounted - expectedMinor;
        if (variance !== 0) {
          // Keep the modal open with a subtle adjustment affordance.
          setSavedVariance(variance);
          onSaved("Cash count saved.");
        } else {
          onSaved("Cash count saved — cash matches the books.");
        }
      } catch (caught) {
        setFormNote({
          severity: "error",
          model: validationMessage(
            "Could not save the cash count",
            caught instanceof Error ? caught.message : "The request failed.",
            "Check your connection and try again.",
          ),
        });
      } finally {
        setSubmitting(false);
      }
    },
    [prop, date, useDenoms, denomTotalMinor, counted, counts, coins, cashNote, expectedMinor, onSaved],
  );

  const varianceLabel = (v: number): string => {
    if (v === 0) return "Cash matches the books.";
    return v < 0
      ? `Cash shortage ${formatMoney(-v, cur)}`
      : `Cash excess ${formatMoney(v, cur)}`;
  };

  return (
    <div
      className="console-modal-scrim"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="console-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Count cash"
      >
        <header className="console-modal__header">
          <h2 className="console-modal__title">Count cash</h2>
          <button
            type="button"
            className="console-btn console-btn--ghost console-modal__close"
            aria-label="Close"
            onClick={onClose}
          >
            ✕
          </button>
        </header>
        <div className="console-modal__body">
          {formNote !== null ? (
            <UserMessage message={formNote.model} severity={formNote.severity} />
          ) : null}
          {savedVariance !== null ? (
            <div className="accounts-cash-variancebox">
              <p className="recon-caption">{varianceLabel(savedVariance)}</p>
              <p className="recon-caption">
                This count does not change the book balance. If the difference is
                a real, verified discrepancy you can record an adjustment.
              </p>
              <button
                type="button"
                className="console-btn console-btn--ghost"
                onClick={() => onRecordAdjustment(prop)}
              >
                Record adjustment
              </button>
            </div>
          ) : (
            <form className="console-form" noValidate onSubmit={handleSubmit}>
              <div className="console-form__row">
                <div className="console-field">
                  <label htmlFor="count-property">Property *</label>
                  <select
                    id="count-property"
                    value={prop}
                    onChange={(e) => setProp(e.target.value)}
                  >
                    <option value="">Select a property</option>
                    {properties.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="console-field">
                  <label htmlFor="count-date">Count date *</label>
                  <input
                    id="count-date"
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                  />
                </div>
              </div>

              <div className="console-form__row">
                <div className="console-field">
                  <label className="console-field__label-static">Expected cash</label>
                  <p className="accounts-cash-expected recon-num">
                    {formatMoney(expectedMinor, cur)}
                  </p>
                </div>
              </div>

              <label className="accounts-cash-denomtoggle">
                <input
                  type="checkbox"
                  checked={useDenoms}
                  onChange={(e) => setUseDenoms(e.target.checked)}
                />
                <span>Count by denominations</span>
              </label>

              {useDenoms ? (
                <div className="accounts-denomgrid">
                  {CASH_DENOMS.map((face) => (
                    <div className="accounts-denomgrid__row" key={face}>
                      <span className="accounts-denomgrid__face">₹{face}</span>
                      <span className="accounts-denomgrid__x">×</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        aria-label={`Number of ₹${face} notes`}
                        value={counts[face] ?? ""}
                        onChange={(e) =>
                          setCounts((prev) => ({ ...prev, [face]: e.target.value }))
                        }
                        placeholder="0"
                      />
                      <span className="accounts-denomgrid__sub recon-num">
                        {formatMoney(
                          face * (Number(counts[face] ?? "0") || 0) * 100,
                          cur,
                        )}
                      </span>
                    </div>
                  ))}
                  <div className="accounts-denomgrid__row">
                    <span className="accounts-denomgrid__face">Coins</span>
                    <span className="accounts-denomgrid__x" aria-hidden="true" />
                    <input
                      type="text"
                      inputMode="decimal"
                      aria-label="Coins amount"
                      value={coins}
                      onChange={(e) => setCoins(e.target.value)}
                      placeholder="0.00"
                    />
                    <span className="accounts-denomgrid__sub recon-num">
                      {formatMoney(parseMoneyToMinor(coins) ?? 0, cur)}
                    </span>
                  </div>
                </div>
              ) : null}

              <div className="console-form__row">
                <div className="console-field">
                  <label htmlFor="count-counted">Counted cash *</label>
                  <input
                    id="count-counted"
                    type="text"
                    inputMode="decimal"
                    value={useDenoms ? (denomTotalMinor / 100).toFixed(2) : counted}
                    onChange={(e) => setCounted(e.target.value)}
                    disabled={useDenoms}
                    placeholder="0.00"
                  />
                </div>
              </div>

              <p
                className={
                  varianceMinor !== null && varianceMinor !== 0
                    ? "accounts-effect-preview accounts-effect-preview--warn"
                    : "accounts-effect-preview"
                }
              >
                <span className="accounts-effect-preview__label">Variance</span>
                <span>
                  {varianceMinor !== null ? varianceLabel(varianceMinor) : "—"}
                </span>
                {varianceMinor !== null ? (
                  <span
                    className={
                      varianceMinor === 0
                        ? "avos-status avos-status--ok"
                        : "avos-status avos-status--attention"
                    }
                  >
                    {varianceMinor === 0 ? "Matched" : "Variance"}
                  </span>
                ) : null}
              </p>

              <div className="console-form__row">
                <div className="console-field" style={{ flex: "1 1 100%" }}>
                  <label htmlFor="count-note">Notes</label>
                  <input
                    id="count-note"
                    type="text"
                    value={cashNote}
                    onChange={(e) => setCashNote(e.target.value)}
                    placeholder="Optional"
                  />
                </div>
              </div>

              <div className="console-modal__actions">
                <button
                  type="button"
                  className="console-btn console-btn--ghost"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button type="submit" className="console-btn" disabled={submitting}>
                  {submitting ? "Saving…" : "Save count"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Record Movement modal (type-first)
// ---------------------------------------------------------------------------

/** Optional preset for the record-movement modal (e.g. from a bank drawer). */
interface MovementPreset {
  readonly type: MovementType;
  readonly bankAccountId?: string;
}

function RecordMovementModal({
  propertyId,
  properties,
  bankAccounts,
  currency,
  preset,
  onClose,
  onCreated,
}: {
  readonly propertyId: string | undefined;
  readonly properties: readonly Property[];
  readonly bankAccounts: readonly AccountsBankAccountRow[];
  readonly currency: string;
  readonly preset: MovementPreset | null;
  readonly onClose: () => void;
  readonly onCreated: (message: string) => void;
}): ReactElement {
  const [type, setType] = useState<MovementType>(preset?.type ?? "deposit");
  const [prop, setProp] = useState<string>(propertyId ?? "");
  const [date, setDate] = useState<string>("");
  const [amount, setAmount] = useState<string>("");
  const [bankId, setBankId] = useState<string>(preset?.bankAccountId ?? "");
  const [fromBankId, setFromBankId] = useState<string>(preset?.bankAccountId ?? "");
  const [toBankId, setToBankId] = useState<string>("");
  const [reference, setReference] = useState<string>("");
  const [mode, setMode] = useState<string>("");
  const [openingTarget, setOpeningTarget] = useState<string>("cash");
  const [adjTarget, setAdjTarget] = useState<string>("cash");
  const [direction, setDirection] = useState<"increase" | "decrease">("increase");
  const [reason, setReason] = useState<string>("");
  const [movNote, setMovNote] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [formNote, setFormNote] = useState<Note | null>(null);

  const cur = currency || FALLBACK_CURRENCY;
  const amountMinor = parseMoneyToMinor(amount);
  const bankName = (id: string): string =>
    bankAccounts.find((b) => b.id === id)?.name ?? "the bank account";
  const previewAmt = amountMinor !== null ? formatMoney(amountMinor, cur) : "₹0.00";

  // Live effect-preview line per movement type.
  const preview = ((): string | null => {
    switch (type) {
      case "deposit":
        return `Cash in hand − ${previewAmt} · ${bankName(bankId)} + ${previewAmt} (transfer — no effect on revenue/expenses)`;
      case "withdrawal":
        return `${bankName(fromBankId || bankId)} − ${previewAmt} · Cash in hand + ${previewAmt}`;
      case "transfer":
        return `${bankName(fromBankId)} − ${previewAmt} · ${bankName(toBankId)} + ${previewAmt}`;
      case "opening":
        return openingTarget === "cash"
          ? `Cash in hand opening balance set to ${previewAmt}`
          : `${bankName(openingTarget)} opening balance set to ${previewAmt}`;
      case "adjustment": {
        const target = adjTarget === "cash" ? "Cash in hand" : bankName(adjTarget);
        return direction === "increase"
          ? `${target} + ${previewAmt}`
          : `${target} − ${previewAmt}`;
      }
      default:
        return null;
    }
  })();

  const fail = (what: string, why: string, next: string): void => {
    setFormNote({ severity: "error", model: validationMessage(what, why, next) });
  };

  const requireAmount = (): number | null => {
    if (amountMinor === null || amountMinor <= 0) {
      fail(
        "Amount is invalid",
        "The amount is not a positive amount.",
        "Enter an amount greater than zero.",
      );
      return null;
    }
    return amountMinor;
  };

  const requireDate = (): boolean => {
    if (date.trim() === "") {
      fail("Date is required", "A movement needs a date.", "Pick the date.");
      return false;
    }
    return true;
  };

  const handleSubmit = useCallback(
    async (e: FormEvent): Promise<void> => {
      e.preventDefault();
      setFormNote(null);
      try {
        setSubmitting(true);
        if (type === "deposit") {
          if (!requireDate()) return;
          const amt = requireAmount();
          if (amt === null) return;
          if (bankId === "") {
            fail("Bank account is required", "Choose the bank the cash goes into.", "Pick a bank account.");
            return;
          }
          const result = await apiAccountsCashDeposit({
            ...(prop !== "" ? { propertyId: prop } : {}),
            date,
            amountMinor: amt,
            bankAccountId: bankId,
            ...(reference.trim() !== "" ? { reference: reference.trim() } : {}),
            ...(movNote.trim() !== "" ? { note: movNote.trim() } : {}),
          });
          const err = asError(result);
          if (err !== null) { setFormNote({ severity: "error", model: toUserMessage(err) }); return; }
          onCreated("Cash deposit recorded.");
        } else if (type === "withdrawal") {
          if (!requireDate()) return;
          const amt = requireAmount();
          if (amt === null) return;
          if (bankId === "") {
            fail("Bank account is required", "Choose the bank the cash comes from.", "Pick a bank account.");
            return;
          }
          const result = await apiAccountsBankWithdrawal({
            ...(prop !== "" ? { propertyId: prop } : {}),
            date,
            amountMinor: amt,
            bankAccountId: bankId,
            ...(mode.trim() !== "" ? { mode: mode.trim() } : {}),
            ...(reference.trim() !== "" ? { reference: reference.trim() } : {}),
            ...(movNote.trim() !== "" ? { note: movNote.trim() } : {}),
          });
          const err = asError(result);
          if (err !== null) { setFormNote({ severity: "error", model: toUserMessage(err) }); return; }
          onCreated("Bank withdrawal recorded.");
        } else if (type === "transfer") {
          if (!requireDate()) return;
          const amt = requireAmount();
          if (amt === null) return;
          if (fromBankId === "" || toBankId === "") {
            fail("Both accounts are required", "A transfer needs a source and a destination.", "Pick both bank accounts.");
            return;
          }
          if (fromBankId === toBankId) {
            fail("Accounts must differ", "A transfer cannot be to the same account.", "Pick a different destination.");
            return;
          }
          const result = await apiAccountsBankTransfer({
            fromBankAccountId: fromBankId,
            toBankAccountId: toBankId,
            ...(prop !== "" ? { propertyId: prop } : {}),
            date,
            amountMinor: amt,
            ...(mode.trim() !== "" ? { mode: mode.trim() } : {}),
            ...(reference.trim() !== "" ? { reference: reference.trim() } : {}),
            ...(movNote.trim() !== "" ? { note: movNote.trim() } : {}),
          });
          const err = asError(result);
          if (err !== null) { setFormNote({ severity: "error", model: toUserMessage(err) }); return; }
          onCreated("Bank transfer recorded.");
        } else if (type === "opening") {
          if (!requireDate()) return;
          const amt = requireAmount();
          if (amt === null) return;
          const isBank = openingTarget !== "cash";
          const result = await apiAccountsOpeningBalance({
            target: isBank ? "bank" : "cash",
            ...(isBank ? { bankAccountId: openingTarget } : {}),
            ...(!isBank && prop !== "" ? { propertyId: prop } : {}),
            date,
            amountMinor: amt,
            ...(movNote.trim() !== "" ? { note: movNote.trim() } : {}),
          });
          const err = asError(result);
          if (err !== null) { setFormNote({ severity: "error", model: toUserMessage(err) }); return; }
          onCreated("Opening balance recorded.");
        } else if (type === "adjustment") {
          if (!requireDate()) return;
          const amt = requireAmount();
          if (amt === null) return;
          if (reason.trim() === "") {
            fail("Reason is required", "An adjustment must record why it was made.", "Enter a reason.");
            return;
          }
          const isBank = adjTarget !== "cash";
          const result = await apiAccountsAdjustment({
            target: isBank ? "bank" : "cash",
            ...(isBank ? { bankAccountId: adjTarget } : {}),
            ...(!isBank && prop !== "" ? { propertyId: prop } : {}),
            date,
            direction,
            amountMinor: amt,
            reason: reason.trim(),
            ...(reference.trim() !== "" ? { reference: reference.trim() } : {}),
          });
          const err = asError(result);
          if (err !== null) { setFormNote({ severity: "error", model: toUserMessage(err) }); return; }
          onCreated("Adjustment recorded.");
        }
      } catch (caught) {
        setFormNote({
          severity: "error",
          model: validationMessage(
            "Could not record the movement",
            caught instanceof Error ? caught.message : "The request failed.",
            "Check your connection and try again.",
          ),
        });
      } finally {
        setSubmitting(false);
      }
    },
    [
      type, prop, date, amount, amountMinor, bankId, fromBankId, toBankId,
      reference, mode, openingTarget, adjTarget, direction, reason, movNote,
      onCreated,
    ],
  );

  const activeBanks = bankAccounts.filter((b) => b.status === "active");

  return (
    <div
      className="console-modal-scrim"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="console-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Record a movement"
      >
        <header className="console-modal__header">
          <h2 className="console-modal__title">Record a movement</h2>
          <button
            type="button"
            className="console-btn console-btn--ghost console-modal__close"
            aria-label="Close"
            onClick={onClose}
          >
            ✕
          </button>
        </header>
        <div className="console-modal__body">
          {formNote !== null ? (
            <UserMessage message={formNote.model} severity={formNote.severity} />
          ) : null}
          <form className="console-form" noValidate onSubmit={handleSubmit}>
            <div className="console-form__row">
              <div className="console-field" style={{ flex: "1 1 100%" }}>
                <label htmlFor="mov-type">Movement type *</label>
                <select
                  id="mov-type"
                  value={type}
                  onChange={(e) => {
                    setType(e.target.value as MovementType);
                    setFormNote(null);
                  }}
                >
                  {MOVEMENT_TYPES.map((t) => (
                    <option key={t.key} value={t.key}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* ---- Cash deposit to bank ---- */}
            {type === "deposit" ? (
              <>
                <div className="console-form__row">
                  <div className="console-field">
                    <label htmlFor="mov-prop">Property</label>
                    <select id="mov-prop" value={prop} onChange={(e) => setProp(e.target.value)}>
                      <option value="">Whole portfolio</option>
                      {properties.map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="console-field">
                    <label htmlFor="mov-date">Date *</label>
                    <input id="mov-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
                  </div>
                </div>
                <div className="console-form__row">
                  <div className="console-field">
                    <label htmlFor="mov-amount">Amount *</label>
                    <input id="mov-amount" type="text" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" />
                  </div>
                  <div className="console-field">
                    <label htmlFor="mov-bank">Deposit into bank account *</label>
                    <select id="mov-bank" value={bankId} onChange={(e) => setBankId(e.target.value)}>
                      <option value="">Select a bank account</option>
                      {activeBanks.map((b) => (
                        <option key={b.id} value={b.id}>{b.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="console-form__row">
                  <div className="console-field">
                    <label htmlFor="mov-ref">Deposit reference / slip</label>
                    <input id="mov-ref" type="text" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Optional" />
                  </div>
                  <div className="console-field">
                    <label htmlFor="mov-note">Notes</label>
                    <input id="mov-note" type="text" value={movNote} onChange={(e) => setMovNote(e.target.value)} placeholder="Optional" />
                  </div>
                </div>
              </>
            ) : null}

            {/* ---- Bank withdrawal / cash top-up ---- */}
            {type === "withdrawal" ? (
              <>
                <div className="console-form__row">
                  <div className="console-field">
                    <label htmlFor="mov-prop">Property</label>
                    <select id="mov-prop" value={prop} onChange={(e) => setProp(e.target.value)}>
                      <option value="">Whole portfolio</option>
                      {properties.map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="console-field">
                    <label htmlFor="mov-date">Date *</label>
                    <input id="mov-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
                  </div>
                </div>
                <div className="console-form__row">
                  <div className="console-field">
                    <label htmlFor="mov-bank">Withdraw from bank *</label>
                    <select id="mov-bank" value={bankId} onChange={(e) => setBankId(e.target.value)}>
                      <option value="">Select a bank account</option>
                      {activeBanks.map((b) => (
                        <option key={b.id} value={b.id}>{b.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="console-field">
                    <label htmlFor="mov-amount">Amount *</label>
                    <input id="mov-amount" type="text" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" />
                  </div>
                </div>
                <div className="console-form__row">
                  <div className="console-field">
                    <label htmlFor="mov-mode">Mode</label>
                    <select id="mov-mode" value={mode} onChange={(e) => setMode(e.target.value)}>
                      <option value="">Select</option>
                      <option value="cash">Cash</option>
                      <option value="cheque">Cheque</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="console-field">
                    <label htmlFor="mov-ref">Reference</label>
                    <input id="mov-ref" type="text" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Optional" />
                  </div>
                </div>
                <div className="console-form__row">
                  <div className="console-field" style={{ flex: "1 1 100%" }}>
                    <label htmlFor="mov-note">Notes</label>
                    <input id="mov-note" type="text" value={movNote} onChange={(e) => setMovNote(e.target.value)} placeholder="Optional" />
                  </div>
                </div>
              </>
            ) : null}

            {/* ---- Bank transfer ---- */}
            {type === "transfer" ? (
              <>
                <div className="console-form__row">
                  <div className="console-field">
                    <label htmlFor="mov-date">Date *</label>
                    <input id="mov-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
                  </div>
                  <div className="console-field">
                    <label htmlFor="mov-amount">Amount *</label>
                    <input id="mov-amount" type="text" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" />
                  </div>
                </div>
                <div className="console-form__row">
                  <div className="console-field">
                    <label htmlFor="mov-from">From bank account *</label>
                    <select id="mov-from" value={fromBankId} onChange={(e) => setFromBankId(e.target.value)}>
                      <option value="">Select a bank account</option>
                      {activeBanks.map((b) => (
                        <option key={b.id} value={b.id}>{b.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="console-field">
                    <label htmlFor="mov-to">To bank account *</label>
                    <select id="mov-to" value={toBankId} onChange={(e) => setToBankId(e.target.value)}>
                      <option value="">Select a bank account</option>
                      {activeBanks.map((b) => (
                        <option key={b.id} value={b.id}>{b.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="console-form__row">
                  <div className="console-field">
                    <label htmlFor="mov-mode">Mode</label>
                    <select id="mov-mode" value={mode} onChange={(e) => setMode(e.target.value)}>
                      <option value="">Select</option>
                      <option value="UPI">UPI</option>
                      <option value="NEFT">NEFT</option>
                      <option value="RTGS">RTGS</option>
                      <option value="IMPS">IMPS</option>
                      <option value="Cheque">Cheque</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="console-field">
                    <label htmlFor="mov-ref">UTR / Reference</label>
                    <input id="mov-ref" type="text" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Optional" />
                  </div>
                </div>
                <div className="console-form__row">
                  <div className="console-field" style={{ flex: "1 1 100%" }}>
                    <label htmlFor="mov-note">Notes</label>
                    <input id="mov-note" type="text" value={movNote} onChange={(e) => setMovNote(e.target.value)} placeholder="Optional" />
                  </div>
                </div>
              </>
            ) : null}

            {/* ---- Opening balance / float ---- */}
            {type === "opening" ? (
              <>
                <div className="console-form__row">
                  <div className="console-field">
                    <label htmlFor="mov-target">Target</label>
                    <select id="mov-target" value={openingTarget} onChange={(e) => setOpeningTarget(e.target.value)}>
                      <option value="cash">Cash</option>
                      {bankAccounts.map((b) => (
                        <option key={b.id} value={b.id}>{b.name}</option>
                      ))}
                    </select>
                  </div>
                  {openingTarget === "cash" ? (
                    <div className="console-field">
                      <label htmlFor="mov-prop">Property</label>
                      <select id="mov-prop" value={prop} onChange={(e) => setProp(e.target.value)}>
                        <option value="">Whole portfolio</option>
                        {properties.map((p) => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                    </div>
                  ) : null}
                </div>
                <div className="console-form__row">
                  <div className="console-field">
                    <label htmlFor="mov-date">As-of date *</label>
                    <input id="mov-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
                  </div>
                  <div className="console-field">
                    <label htmlFor="mov-amount">Amount *</label>
                    <input id="mov-amount" type="text" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" />
                  </div>
                </div>
                <div className="console-form__row">
                  <div className="console-field" style={{ flex: "1 1 100%" }}>
                    <label htmlFor="mov-note">Reason / Notes</label>
                    <input id="mov-note" type="text" value={movNote} onChange={(e) => setMovNote(e.target.value)} placeholder="Optional" />
                  </div>
                </div>
              </>
            ) : null}

            {/* ---- Adjustment ---- */}
            {type === "adjustment" ? (
              <>
                <p className="accounts-effect-preview accounts-effect-preview--warn">
                  Adjustments change the book balance and should only be used to
                  correct verified discrepancies.
                </p>
                <div className="console-form__row">
                  <div className="console-field">
                    <label htmlFor="mov-target">Target</label>
                    <select id="mov-target" value={adjTarget} onChange={(e) => setAdjTarget(e.target.value)}>
                      <option value="cash">Cash</option>
                      {bankAccounts.map((b) => (
                        <option key={b.id} value={b.id}>{b.name}</option>
                      ))}
                    </select>
                  </div>
                  {adjTarget === "cash" ? (
                    <div className="console-field">
                      <label htmlFor="mov-prop">Property</label>
                      <select id="mov-prop" value={prop} onChange={(e) => setProp(e.target.value)}>
                        <option value="">Whole portfolio</option>
                        {properties.map((p) => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                    </div>
                  ) : null}
                </div>
                <div className="console-form__row">
                  <div className="console-field">
                    <label htmlFor="mov-date">Date *</label>
                    <input id="mov-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
                  </div>
                  <div className="console-field">
                    <label htmlFor="mov-dir">Direction *</label>
                    <select id="mov-dir" value={direction} onChange={(e) => setDirection(e.target.value === "decrease" ? "decrease" : "increase")}>
                      <option value="increase">Increase (+)</option>
                      <option value="decrease">Decrease (−)</option>
                    </select>
                  </div>
                </div>
                <div className="console-form__row">
                  <div className="console-field">
                    <label htmlFor="mov-amount">Amount *</label>
                    <input id="mov-amount" type="text" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" />
                  </div>
                  <div className="console-field">
                    <label htmlFor="mov-ref">Reference</label>
                    <input id="mov-ref" type="text" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Optional" />
                  </div>
                </div>
                <div className="console-form__row">
                  <div className="console-field" style={{ flex: "1 1 100%" }}>
                    <label htmlFor="mov-reason">Reason *</label>
                    <input id="mov-reason" type="text" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why is this adjustment needed?" />
                  </div>
                </div>
              </>
            ) : null}

            {/* ---- Cash count ---- */}
            {type === "count" ? (
              <p className="recon-caption">
                Use the dedicated “Count cash” action to record a physical cash
                count with the expected figure and an optional denomination grid.
              </p>
            ) : null}

            {preview !== null ? (
              <p className="accounts-effect-preview">
                <span className="accounts-effect-preview__label">Effect</span>
                <span>{preview}</span>
              </p>
            ) : null}

            {type !== "count" ? (
              <div className="console-modal__actions">
                <button type="button" className="console-btn console-btn--ghost" onClick={onClose}>
                  Cancel
                </button>
                <button type="submit" className="console-btn" disabled={submitting}>
                  {submitting ? "Recording…" : "Record movement"}
                </button>
              </div>
            ) : (
              <div className="console-modal__actions">
                <button type="button" className="console-btn console-btn--ghost" onClick={onClose}>
                  Close
                </button>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bank account create / edit drawer
// ---------------------------------------------------------------------------

function BankAccountFormDrawer({
  propertyId,
  properties,
  editing,
  onClose,
  onSaved,
}: {
  readonly propertyId: string | undefined;
  readonly properties: readonly Property[];
  readonly editing: AccountsBankAccountRow | null;
  readonly onClose: () => void;
  readonly onSaved: (message: string) => void;
}): ReactElement {
  const isEdit = editing !== null;
  const [name, setName] = useState<string>(editing?.name ?? "");
  const [bankName, setBankName] = useState<string>(editing?.bankName ?? "");
  const [accountType, setAccountType] = useState<string>(editing?.accountType ?? "current");
  const [accountNumber, setAccountNumber] = useState<string>("");
  const [ifsc, setIfsc] = useState<string>(editing?.ifsc ?? "");
  const [branch, setBranch] = useState<string>(editing?.branch ?? "");
  const [prop, setProp] = useState<string>(editing?.propertyId ?? propertyId ?? "");
  const [openingBalance, setOpeningBalance] = useState<string>("");
  const [openingAsOf, setOpeningAsOf] = useState<string>("");
  const [upiId, setUpiId] = useState<string>(editing?.upiId ?? "");
  const [purpose, setPurpose] = useState<string>(editing?.purpose ?? "General");
  const [isDefault, setIsDefault] = useState<boolean>(editing?.isDefault ?? false);
  const [bankNote, setBankNote] = useState<string>(editing?.note ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [formNote, setFormNote] = useState<Note | null>(null);

  const handleSubmit = useCallback(
    async (e: FormEvent): Promise<void> => {
      e.preventDefault();
      setFormNote(null);
      if (name.trim() === "" || bankName.trim() === "") {
        setFormNote({
          severity: "error",
          model: validationMessage(
            "Name is required",
            "An account needs a name and a bank name.",
            "Fill in the account name and bank name.",
          ),
        });
        return;
      }
      if (!isEdit && accountNumber.trim() === "") {
        setFormNote({
          severity: "error",
          model: validationMessage(
            "Account number is required",
            "Only the last four digits are stored, but the number is needed to record it.",
            "Enter the account number.",
          ),
        });
        return;
      }
      const acctType = accountType as "current" | "savings" | "cash" | "other";
      setSubmitting(true);
      try {
        if (isEdit && editing !== null) {
          const result = await apiAccountsUpdateBankAccount({
            id: editing.id,
            name: name.trim(),
            bankName: bankName.trim(),
            accountType: acctType,
            ifsc: ifsc.trim() !== "" ? ifsc.trim() : null,
            branch: branch.trim() !== "" ? branch.trim() : null,
            upiId: upiId.trim() !== "" ? upiId.trim() : null,
            purpose: purpose.trim() !== "" ? purpose.trim() : null,
            isDefault,
            note: bankNote.trim() !== "" ? bankNote.trim() : null,
          });
          const err = asError(result);
          if (err !== null) { setFormNote({ severity: "error", model: toUserMessage(err) }); return; }
          onSaved("Bank account updated.");
        } else {
          const openingMinor = parseMoneyToMinor(openingBalance);
          const result = await apiAccountsCreateBankAccount({
            ...(prop !== "" ? { propertyId: prop } : {}),
            name: name.trim(),
            bankName: bankName.trim(),
            accountType: acctType,
            accountNumber: accountNumber.trim(),
            ...(ifsc.trim() !== "" ? { ifsc: ifsc.trim() } : {}),
            ...(branch.trim() !== "" ? { branch: branch.trim() } : {}),
            ...(upiId.trim() !== "" ? { upiId: upiId.trim() } : {}),
            ...(purpose.trim() !== "" ? { purpose: purpose.trim() } : {}),
            isDefault,
            ...(openingMinor !== null && openingMinor > 0 ? { openingBalanceMinor: openingMinor } : {}),
            ...(openingAsOf.trim() !== "" ? { openingAsOf } : {}),
            ...(bankNote.trim() !== "" ? { note: bankNote.trim() } : {}),
          });
          const err = asError(result);
          if (err !== null) { setFormNote({ severity: "error", model: toUserMessage(err) }); return; }
          onSaved("Bank account added.");
        }
      } catch (caught) {
        setFormNote({
          severity: "error",
          model: validationMessage(
            "Could not save the account",
            caught instanceof Error ? caught.message : "The request failed.",
            "Check your connection and try again.",
          ),
        });
      } finally {
        setSubmitting(false);
      }
    },
    [
      name, bankName, accountType, accountNumber, ifsc, branch, prop,
      openingBalance, openingAsOf, upiId, purpose, isDefault, bankNote,
      isEdit, editing, onSaved,
    ],
  );

  return (
    <div
      className="prop-drawer-scrim"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="prop-drawer" role="dialog" aria-modal="true" aria-label={isEdit ? "Edit bank account" : "Add bank account"}>
        <header className="prop-drawer__header">
          <div>
            <p className="prop-drawer__eyebrow">Bank account</p>
            <h2 className="prop-drawer__title">{isEdit ? "Edit account" : "Add bank account"}</h2>
          </div>
          <button
            type="button"
            className="console-btn console-btn--ghost prop-drawer__close"
            aria-label="Close"
            onClick={onClose}
          >
            ✕
          </button>
        </header>
        <div className="prop-drawer__body">
          {formNote !== null ? (
            <UserMessage message={formNote.model} severity={formNote.severity} />
          ) : null}
          <form className="console-form" noValidate onSubmit={handleSubmit}>
            <section className="prop-drawer__section">
              <h3 className="prop-drawer__section-title">Account</h3>
              <div className="console-form__row">
                <div className="console-field">
                  <label htmlFor="bank-name">Account Name *</label>
                  <input id="bank-name" type="text" value={name} onChange={(e) => setName(e.target.value)} />
                </div>
                <div className="console-field">
                  <label htmlFor="bank-bankname">Bank Name *</label>
                  <input id="bank-bankname" type="text" value={bankName} onChange={(e) => setBankName(e.target.value)} />
                </div>
              </div>
              <div className="console-form__row">
                <div className="console-field">
                  <label htmlFor="bank-type">Account Type *</label>
                  <select id="bank-type" value={accountType} onChange={(e) => setAccountType(e.target.value)}>
                    {BANK_ACCOUNT_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                {isEdit ? (
                  <div className="console-field">
                    <label className="console-field__label-static">Account Number</label>
                    <p className="recon-caption">{maskedAccount(editing?.last4 ?? null)}</p>
                  </div>
                ) : (
                  <div className="console-field">
                    <label htmlFor="bank-number">Account Number *</label>
                    <input id="bank-number" type="text" value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)} />
                    <p className="recon-caption">Only the last 4 digits are stored.</p>
                  </div>
                )}
              </div>
              <div className="console-form__row">
                <div className="console-field">
                  <label htmlFor="bank-ifsc">IFSC</label>
                  <input id="bank-ifsc" type="text" value={ifsc} onChange={(e) => setIfsc(e.target.value)} placeholder="Optional" />
                </div>
                <div className="console-field">
                  <label htmlFor="bank-branch">Branch</label>
                  <input id="bank-branch" type="text" value={branch} onChange={(e) => setBranch(e.target.value)} placeholder="Optional" />
                </div>
              </div>
            </section>

            <section className="prop-drawer__section">
              <h3 className="prop-drawer__section-title">Scope &amp; balance</h3>
              <div className="console-form__row">
                <div className="console-field">
                  <label htmlFor="bank-prop">Property</label>
                  <select id="bank-prop" value={prop} onChange={(e) => setProp(e.target.value)} disabled={isEdit}>
                    <option value="">Whole portfolio</option>
                    {properties.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
                <div className="console-field">
                  <label htmlFor="bank-purpose">Purpose</label>
                  <select id="bank-purpose" value={purpose} onChange={(e) => setPurpose(e.target.value)}>
                    {BANK_PURPOSES.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>
              </div>
              {isEdit ? null : (
                <div className="console-form__row">
                  <div className="console-field">
                    <label htmlFor="bank-opening">Opening Book Balance</label>
                    <input id="bank-opening" type="text" inputMode="decimal" value={openingBalance} onChange={(e) => setOpeningBalance(e.target.value)} placeholder="0.00" />
                  </div>
                  <div className="console-field">
                    <label htmlFor="bank-openingdate">Balance As-On</label>
                    <input id="bank-openingdate" type="date" value={openingAsOf} onChange={(e) => setOpeningAsOf(e.target.value)} />
                  </div>
                </div>
              )}
              <div className="console-form__row">
                <div className="console-field">
                  <label htmlFor="bank-upi">UPI ID</label>
                  <input id="bank-upi" type="text" value={upiId} onChange={(e) => setUpiId(e.target.value)} placeholder="Optional" />
                </div>
              </div>
              <label className="accounts-cash-denomtoggle">
                <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />
                <span>Mark as default collection account</span>
              </label>
              <div className="console-form__row">
                <div className="console-field" style={{ flex: "1 1 100%" }}>
                  <label htmlFor="bank-note">Notes</label>
                  <input id="bank-note" type="text" value={bankNote} onChange={(e) => setBankNote(e.target.value)} placeholder="Optional" />
                </div>
              </div>
            </section>

            <div className="prop-drawer__footer">
              <button type="button" className="console-btn console-btn--ghost" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="console-btn" disabled={submitting}>
                {submitting ? "Saving…" : isEdit ? "Save changes" : "Add account"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bank account detail drawer
// ---------------------------------------------------------------------------

function BankAccountDetailDrawer({
  bankAccountId,
  currency,
  onClose,
  onRecordMovement,
  onEdit,
  onReversed,
}: {
  readonly bankAccountId: string;
  readonly currency: string;
  readonly onClose: () => void;
  readonly onRecordMovement: (type: MovementType, bankAccountId: string) => void;
  readonly onEdit: (account: AccountsBankAccountRow) => void;
  readonly onReversed: (message: string) => void;
}): ReactElement {
  const [detail, setDetail] = useState<AccountsBankAccountDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState<Note | null>(null);
  const [confirmTxn, setConfirmTxn] = useState<AccountsBankTxnRow | null>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmError, setConfirmError] = useState<UserMessageModel | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async (): Promise<void> => {
      try {
        const result = await apiAccountsBankAccountDetail(bankAccountId);
        const err = asError(result);
        if (!cancelled) {
          if (err !== null) {
            setNote({ severity: "error", model: toUserMessage(err) });
            setDetail(null);
          } else {
            setDetail(result as AccountsBankAccountDetail);
          }
        }
      } catch (caught) {
        if (!cancelled) {
          setNote({
            severity: "error",
            model: validationMessage(
              "Could not load the account",
              caught instanceof Error ? caught.message : "The request failed.",
              "Check your connection and try again.",
            ),
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bankAccountId]);

  const handleReverse = useCallback(async (): Promise<void> => {
    if (confirmTxn === null) return;
    setConfirmBusy(true);
    setConfirmError(null);
    try {
      const result = await apiAccountsReverseMovement({ source: "bank", movementId: confirmTxn.id });
      const err = asError(result);
      if (err !== null) { setConfirmError(toUserMessage(err)); return; }
      setConfirmTxn(null);
      onReversed("Transaction reversed.");
    } catch (caught) {
      setConfirmError(
        validationMessage(
          "Could not reverse the transaction",
          caught instanceof Error ? caught.message : "The request failed.",
          "Check your connection and try again.",
        ),
      );
    } finally {
      setConfirmBusy(false);
    }
  }, [confirmTxn, onReversed]);

  const account = detail?.account ?? null;
  const cur = account?.currency || currency || FALLBACK_CURRENCY;

  return (
    <div
      className="prop-drawer-scrim"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="prop-drawer" role="dialog" aria-modal="true" aria-label="Bank account detail">
        <header className="prop-drawer__header">
          <div>
            <p className="prop-drawer__eyebrow">Bank account</p>
            <h2 className="prop-drawer__title">{account?.name ?? "Account"}</h2>
          </div>
          <button
            type="button"
            className="console-btn console-btn--ghost prop-drawer__close"
            aria-label="Close"
            onClick={onClose}
          >
            ✕
          </button>
        </header>
        <div className="prop-drawer__body">
          {note !== null ? (
            <UserMessage message={note.model} severity={note.severity} />
          ) : null}
          {loading ? (
            <p className="console-loading" role="status" aria-live="polite">Loading account…</p>
          ) : account === null ? (
            <p className="console-empty">Account not found.</p>
          ) : (
            <>
              <section className="prop-drawer__section">
                <h3 className="prop-drawer__section-title">Account</h3>
                <dl className="accounts-invdetail__rows">
                  <div className="accounts-invdetail__row"><dt>Bank</dt><dd>{account.bankName}</dd></div>
                  <div className="accounts-invdetail__row"><dt>Type</dt><dd>{bankAccountTypeLabel(account.accountType)}</dd></div>
                  <div className="accounts-invdetail__row"><dt>Number</dt><dd>{maskedAccount(account.last4)}</dd></div>
                  <div className="accounts-invdetail__row"><dt>IFSC</dt><dd>{account.ifsc ?? "—"}</dd></div>
                  <div className="accounts-invdetail__row"><dt>Branch</dt><dd>{account.branch ?? "—"}</dd></div>
                  <div className="accounts-invdetail__row"><dt>Purpose</dt><dd>{account.purpose ?? "—"}</dd></div>
                  <div className="accounts-invdetail__row"><dt>Property</dt><dd>{account.propertyName ?? "Whole portfolio"}</dd></div>
                </dl>
              </section>

              <div className="console-card-grid accounts-bank-detailkpis">
                <div className="console-card accounts-kpi">
                  <p className="console-card__label">Book Balance</p>
                  <MoneyValue minor={Number(account.bookBalanceMinor)} currency={cur} />
                </div>
                <div className="console-card accounts-kpi">
                  <p className="console-card__label">Money In</p>
                  <MoneyValue minor={Number(account.moneyInMinor)} currency={cur} />
                </div>
                <div className="console-card accounts-kpi">
                  <p className="console-card__label">Money Out</p>
                  <MoneyValue minor={Number(account.moneyOutMinor)} currency={cur} />
                </div>
              </div>

              <section className="prop-drawer__section">
                <h3 className="prop-drawer__section-title">Transactions</h3>
                {detail !== null && detail.txns.length === 0 ? (
                  <p className="console-empty">No transactions yet.</p>
                ) : (
                  <table className="console-table">
                    <caption className="console-sr-only">Bank account transactions</caption>
                    <thead>
                      <tr>
                        <th scope="col">Date</th>
                        <th scope="col">Description</th>
                        <th scope="col">Money In</th>
                        <th scope="col">Money Out</th>
                        <th scope="col">Balance</th>
                        <th scope="col">Reference</th>
                        <th scope="col" aria-label="Actions" />
                      </tr>
                    </thead>
                    <tbody>
                      {(detail?.txns ?? []).map((t) => {
                        const amt = Number(t.amountMinor);
                        return (
                          <tr key={t.id}>
                            <td>{formatDate(t.date)}</td>
                            <td>{CASH_KIND_LABEL[t.kind] ?? t.kind}</td>
                            <td className="recon-num">{amt > 0 ? formatMoney(amt, cur) : "—"}</td>
                            <td className={amt < 0 ? "recon-num accounts-ledger-neg" : "recon-num"}>{amt < 0 ? formatMoney(amt, cur) : "—"}</td>
                            <td className="recon-num">{formatMoney(Number(t.runningMinor), cur)}</td>
                            <td>{t.reference ?? "—"}</td>
                            <td>
                              <RowMenu
                                actions={[
                                  {
                                    key: "reverse",
                                    label: "Reverse transaction",
                                    danger: true,
                                    onClick: () => setConfirmTxn(t),
                                  },
                                ]}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </section>
            </>
          )}
        </div>
        {account !== null ? (
          <div className="prop-drawer__footer">
            <button type="button" className="console-btn console-btn--ghost" onClick={() => onRecordMovement("deposit", account.id)}>
              Record Deposit
            </button>
            <button type="button" className="console-btn console-btn--ghost" onClick={() => onRecordMovement("withdrawal", account.id)}>
              Record Withdrawal
            </button>
            <button type="button" className="console-btn console-btn--ghost" onClick={() => onRecordMovement("transfer", account.id)}>
              Transfer Money
            </button>
            <button type="button" className="console-btn console-btn--ghost" onClick={() => onEdit(account)}>
              Edit Account
            </button>
          </div>
        ) : null}
      </div>

      {confirmTxn !== null ? (
        <div
          className="console-modal-scrim"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget) setConfirmTxn(null);
          }}
        >
          <div className="console-modal console-modal--confirm" role="dialog" aria-modal="true" aria-label="Reverse transaction">
            <header className="console-modal__header">
              <h2 className="console-modal__title">Reverse transaction</h2>
              <button type="button" className="console-btn console-btn--ghost console-modal__close" aria-label="Close" onClick={() => setConfirmTxn(null)}>✕</button>
            </header>
            <div className="console-modal__body">
              {confirmError !== null ? <UserMessage message={confirmError} severity="error" /> : null}
              <p className="recon-caption">
                This posts a reversing entry (the original is retained for audit).
                It cannot be undone.
              </p>
              <div className="console-modal__actions">
                <button type="button" className="console-btn console-btn--ghost" onClick={() => setConfirmTxn(null)}>Cancel</button>
                <button type="button" className="console-btn console-btn--danger" disabled={confirmBusy} onClick={() => void handleReverse()}>
                  {confirmBusy ? "Working…" : "Reverse"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Movement detail drawer
// ---------------------------------------------------------------------------

function MovementDetailDrawer({
  row,
  currency,
  onClose,
}: {
  readonly row: AccountsMovementRow;
  readonly currency: string;
  readonly onClose: () => void;
}): ReactElement {
  const cur = currency || FALLBACK_CURRENCY;
  const amt = Number(row.amountMinor);
  return (
    <div
      className="prop-drawer-scrim"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="prop-drawer" role="dialog" aria-modal="true" aria-label="Movement detail">
        <header className="prop-drawer__header">
          <div>
            <p className="prop-drawer__eyebrow">Movement</p>
            <h2 className="prop-drawer__title">{row.movement}</h2>
          </div>
          <button type="button" className="console-btn console-btn--ghost prop-drawer__close" aria-label="Close" onClick={onClose}>✕</button>
        </header>
        <div className="prop-drawer__body">
          <section className="prop-drawer__section">
            <dl className="accounts-invdetail__rows">
              <div className="accounts-invdetail__row"><dt>Reference</dt><dd>{row.reference ?? "—"}</dd></div>
              <div className="accounts-invdetail__row"><dt>Date</dt><dd>{formatDate(row.date)}</dd></div>
              <div className="accounts-invdetail__row"><dt>Property</dt><dd>{row.propertyName ?? "Whole portfolio"}</dd></div>
              <div className="accounts-invdetail__row"><dt>Movement type</dt><dd>{row.movement}</dd></div>
              <div className="accounts-invdetail__row"><dt>Kind</dt><dd>{CASH_KIND_LABEL[row.kind] ?? row.kind}</dd></div>
              <div className="accounts-invdetail__row">
                <dt>Amount</dt>
                <dd className={amt < 0 ? "accounts-ledger-neg" : ""}>{formatMoney(amt, cur)}</dd>
              </div>
              <div className="accounts-invdetail__row"><dt>Note</dt><dd>{row.note ?? "—"}</dd></div>
              <div className="accounts-invdetail__row"><dt>Created by</dt><dd>{row.createdBy ?? "—"}</dd></div>
              <div className="accounts-invdetail__row"><dt>Bank account</dt><dd>{row.bankAccountName ?? "—"}</dd></div>
              <div className="accounts-invdetail__row"><dt>Transfer group</dt><dd>{row.transferGroupId ?? "—"}</dd></div>
            </dl>
            {row.transferGroupId !== null ? (
              <p className="recon-caption">This is one leg of a transfer.</p>
            ) : null}
          </section>
        </div>
        <div className="prop-drawer__footer">
          <button type="button" className="console-btn console-btn--ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CashTab — owns the four sub-tabs, its own data loading and all overlays
// ---------------------------------------------------------------------------

function CashTab({
  propertyId,
  currency,
  onRefresh,
}: {
  readonly propertyId: string | undefined;
  readonly currency: string;
  readonly onRefresh: (message: string) => void;
}): ReactElement {
  const [activeSub, setActiveSub] = useState<CashSubKey>("overview");
  const [subToken, setSubToken] = useState<number>(0);

  // Data per sub-view (each loaded in the effect below).
  const [overview, setOverview] = useState<AccountsCashBanksOverview | null>(null);
  const [cashInHand, setCashInHand] = useState<readonly AccountsCashInHandRow[]>([]);
  const [cashbook, setCashbook] = useState<readonly AccountsCashbookRow[]>([]);
  const [banks, setBanks] = useState<readonly AccountsBankAccountRow[]>([]);
  const [movements, setMovements] = useState<readonly AccountsMovementRow[]>([]);
  // Shared reference data (properties + bank accounts) for modals.
  const [properties, setProperties] = useState<readonly Property[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [note, setNote] = useState<Note | null>(null);

  // Overlays.
  const [recordPreset, setRecordPreset] = useState<MovementPreset | null>(null);
  const [recordOpen, setRecordOpen] = useState(false);
  const [countOpen, setCountOpen] = useState(false);
  // When set, the Count Cash modal opens pre-scoped to this property.
  const [countScope, setCountScope] = useState<string | undefined>(undefined);
  const [bankFormEditing, setBankFormEditing] = useState<AccountsBankAccountRow | null>(null);
  const [bankFormOpen, setBankFormOpen] = useState(false);
  const [detailBankId, setDetailBankId] = useState<string | null>(null);
  const [movementDetail, setMovementDetail] = useState<AccountsMovementRow | null>(null);
  const [reverseRow, setReverseRow] = useState<AccountsMovementRow | null>(null);
  const [reverseBusy, setReverseBusy] = useState(false);
  const [reverseError, setReverseError] = useState<UserMessageModel | null>(null);

  const cur = overview?.currency || currency || FALLBACK_CURRENCY;

  // Bump the local refresh token AND notify the parent so global state stays
  // fresh after any mutation.
  const refresh = useCallback(
    (message: string): void => {
      setSubToken((t) => t + 1);
      onRefresh(message);
    },
    [onRefresh],
  );

  // Load the property list once (SSR-safe: inside an effect).
  useEffect(() => {
    let cancelled = false;
    void (async (): Promise<void> => {
      try {
        const list = await apiAccountsProperties();
        if (!cancelled) setProperties(list);
      } catch {
        if (!cancelled) setProperties([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Bank accounts are shared by several sub-views + modals; keep them fresh.
  useEffect(() => {
    let cancelled = false;
    void (async (): Promise<void> => {
      try {
        const list = await apiAccountsBankAccounts(propertyId);
        if (!cancelled) setBanks(list);
      } catch {
        if (!cancelled) setBanks([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [propertyId, subToken]);

  // Load the active sub-view's data.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNote(null);
    void (async (): Promise<void> => {
      try {
        if (activeSub === "overview") {
          const [ov, cih] = await Promise.all([
            apiAccountsCashBanksOverview(propertyId),
            apiAccountsCashInHand(propertyId),
          ]);
          const err = asError(ov);
          if (!cancelled) {
            if (err !== null) {
              setNote({ severity: "error", model: toUserMessage(err) });
              setOverview(null);
            } else {
              setOverview(ov as AccountsCashBanksOverview);
            }
            setCashInHand(cih);
          }
        } else if (activeSub === "cashbook") {
          const [rows, cih] = await Promise.all([
            apiAccountsCashbook(propertyId),
            apiAccountsCashInHand(propertyId),
          ]);
          if (!cancelled) {
            setCashbook(rows);
            setCashInHand(cih);
          }
        } else if (activeSub === "banks") {
          const list = await apiAccountsBankAccounts(propertyId);
          if (!cancelled) setBanks(list);
        } else if (activeSub === "movements") {
          const rows = await apiAccountsMovements(propertyId);
          if (!cancelled) setMovements(rows);
        }
      } catch (caught) {
        if (!cancelled) {
          setNote({
            severity: "error",
            model: validationMessage(
              "Could not load this view",
              caught instanceof Error ? caught.message : "The request failed.",
              "Check your connection and try again.",
            ),
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeSub, propertyId, subToken]);

  const openRecord = useCallback((preset: MovementPreset | null): void => {
    setRecordPreset(preset);
    setRecordOpen(true);
  }, []);

  const openRecordFromBank = useCallback((type: MovementType, bankAccountId: string): void => {
    setDetailBankId(null);
    setRecordPreset({ type, bankAccountId });
    setRecordOpen(true);
  }, []);

  const handleReverse = useCallback(async (): Promise<void> => {
    if (reverseRow === null) return;
    setReverseBusy(true);
    setReverseError(null);
    try {
      const result = await apiAccountsReverseMovement({ source: reverseRow.source, movementId: reverseRow.id });
      const err = asError(result);
      if (err !== null) { setReverseError(toUserMessage(err)); return; }
      setReverseRow(null);
      refresh("Transaction reversed.");
    } catch (caught) {
      setReverseError(
        validationMessage(
          "Could not reverse the transaction",
          caught instanceof Error ? caught.message : "The request failed.",
          "Check your connection and try again.",
        ),
      );
    } finally {
      setReverseBusy(false);
    }
  }, [reverseRow, refresh]);

  return (
    <>
      {/* Internal sub-tab strip (reuses the shared tab-strip markup). */}
      <div className="channel-detail__tabs accounts-subtabs" role="tablist" aria-label="Cash & Banks sections">
        {CASH_SUBS.map((s) => (
          <button
            key={s.key}
            type="button"
            role="tab"
            aria-selected={activeSub === s.key}
            className={
              activeSub === s.key
                ? "channel-detail__tab channel-detail__tab--active"
                : "channel-detail__tab"
            }
            onClick={() => setActiveSub(s.key)}
          >
            {s.label}
          </button>
        ))}
      </div>

      {note !== null ? <UserMessage message={note.model} severity={note.severity} /> : null}

      {activeSub === "overview" ? (
        <CashOverviewSub
          overview={overview}
          cashInHand={cashInHand}
          currency={cur}
          loading={loading}
          onCountCash={(pid) => { setCountScope(pid); setCountOpen(true); }}
        />
      ) : null}

      {activeSub === "cashbook" ? (
        <CashbookSub
          rows={cashbook}
          currency={cur}
          loading={loading}
          onCountCash={() => { setCountScope(propertyId); setCountOpen(true); }}
          onRecordMovement={() => openRecord(null)}
        />
      ) : null}

      {activeSub === "banks" ? (
        <BankAccountsSub
          rows={banks}
          currency={cur}
          loading={loading}
          onAdd={() => { setBankFormEditing(null); setBankFormOpen(true); }}
          onView={(id) => setDetailBankId(id)}
          onEdit={(a) => { setBankFormEditing(a); setBankFormOpen(true); }}
          onArchiveToggle={async (a) => {
            const next = a.status === "active" ? "archived" : "active";
            const result = await apiAccountsUpdateBankAccount({ id: a.id, status: next });
            const err = asError(result);
            if (err !== null) { setNote({ severity: "error", model: toUserMessage(err) }); return; }
            refresh(next === "archived" ? "Account archived." : "Account activated.");
          }}
        />
      ) : null}

      {activeSub === "movements" ? (
        <MovementsSub
          rows={movements}
          currency={cur}
          loading={loading}
          onView={(r) => setMovementDetail(r)}
          onReverse={(r) => setReverseRow(r)}
        />
      ) : null}

      {recordOpen ? (
        <RecordMovementModal
          propertyId={propertyId}
          properties={properties}
          bankAccounts={banks}
          currency={cur}
          preset={recordPreset}
          onClose={() => setRecordOpen(false)}
          onCreated={(m) => { setRecordOpen(false); refresh(m); }}
        />
      ) : null}

      {countOpen ? (
        <CountCashModal
          propertyId={countScope}
          properties={properties}
          cashInHand={cashInHand}
          currency={cur}
          onClose={() => setCountOpen(false)}
          onSaved={(m) => refresh(m)}
          onRecordAdjustment={(pid) => {
            setCountOpen(false);
            setRecordPreset({ type: "adjustment" });
            setDetailBankId(null);
            setRecordOpen(true);
            void pid;
          }}
        />
      ) : null}

      {bankFormOpen ? (
        <BankAccountFormDrawer
          propertyId={propertyId}
          properties={properties}
          editing={bankFormEditing}
          onClose={() => setBankFormOpen(false)}
          onSaved={(m) => { setBankFormOpen(false); refresh(m); }}
        />
      ) : null}

      {detailBankId !== null ? (
        <BankAccountDetailDrawer
          bankAccountId={detailBankId}
          currency={cur}
          onClose={() => setDetailBankId(null)}
          onRecordMovement={openRecordFromBank}
          onEdit={(a) => { setDetailBankId(null); setBankFormEditing(a); setBankFormOpen(true); }}
          onReversed={(m) => refresh(m)}
        />
      ) : null}

      {movementDetail !== null ? (
        <MovementDetailDrawer
          row={movementDetail}
          currency={cur}
          onClose={() => setMovementDetail(null)}
        />
      ) : null}

      {reverseRow !== null ? (
        <div
          className="console-modal-scrim"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget) setReverseRow(null);
          }}
        >
          <div className="console-modal console-modal--confirm" role="dialog" aria-modal="true" aria-label="Reverse transaction">
            <header className="console-modal__header">
              <h2 className="console-modal__title">Reverse transaction</h2>
              <button type="button" className="console-btn console-btn--ghost console-modal__close" aria-label="Close" onClick={() => setReverseRow(null)}>✕</button>
            </header>
            <div className="console-modal__body">
              {reverseError !== null ? <UserMessage message={reverseError} severity="error" /> : null}
              <p className="recon-caption">
                This posts a reversing entry (the original is retained for audit).
                It cannot be undone.
              </p>
              <div className="console-modal__actions">
                <button type="button" className="console-btn console-btn--ghost" onClick={() => setReverseRow(null)}>Cancel</button>
                <button type="button" className="console-btn console-btn--danger" disabled={reverseBusy} onClick={() => void handleReverse()}>
                  {reverseBusy ? "Working…" : "Reverse"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// Cash & Banks — Overview sub-tab
// ---------------------------------------------------------------------------

function CashOverviewSub({
  overview,
  cashInHand,
  currency,
  loading,
  onCountCash,
}: {
  readonly overview: AccountsCashBanksOverview | null;
  readonly cashInHand: readonly AccountsCashInHandRow[];
  readonly currency: string;
  readonly loading: boolean;
  readonly onCountCash: (propertyId: string) => void;
}): ReactElement {
  if (loading) {
    return <p className="console-loading" role="status" aria-live="polite">Loading cash position…</p>;
  }
  const cur = overview?.currency || currency || FALLBACK_CURRENCY;
  return (
    <>
      <div className="console-card-grid accounts-cash-cards">
        <div className="console-card console-card--gold accounts-kpi">
          <p className="console-card__label">Cash in Hand</p>
          <MoneyValue minor={Number(overview?.cashInHandMinor ?? "0")} currency={cur} />
          <p className="console-card__hint">Physical cash across properties</p>
        </div>
        <div className="console-card accounts-kpi">
          <p className="console-card__label">Bank Book Balance</p>
          <MoneyValue minor={Number(overview?.bankBalanceMinor ?? "0")} currency={cur} />
          <p className="console-card__hint">Balance across bank accounts</p>
        </div>
        <div className="console-card accounts-kpi">
          <p className="console-card__label">Pending Cash</p>
          <MoneyValue minor={Number(overview?.pendingCashMinor ?? "0")} currency={cur} />
          <p className="console-card__hint">Collected but not yet deposited</p>
        </div>
        <div className="console-card accounts-kpi">
          <p className="console-card__label">Cash Variance</p>
          <MoneyValue minor={Number(overview?.varianceMinor ?? "0")} currency={cur} />
          <p className="console-card__hint">Counted cash vs the books</p>
        </div>
      </div>

      <section className="console-panel" aria-label="Property cash position">
        <h2 className="console-panel__title">Property cash position</h2>
        {cashInHand.length === 0 ? (
          <p className="console-empty">No cash-in-hand figures yet.</p>
        ) : (
          <table className="console-table">
            <caption className="console-sr-only">Property cash position</caption>
            <thead>
              <tr>
                <th scope="col">Property</th>
                <th scope="col">Cash received (stays)</th>
                <th scope="col">Cash spent (petty)</th>
                <th scope="col">Bank movements</th>
                <th scope="col">Expected cash</th>
                <th scope="col">Counted</th>
                <th scope="col">Variance</th>
                <th scope="col" aria-label="Action" />
              </tr>
            </thead>
            <tbody>
              {cashInHand.map((r) => {
                const rc = r.currency || cur || FALLBACK_CURRENCY;
                const bank = Number(r.bankMovementsMinor);
                const petty = Number(r.pettyCashSpendMinor);
                const variance = r.varianceMinor !== null ? Number(r.varianceMinor) : null;
                return (
                  <tr key={r.propertyId}>
                    <td>{r.propertyName ?? "—"}</td>
                    <td className="recon-num">{formatMoney(Number(r.cashFromStaysMinor), rc)}</td>
                    <td className={petty < 0 ? "recon-num accounts-ledger-neg" : "recon-num"}>{formatMoney(petty, rc)}</td>
                    <td className={bank < 0 ? "recon-num accounts-ledger-neg" : "recon-num"}>{formatMoney(bank, rc)}</td>
                    <td className="recon-num">{formatMoney(Number(r.expectedMinor), rc)}</td>
                    <td className="recon-num">{r.countedMinor !== null ? formatMoney(Number(r.countedMinor), rc) : "—"}</td>
                    <td className={variance !== null && variance < 0 ? "recon-num accounts-ledger-neg" : "recon-num"}>{variance !== null ? formatMoney(variance, rc) : "—"}</td>
                    <td>
                      <button type="button" className="console-btn console-btn--ghost" onClick={() => onCountCash(r.propertyId)}>
                        Count cash
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}

// ---------------------------------------------------------------------------
// Cash & Banks — Cashbook sub-tab
// ---------------------------------------------------------------------------

function CashbookSub({
  rows,
  currency,
  loading,
  onCountCash,
  onRecordMovement,
}: {
  readonly rows: readonly AccountsCashbookRow[];
  readonly currency: string;
  readonly loading: boolean;
  readonly onCountCash: () => void;
  readonly onRecordMovement: () => void;
}): ReactElement {
  const [search, setSearch] = useState<string>("");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [period, setPeriod] = useState<PeriodState>({
    preset: "this-month",
    from: undefined,
    to: undefined,
    label: "This month",
  });

  useEffect(() => {
    setPeriod(resolvePreset("this-month"));
  }, []);
  const handlePresetChange = useCallback((preset: PresetKey): void => {
    setPeriod(resolvePreset(preset));
  }, []);
  const handleCustomApply = useCallback((from: string, to: string): void => {
    setPeriod(resolvePreset("custom", from, to));
  }, []);

  // Distinct movement types present in the data (for the type select).
  const typeOptions = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows) set.add(r.type);
    return [...set].sort();
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (q !== "") {
        const hay = [r.reference, r.description, r.propertyName ?? "", r.type].join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (typeFilter !== "all" && r.type !== typeFilter) return false;
      if (period.from !== undefined || period.to !== undefined) {
        const day = r.date.slice(0, 10);
        if (period.from !== undefined && day < period.from) return false;
        if (period.to !== undefined && day > period.to) return false;
      }
      return true;
    });
  }, [rows, search, typeFilter, period.from, period.to]);

  return (
    <section className="console-panel" aria-label="Cashbook">
      <div className="accounts-panel-head">
        <div>
          <h2 className="console-panel__title">Cashbook</h2>
          <p className="recon-caption">Track physical cash received, spent and deposited for each property.</p>
        </div>
        <div className="accounts-panel-head__actions">
          <button type="button" className="console-btn console-btn--ghost" onClick={onCountCash}>Count cash</button>
          <button type="button" className="console-btn" onClick={onRecordMovement}>+ Record cash movement</button>
        </div>
      </div>

      <div className="accounts-invcontrols">
        <div className="console-field accounts-invcontrols__search">
          <label htmlFor="cb-search">Search</label>
          <input id="cb-search" type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Reference, description, property or type" />
        </div>
        <div className="console-field accounts-invcontrols__status">
          <label htmlFor="cb-type">Type</label>
          <select id="cb-type" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="all">All</option>
            {typeOptions.map((t) => (
              <option key={t} value={t}>{CASH_KIND_LABEL[t] ?? t}</option>
            ))}
          </select>
        </div>
        <div className="accounts-invcontrols__period">
          <PeriodFilter period={period} onPresetChange={handlePresetChange} onCustomApply={handleCustomApply} />
        </div>
      </div>

      {loading ? (
        <p className="console-loading" role="status" aria-live="polite">Loading cashbook…</p>
      ) : rows.length === 0 ? (
        <p className="console-empty">No cash movements recorded yet.</p>
      ) : filtered.length === 0 ? (
        <p className="console-empty">No cashbook lines match your search or filters.</p>
      ) : (
        <table className="console-table">
          <caption className="console-sr-only">Cashbook</caption>
          <thead>
            <tr>
              <th scope="col">Date</th>
              <th scope="col">Reference</th>
              <th scope="col">Property</th>
              <th scope="col">Description</th>
              <th scope="col">Type</th>
              <th scope="col">Cash In</th>
              <th scope="col">Cash Out</th>
              <th scope="col">Running Balance</th>
              <th scope="col">Source</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r, i) => {
              const rc = r.currency || currency || FALLBACK_CURRENCY;
              const cashIn = Number(r.cashInMinor);
              const cashOut = Number(r.cashOutMinor);
              return (
                <tr key={`${r.reference}-${i}`}>
                  <td>{formatDate(r.date)}</td>
                  <td>{r.reference}</td>
                  <td>{r.propertyName ?? "—"}</td>
                  <td>{r.description}</td>
                  <td>{CASH_KIND_LABEL[r.type] ?? r.type}</td>
                  <td className="recon-num">{cashIn > 0 ? formatMoney(cashIn, rc) : "—"}</td>
                  <td className={cashOut > 0 ? "recon-num accounts-ledger-neg" : "recon-num"}>{cashOut > 0 ? formatMoney(-cashOut, rc) : "—"}</td>
                  <td className="recon-num">{formatMoney(Number(r.runningMinor), rc)}</td>
                  <td>{r.source}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Cash & Banks — Bank Accounts sub-tab
// ---------------------------------------------------------------------------

function BankAccountsSub({
  rows,
  currency,
  loading,
  onAdd,
  onView,
  onEdit,
  onArchiveToggle,
}: {
  readonly rows: readonly AccountsBankAccountRow[];
  readonly currency: string;
  readonly loading: boolean;
  readonly onAdd: () => void;
  readonly onView: (id: string) => void;
  readonly onEdit: (account: AccountsBankAccountRow) => void;
  readonly onArchiveToggle: (account: AccountsBankAccountRow) => void;
}): ReactElement {
  return (
    <section className="console-panel" aria-label="Bank accounts">
      <div className="accounts-panel-head">
        <h2 className="console-panel__title">Bank accounts</h2>
        <button type="button" className="console-btn" onClick={onAdd}>+ Add Bank Account</button>
      </div>

      {loading ? (
        <p className="console-loading" role="status" aria-live="polite">Loading bank accounts…</p>
      ) : rows.length === 0 ? (
        <p className="console-empty">No bank accounts yet. Add one to start tracking bank balances.</p>
      ) : (
        <div className="accounts-bankgrid">
          {rows.map((a) => {
            const cur = a.currency || currency || FALLBACK_CURRENCY;
            const statusPill =
              a.status === "active"
                ? "avos-status avos-status--ok"
                : "avos-status avos-status--pending";
            return (
              <div className="console-card accounts-bankcard" key={a.id}>
                <div className="accounts-bankcard__head">
                  <div>
                    <p className="accounts-bankcard__name">{a.name}</p>
                    <p className="recon-caption">{a.bankName} · {bankAccountTypeLabel(a.accountType)}</p>
                  </div>
                  <RowMenu
                    actions={[
                      { key: "edit", label: "Edit account", onClick: () => onEdit(a) },
                      {
                        key: "archive",
                        label: a.status === "active" ? "Archive" : "Activate",
                        onClick: () => onArchiveToggle(a),
                      },
                    ]}
                  />
                </div>
                <div className="accounts-bankcard__badges">
                  <span className={statusPill}>{a.status === "active" ? "Active" : "Archived"}</span>
                  {a.isDefault ? <span className="avos-status avos-status--attention">Default collection</span> : null}
                </div>
                <p className="accounts-bankcard__num">{maskedAccount(a.last4)}</p>
                <p className="recon-caption">{a.propertyName ?? "Whole portfolio"}</p>
                <div className="accounts-bankcard__balance">
                  <p className="console-card__label">Book Balance</p>
                  <MoneyValue minor={Number(a.bookBalanceMinor)} currency={cur} />
                </div>
                <p className="recon-caption">Last movement {formatDate(a.lastMovementOn)}</p>
                <div className="accounts-bankcard__actions">
                  <button type="button" className="console-btn console-btn--ghost" onClick={() => onView(a.id)}>
                    View transactions
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Cash & Banks — Movements sub-tab
// ---------------------------------------------------------------------------

function MovementsSub({
  rows,
  currency,
  loading,
  onView,
  onReverse,
}: {
  readonly rows: readonly AccountsMovementRow[];
  readonly currency: string;
  readonly loading: boolean;
  readonly onView: (row: AccountsMovementRow) => void;
  readonly onReverse: (row: AccountsMovementRow) => void;
}): ReactElement {
  const [search, setSearch] = useState<string>("");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [period, setPeriod] = useState<PeriodState>({
    preset: "this-month",
    from: undefined,
    to: undefined,
    label: "This month",
  });

  useEffect(() => {
    setPeriod(resolvePreset("this-month"));
  }, []);
  const handlePresetChange = useCallback((preset: PresetKey): void => {
    setPeriod(resolvePreset(preset));
  }, []);
  const handleCustomApply = useCallback((from: string, to: string): void => {
    setPeriod(resolvePreset("custom", from, to));
  }, []);

  const typeOptions = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows) set.add(r.movement);
    return [...set].sort();
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (q !== "") {
        const hay = [r.movement, r.reference ?? "", r.propertyName ?? "", r.createdBy ?? "", r.note ?? ""].join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (typeFilter !== "all" && r.movement !== typeFilter) return false;
      if (period.from !== undefined || period.to !== undefined) {
        const day = r.date.slice(0, 10);
        if (period.from !== undefined && day < period.from) return false;
        if (period.to !== undefined && day > period.to) return false;
      }
      return true;
    });
  }, [rows, search, typeFilter, period.from, period.to]);

  return (
    <section className="console-panel" aria-label="Movements">
      <h2 className="console-panel__title">Movements</h2>
      <p className="recon-caption">A consolidated history of internal cash and bank movements.</p>

      <div className="accounts-invcontrols">
        <div className="console-field accounts-invcontrols__search">
          <label htmlFor="mv-search">Search</label>
          <input id="mv-search" type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Movement, reference, property or person" />
        </div>
        <div className="console-field accounts-invcontrols__status">
          <label htmlFor="mv-type">Type</label>
          <select id="mv-type" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="all">All</option>
            {typeOptions.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <div className="accounts-invcontrols__period">
          <PeriodFilter period={period} onPresetChange={handlePresetChange} onCustomApply={handleCustomApply} />
        </div>
      </div>

      {loading ? (
        <p className="console-loading" role="status" aria-live="polite">Loading movements…</p>
      ) : rows.length === 0 ? (
        <p className="console-empty">No movements recorded yet.</p>
      ) : filtered.length === 0 ? (
        <p className="console-empty">No movements match your search or filters.</p>
      ) : (
        <table className="console-table">
          <caption className="console-sr-only">Movements</caption>
          <thead>
            <tr>
              <th scope="col">Date</th>
              <th scope="col">Property</th>
              <th scope="col">Movement</th>
              <th scope="col">Amount</th>
              <th scope="col">Reference</th>
              <th scope="col">Created by</th>
              <th scope="col">Status</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => {
              const rc = currency || FALLBACK_CURRENCY;
              const amt = Number(r.amountMinor);
              return (
                <tr
                  key={r.id}
                  className="accounts-clickrow"
                  onClick={() => onView(r)}
                >
                  <td>{formatDate(r.date)}</td>
                  <td>{r.propertyName ?? "—"}</td>
                  <td>{r.movement}</td>
                  <td className={amt < 0 ? "recon-num accounts-ledger-neg" : "recon-num"}>{formatMoney(amt, rc)}</td>
                  <td>{r.reference ?? "—"}</td>
                  <td>{r.createdBy ?? "—"}</td>
                  <td>
                    {r.isReversal ? (
                      <span className="avos-status avos-status--attention">Reversal</span>
                    ) : (
                      <span className="avos-status avos-status--ok">Posted</span>
                    )}
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="accounts-rowactions">
                      <button type="button" className="console-btn console-btn--ghost" onClick={() => onView(r)}>
                        View
                      </button>
                      <RowMenu
                        actions={[
                          {
                            key: "reverse",
                            label: "Reverse transaction",
                            danger: true,
                            disabled: r.isReversal,
                            onClick: () => onReverse(r),
                          },
                        ]}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}

// ===========================================================================
// Offers & Vouchers tab
// ===========================================================================

/** The display name of an offer/voucher — its name if set, else its code. */
function offerName(v: AccountsVoucherRow): string {
  return v.name !== null && v.name.trim() !== "" ? v.name : v.code;
}

/**
 * Build a short human summary of an offer/voucher's terms. Promo codes show
 * their discount, cap, min-spend and channel scope; gift vouchers show their
 * initial value and remaining balance.
 */
function voucherTerms(v: AccountsVoucherRow, currency: string): string {
  const cur = currency || FALLBACK_CURRENCY;
  if (v.kind === "gift") {
    const initial = v.initialValueMinor ?? v.value;
    const remaining = v.remainingMinor ?? initial;
    return `${formatMoney(initial, cur)} gift · ${formatMoney(remaining, cur)} remaining`;
  }
  const parts: string[] = [];
  parts.push(
    v.type === "pct" ? `${v.value}% off` : `${formatMoney(v.value, cur)} off`,
  );
  if (v.maxCapMinor !== null) {
    parts.push(`cap ${formatMoney(v.maxCapMinor, cur)}`);
  }
  if (v.minSpendMinor !== null) {
    parts.push(`min ${formatMoney(v.minSpendMinor, cur)}`);
  }
  parts.push(v.directOnly ? "direct/referral only" : "all channels");
  return parts.join(" · ");
}

/** A derived offer/voucher status: a human label plus its status-pill class. */
interface OfferStatus {
  readonly label: string;
  readonly cls: string;
}

/**
 * Derive the display status of an offer/voucher against a given `today`
 * (ISO `YYYY-MM-DD`). Archived rows are always "Inactive". Gift vouchers add a
 * "Fully Redeemed" state when their remaining balance reaches zero.
 */
function voucherStatus(v: AccountsVoucherRow, today: string): OfferStatus {
  const OK = "avos-status--ok";
  const PENDING = "avos-status--pending";
  const CRITICAL = "avos-status--critical";

  if (v.archived) {
    return { label: "Inactive", cls: PENDING };
  }

  const scheduled = v.validFrom !== null && v.validFrom > today;
  const expired = v.validTo !== null && v.validTo < today;

  if (v.kind === "gift") {
    const remaining = v.remainingMinor ?? v.initialValueMinor ?? v.value;
    if (remaining <= 0) {
      return { label: "Fully Redeemed", cls: PENDING };
    }
    if (!v.active) {
      return { label: "Inactive", cls: PENDING };
    }
    if (scheduled) {
      return { label: "Scheduled", cls: PENDING };
    }
    if (expired) {
      return { label: "Expired", cls: CRITICAL };
    }
    return { label: "Active", cls: OK };
  }

  // Promo.
  if (!v.active) {
    return { label: "Inactive", cls: PENDING };
  }
  if (scheduled) {
    return { label: "Scheduled", cls: PENDING };
  }
  if (expired) {
    return { label: "Expired", cls: CRITICAL };
  }
  return { label: "Active", cls: OK };
}

/** Result of the promo checker. */
interface CheckResult {
  readonly ok: boolean;
  readonly discountMinor?: number;
  readonly reason?: string;
  readonly currency: string;
}

/** Discount-type options for the promo form. */
type PromoDiscountType = "pct" | "fixed";

/** The kind chooser value on the create flow's first step. */
type OfferKind = "promo" | "gift";

// ---------------------------------------------------------------------------
// Create-offer flow (modal): step-1 chooser → promo or gift form
// ---------------------------------------------------------------------------

function CreateOfferFlow({
  properties,
  editing,
  duplicating,
  onClose,
  onSaved,
}: {
  readonly properties: readonly Property[];
  readonly editing: AccountsVoucherRow | null;
  readonly duplicating: boolean;
  readonly onClose: () => void;
  readonly onSaved: (message: string) => void;
}): ReactElement {
  // When editing/duplicating, jump straight to the matching form; otherwise
  // start on the type chooser (kind === null).
  const initialKind: OfferKind | null = editing !== null ? editing.kind : null;
  const [kind, setKind] = useState<OfferKind | null>(initialKind);

  // Shared prefill helpers (edit / duplicate). Duplicating blanks the code.
  const seedCode = editing !== null && !duplicating ? editing.code : "";
  const seedName = editing?.name ?? "";

  // ---- Promo form state -------------------------------------------------
  const [pName, setPName] = useState<string>(seedName);
  const [pCode, setPCode] = useState<string>(seedCode);
  const [pDiscType, setPDiscType] = useState<PromoDiscountType>(
    editing !== null ? editing.type : "pct",
  );
  const [pValue, setPValue] = useState<string>(
    editing !== null
      ? editing.type === "pct"
        ? String(editing.value)
        : (editing.value / 100).toFixed(2)
      : "",
  );
  const [pMaxCap, setPMaxCap] = useState<string>(
    editing?.maxCapMinor != null ? (editing.maxCapMinor / 100).toFixed(2) : "",
  );
  const [pMinSpend, setPMinSpend] = useState<string>(
    editing?.minSpendMinor != null ? (editing.minSpendMinor / 100).toFixed(2) : "",
  );
  const [pEligibility, setPEligibility] = useState<"all" | "new" | "returning">(
    editing?.eligibility ?? "all",
  );
  const [pScope, setPScope] = useState<"all" | "selected">(
    editing?.propertyScope ?? "all",
  );
  const [pPropIds, setPPropIds] = useState<readonly string[]>(
    editing?.propertyIds ?? [],
  );
  // Promos default to direct-only; never auto-enable OTA discounting.
  const [pDirectOnly, setPDirectOnly] = useState<boolean>(
    editing !== null ? editing.directOnly : true,
  );
  const [pValidFrom, setPValidFrom] = useState<string>(editing?.validFrom ?? "");
  const [pValidTo, setPValidTo] = useState<string>(editing?.validTo ?? "");
  const [pLimit, setPLimit] = useState<string>(
    editing?.limit != null ? String(editing.limit) : "",
  );
  const [pActive, setPActive] = useState<boolean>(
    editing !== null ? editing.active : true,
  );

  // ---- Gift form state --------------------------------------------------
  const [gName, setGName] = useState<string>(seedName);
  const [gCode, setGCode] = useState<string>(seedCode);
  const [gInitial, setGInitial] = useState<string>(
    editing?.initialValueMinor != null
      ? (editing.initialValueMinor / 100).toFixed(2)
      : "",
  );
  const [gScope, setGScope] = useState<"all" | "selected">(
    editing?.propertyScope ?? "all",
  );
  const [gPropIds, setGPropIds] = useState<readonly string[]>(
    editing?.propertyIds ?? [],
  );
  const [gValidFrom, setGValidFrom] = useState<string>(editing?.validFrom ?? "");
  const [gValidTo, setGValidTo] = useState<string>(editing?.validTo ?? "");
  const [gNotes, setGNotes] = useState<string>(editing?.notes ?? "");
  const [gActive, setGActive] = useState<boolean>(
    editing !== null ? editing.active : true,
  );

  const [submitting, setSubmitting] = useState(false);
  const [formNote, setFormNote] = useState<Note | null>(null);

  const toggleProp = (
    ids: readonly string[],
    setIds: (next: readonly string[]) => void,
    id: string,
  ): void => {
    setIds(ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]);
  };

  const fail = (what: string, why: string, next: string): void => {
    setFormNote({ severity: "error", model: validationMessage(what, why, next) });
  };

  // ---- Submit: promo ----------------------------------------------------
  const submitPromo = useCallback(
    async (e: FormEvent): Promise<void> => {
      e.preventDefault();
      setFormNote(null);
      if (pName.trim() === "") {
        fail("Offer name is required", "An offer needs a name.", "Enter a name such as Summer Sale.");
        return;
      }
      if (pCode.trim() === "") {
        fail("Promo code is required", "A promotional offer needs a code.", "Enter a code such as WELCOME10.");
        return;
      }
      let valueNum: number;
      if (pDiscType === "pct") {
        const v = Number(pValue);
        if (!Number.isFinite(v) || v <= 0) {
          fail("Percentage is invalid", "The discount percentage is not a valid positive number.", "Enter a percentage such as 10.");
          return;
        }
        valueNum = v;
      } else {
        const minor = parseMoneyToMinor(pValue);
        if (minor === null || minor <= 0) {
          fail("Amount is invalid", "The fixed discount amount is not a positive amount.", "Enter an amount greater than zero.");
          return;
        }
        valueNum = minor;
      }
      let maxCapMinor: number | null | undefined;
      if (pMaxCap.trim() !== "") {
        const m = parseMoneyToMinor(pMaxCap);
        if (m === null) {
          fail("Maximum discount is invalid", "The maximum discount is not a valid amount.", "Leave it blank or enter a non-negative amount.");
          return;
        }
        maxCapMinor = m;
      }
      let minSpendMinor: number | null | undefined;
      if (pMinSpend.trim() !== "") {
        const m = parseMoneyToMinor(pMinSpend);
        if (m === null) {
          fail("Minimum booking value is invalid", "The minimum booking value is not a valid amount.", "Leave it blank or enter a non-negative amount.");
          return;
        }
        minSpendMinor = m;
      }
      let limit: number | null | undefined;
      if (pLimit.trim() !== "") {
        const n = Number(pLimit);
        if (!Number.isInteger(n) || n <= 0) {
          fail("Redemption limit is invalid", "The redemption limit is not a positive whole number.", "Leave it blank or enter a whole number such as 100.");
          return;
        }
        limit = n;
      }
      if (pScope === "selected" && pPropIds.length === 0) {
        fail("Select at least one property", "You chose selected properties but picked none.", "Tick one or more properties, or choose All properties.");
        return;
      }

      setSubmitting(true);
      try {
        const result = await apiAccountsUpsertVoucher({
          kind: "promo",
          code: pCode.trim().toUpperCase(),
          name: pName.trim(),
          active: pActive,
          type: pDiscType,
          value: valueNum,
          eligibility: pEligibility,
          propertyScope: pScope,
          directOnly: pDirectOnly,
          ...(pScope === "selected" ? { propertyIds: pPropIds } : {}),
          ...(maxCapMinor !== undefined ? { maxCapMinor } : {}),
          ...(minSpendMinor !== undefined ? { minSpendMinor } : {}),
          ...(limit !== undefined ? { redemptionLimit: limit } : {}),
          ...(pValidFrom.trim() !== "" ? { validFrom: pValidFrom } : {}),
          ...(pValidTo.trim() !== "" ? { validTo: pValidTo } : {}),
        });
        const err = asError(result);
        if (err !== null) {
          setFormNote({ severity: "error", model: toUserMessage(err) });
          return;
        }
        onSaved("Offer saved.");
      } catch (caught) {
        fail(
          "Could not save the offer",
          caught instanceof Error ? caught.message : "The request failed.",
          "Check your connection and try again.",
        );
      } finally {
        setSubmitting(false);
      }
    },
    [
      pName, pCode, pDiscType, pValue, pMaxCap, pMinSpend, pLimit, pScope,
      pPropIds, pEligibility, pDirectOnly, pValidFrom, pValidTo, pActive, onSaved,
    ],
  );

  // ---- Submit: gift -----------------------------------------------------
  const submitGift = useCallback(
    async (e: FormEvent): Promise<void> => {
      e.preventDefault();
      setFormNote(null);
      if (gName.trim() === "") {
        fail("Voucher name is required", "A gift voucher needs a name.", "Enter a name such as Anniversary Gift.");
        return;
      }
      if (gCode.trim() === "") {
        fail("Voucher code is required", "A gift voucher needs a code.", "Enter a code such as GIFT500.");
        return;
      }
      const initialMinor = parseMoneyToMinor(gInitial);
      if (initialMinor === null || initialMinor <= 0) {
        fail("Initial value is invalid", "The initial value is not a positive amount.", "Enter an amount greater than zero.");
        return;
      }
      if (gScope === "selected" && gPropIds.length === 0) {
        fail("Select at least one property", "You chose selected properties but picked none.", "Tick one or more properties, or choose All properties.");
        return;
      }

      setSubmitting(true);
      try {
        const result = await apiAccountsUpsertVoucher({
          kind: "gift",
          code: gCode.trim().toUpperCase(),
          name: gName.trim(),
          active: gActive,
          type: "fixed",
          value: initialMinor,
          initialValueMinor: initialMinor,
          propertyScope: gScope,
          ...(gScope === "selected" ? { propertyIds: gPropIds } : {}),
          ...(gValidFrom.trim() !== "" ? { validFrom: gValidFrom } : {}),
          ...(gValidTo.trim() !== "" ? { validTo: gValidTo } : {}),
          ...(gNotes.trim() !== "" ? { notes: gNotes.trim() } : {}),
        });
        const err = asError(result);
        if (err !== null) {
          setFormNote({ severity: "error", model: toUserMessage(err) });
          return;
        }
        onSaved("Offer saved.");
      } catch (caught) {
        fail(
          "Could not save the voucher",
          caught instanceof Error ? caught.message : "The request failed.",
          "Check your connection and try again.",
        );
      } finally {
        setSubmitting(false);
      }
    },
    [
      gName, gCode, gInitial, gScope, gPropIds, gValidFrom, gValidTo, gNotes,
      gActive, onSaved,
    ],
  );

  const title =
    editing !== null
      ? duplicating
        ? "Duplicate offer"
        : "Edit offer"
      : "Create offer";

  return (
    <div
      className="console-modal-scrim"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="console-modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <header className="console-modal__header">
          <h2 className="console-modal__title">{title}</h2>
          <button
            type="button"
            className="console-btn console-btn--ghost console-modal__close"
            aria-label="Close"
            onClick={onClose}
          >
            ✕
          </button>
        </header>
        <div className="console-modal__body">
          {formNote !== null ? (
            <UserMessage message={formNote.model} severity={formNote.severity} />
          ) : null}

          {/* ---- Step 1: what would you like to create? ---- */}
          {kind === null ? (
            <div className="accounts-offer-chooser">
              <p className="recon-caption">What would you like to create?</p>
              <button
                type="button"
                className="accounts-offer-chooser__card"
                onClick={() => setKind("promo")}
              >
                <span className="accounts-offer-chooser__title">Promotional Offer</span>
                <span className="accounts-offer-chooser__sub">
                  Discount a booking using a code or offer.
                </span>
              </button>
              <button
                type="button"
                className="accounts-offer-chooser__card"
                onClick={() => setKind("gift")}
              >
                <span className="accounts-offer-chooser__title">Gift Voucher</span>
                <span className="accounts-offer-chooser__sub">
                  Create a monetary voucher that can be redeemed against bookings.
                </span>
              </button>
            </div>
          ) : null}

          {/* ---- Promotional offer form ---- */}
          {kind === "promo" ? (
            <form className="console-form" noValidate onSubmit={submitPromo}>
              <div className="console-form__row">
                <div className="console-field">
                  <label htmlFor="po-name">Offer name</label>
                  <input id="po-name" type="text" value={pName} onChange={(e) => setPName(e.target.value)} placeholder="Summer Sale" />
                </div>
                <div className="console-field">
                  <label htmlFor="po-code">Promo code</label>
                  <input id="po-code" type="text" value={pCode} onChange={(e) => setPCode(e.target.value)} placeholder="WELCOME10" />
                </div>
              </div>
              <div className="console-form__row">
                <div className="console-field">
                  <label htmlFor="po-disctype">Discount type</label>
                  <select id="po-disctype" value={pDiscType} onChange={(e) => setPDiscType(e.target.value === "fixed" ? "fixed" : "pct")}>
                    <option value="pct">Percentage</option>
                    <option value="fixed">Fixed amount</option>
                  </select>
                </div>
                <div className="console-field">
                  <label htmlFor="po-value">{pDiscType === "pct" ? "Discount value (%)" : "Discount value (amount)"}</label>
                  <input id="po-value" type="text" inputMode="decimal" value={pValue} onChange={(e) => setPValue(e.target.value)} placeholder={pDiscType === "pct" ? "10" : "0.00"} />
                </div>
              </div>
              <div className="console-form__row">
                <div className="console-field">
                  <label htmlFor="po-maxcap">Maximum discount (amount)</label>
                  <input id="po-maxcap" type="text" inputMode="decimal" value={pMaxCap} onChange={(e) => setPMaxCap(e.target.value)} placeholder="Optional" />
                </div>
                <div className="console-field">
                  <label htmlFor="po-minspend">Minimum booking value (amount)</label>
                  <input id="po-minspend" type="text" inputMode="decimal" value={pMinSpend} onChange={(e) => setPMinSpend(e.target.value)} placeholder="Optional" />
                </div>
              </div>
              <div className="console-form__row">
                <div className="console-field">
                  <label htmlFor="po-elig">Eligibility</label>
                  <select id="po-elig" value={pEligibility} onChange={(e) => { const v = e.target.value; setPEligibility(v === "new" ? "new" : v === "returning" ? "returning" : "all"); }}>
                    <option value="all">All guests</option>
                    <option value="new">New guests</option>
                    <option value="returning">Returning guests</option>
                  </select>
                </div>
                <div className="console-field">
                  <label htmlFor="po-scope">Applies to</label>
                  <select id="po-scope" value={pScope} onChange={(e) => setPScope(e.target.value === "selected" ? "selected" : "all")}>
                    <option value="all">All properties</option>
                    <option value="selected">Selected properties</option>
                  </select>
                </div>
              </div>
              {pScope === "selected" ? (
                <fieldset className="console-field accounts-offer-proplist">
                  <legend>Choose properties</legend>
                  {properties.map((p) => (
                    <label key={p.id} className="accounts-offer-proplist__item" htmlFor={`po-prop-${p.id}`}>
                      <input
                        id={`po-prop-${p.id}`}
                        type="checkbox"
                        checked={pPropIds.includes(p.id)}
                        onChange={() => toggleProp(pPropIds, setPPropIds, p.id)}
                      />
                      {p.name}
                    </label>
                  ))}
                </fieldset>
              ) : null}
              <div className="console-form__row">
                <div className="console-field">
                  <label htmlFor="po-from">Valid from</label>
                  <input id="po-from" type="date" value={pValidFrom} onChange={(e) => setPValidFrom(e.target.value)} />
                </div>
                <div className="console-field">
                  <label htmlFor="po-to">Valid until</label>
                  <input id="po-to" type="date" value={pValidTo} onChange={(e) => setPValidTo(e.target.value)} />
                </div>
                <div className="console-field">
                  <label htmlFor="po-limit">Total redemption limit</label>
                  <input id="po-limit" type="number" min="1" step="1" value={pLimit} onChange={(e) => setPLimit(e.target.value)} placeholder="Optional" />
                </div>
              </div>
              <div className="console-form__row">
                <div className="console-field">
                  <label htmlFor="po-channel">Booking channel</label>
                  <select id="po-channel" value="direct" disabled>
                    <option value="direct">Direct Booking</option>
                  </select>
                </div>
                <div className="console-field console-field--checkbox">
                  <label htmlFor="po-directonly">
                    <input id="po-directonly" type="checkbox" checked={pDirectOnly} onChange={(e) => setPDirectOnly(e.target.checked)} />
                    Direct bookings only
                  </label>
                </div>
                <div className="console-field console-field--checkbox">
                  <label htmlFor="po-active">
                    <input id="po-active" type="checkbox" checked={pActive} onChange={(e) => setPActive(e.target.checked)} />
                    Active
                  </label>
                </div>
              </div>
              <div className="console-modal__actions">
                <button type="button" className="console-btn console-btn--ghost" onClick={onClose}>Cancel</button>
                <button type="submit" className="console-btn" disabled={submitting}>{submitting ? "Saving…" : "Save offer"}</button>
              </div>
            </form>
          ) : null}

          {/* ---- Gift voucher form ---- */}
          {kind === "gift" ? (
            <form className="console-form" noValidate onSubmit={submitGift}>
              <div className="console-form__row">
                <div className="console-field">
                  <label htmlFor="gv-name">Voucher name</label>
                  <input id="gv-name" type="text" value={gName} onChange={(e) => setGName(e.target.value)} placeholder="Anniversary Gift" />
                </div>
                <div className="console-field">
                  <label htmlFor="gv-code">Voucher code</label>
                  <input id="gv-code" type="text" value={gCode} onChange={(e) => setGCode(e.target.value)} placeholder="GIFT500" />
                </div>
              </div>
              <div className="console-form__row">
                <div className="console-field">
                  <label htmlFor="gv-initial">Initial value (amount)</label>
                  <input id="gv-initial" type="text" inputMode="decimal" value={gInitial} onChange={(e) => setGInitial(e.target.value)} placeholder="0.00" />
                </div>
                <div className="console-field">
                  <label htmlFor="gv-scope">Property applicability</label>
                  <select id="gv-scope" value={gScope} onChange={(e) => setGScope(e.target.value === "selected" ? "selected" : "all")}>
                    <option value="all">All properties</option>
                    <option value="selected">Selected properties</option>
                  </select>
                </div>
              </div>
              {gScope === "selected" ? (
                <fieldset className="console-field accounts-offer-proplist">
                  <legend>Choose properties</legend>
                  {properties.map((p) => (
                    <label key={p.id} className="accounts-offer-proplist__item" htmlFor={`gv-prop-${p.id}`}>
                      <input
                        id={`gv-prop-${p.id}`}
                        type="checkbox"
                        checked={gPropIds.includes(p.id)}
                        onChange={() => toggleProp(gPropIds, setGPropIds, p.id)}
                      />
                      {p.name}
                    </label>
                  ))}
                </fieldset>
              ) : null}
              <div className="console-form__row">
                <div className="console-field">
                  <label htmlFor="gv-from">Valid from</label>
                  <input id="gv-from" type="date" value={gValidFrom} onChange={(e) => setGValidFrom(e.target.value)} />
                </div>
                <div className="console-field">
                  <label htmlFor="gv-to">Expiry date</label>
                  <input id="gv-to" type="date" value={gValidTo} onChange={(e) => setGValidTo(e.target.value)} />
                </div>
              </div>
              <div className="console-form__row">
                <div className="console-field">
                  <label htmlFor="gv-notes">Redemption rules &amp; notes</label>
                  <textarea id="gv-notes" rows={3} value={gNotes} onChange={(e) => setGNotes(e.target.value)} placeholder="Optional redemption rules or notes" />
                </div>
                <div className="console-field console-field--checkbox">
                  <label htmlFor="gv-active">
                    <input id="gv-active" type="checkbox" checked={gActive} onChange={(e) => setGActive(e.target.checked)} />
                    Active
                  </label>
                </div>
              </div>
              <div className="console-modal__actions">
                <button type="button" className="console-btn console-btn--ghost" onClick={onClose}>Cancel</button>
                <button type="submit" className="console-btn" disabled={submitting}>{submitting ? "Saving…" : "Save voucher"}</button>
              </div>
            </form>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Redemption-history modal (gift vouchers)
// ---------------------------------------------------------------------------

function RedemptionHistoryModal({
  voucher,
  currency,
  onClose,
}: {
  readonly voucher: AccountsVoucherRow;
  readonly currency: string;
  readonly onClose: () => void;
}): ReactElement {
  const [rows, setRows] = useState<readonly AccountsVoucherRedemptionRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const cur = currency || FALLBACK_CURRENCY;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async (): Promise<void> => {
      try {
        const list = await apiAccountsVoucherRedemptions(voucher.id);
        if (!cancelled) setRows(list);
      } catch {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [voucher.id]);

  const initial = voucher.initialValueMinor ?? voucher.value;
  const remaining = voucher.remainingMinor ?? initial;

  return (
    <div
      className="console-modal-scrim"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="console-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Redemption history"
      >
        <header className="console-modal__header">
          <h2 className="console-modal__title">
            Redemption history — {offerName(voucher)}
          </h2>
          <button
            type="button"
            className="console-btn console-btn--ghost console-modal__close"
            aria-label="Close"
            onClick={onClose}
          >
            ✕
          </button>
        </header>
        <div className="console-modal__body">
          <p className="recon-caption">
            {voucher.code} · Initial {formatMoney(initial, cur)} · Redeemed{" "}
            {formatMoney(voucher.redeemedMinor, cur)} · Remaining{" "}
            {formatMoney(remaining, cur)}
          </p>
          {loading ? (
            <p className="console-loading" role="status" aria-live="polite">
              Loading redemptions…
            </p>
          ) : rows.length === 0 ? (
            <p className="console-empty">No redemptions yet.</p>
          ) : (
            <table className="console-table">
              <caption className="console-sr-only">Redemptions</caption>
              <thead>
                <tr>
                  <th scope="col">Date</th>
                  <th scope="col">Amount</th>
                  <th scope="col">Note</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>{formatDate(r.date)}</td>
                    <td className="recon-num">{formatMoney(Number(r.amountMinor), cur)}</td>
                    <td>{r.note ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="console-modal__actions">
            <button type="button" className="console-btn console-btn--ghost" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function VoucherCheckModal({
  onClose,
}: {
  readonly onClose: () => void;
}): ReactElement {
  const [checkCode, setCheckCode] = useState<string>("");
  const [checkAmount, setCheckAmount] = useState<string>("");
  const [checkIsOta, setCheckIsOta] = useState<boolean>(false);
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState<CheckResult | null>(null);
  const [checkNote, setCheckNote] = useState<Note | null>(null);

  const handleCheck = useCallback(
    async (e: FormEvent): Promise<void> => {
      e.preventDefault();
      setCheckNote(null);
      setCheckResult(null);
      if (checkCode.trim() === "") {
        setCheckNote({
          severity: "error",
          model: validationMessage(
            "Code is required",
            "Enter the voucher code you want to test.",
            "Type a code such as WELCOME10.",
          ),
        });
        return;
      }
      const amountMinor = parseMoneyToMinor(checkAmount);
      if (amountMinor === null || amountMinor <= 0) {
        setCheckNote({
          severity: "error",
          model: validationMessage(
            "Amount is invalid",
            "The order amount is not a positive amount.",
            "Enter an amount greater than zero.",
          ),
        });
        return;
      }
      setChecking(true);
      try {
        // The checker asks against "today" — but we must not read the clock at
        // module/render level. Deriving it inside this handler is SSR-safe.
        const today = new Date().toISOString().slice(0, 10);
        const result = await apiAccountsCheckVoucher({
          code: checkCode.trim(),
          date: today,
          channelIsOta: checkIsOta,
          amountMinor,
        });
        const err = asError(result);
        if (err !== null) {
          setCheckNote({ severity: "error", model: toUserMessage(err) });
          return;
        }
        const ok = (result as { ok: boolean }).ok;
        const discountMinor = (result as { discountMinor?: number }).discountMinor;
        const reason = (result as { reason?: string }).reason;
        setCheckResult({
          ok,
          ...(discountMinor !== undefined ? { discountMinor } : {}),
          ...(reason !== undefined ? { reason } : {}),
          currency: FALLBACK_CURRENCY,
        });
      } catch (caught) {
        setCheckNote({
          severity: "error",
          model: validationMessage(
            "Could not check the voucher",
            caught instanceof Error ? caught.message : "The request failed.",
            "Check your connection and try again.",
          ),
        });
      } finally {
        setChecking(false);
      }
    },
    [checkCode, checkAmount, checkIsOta],
  );

  return (
    <div
      className="console-modal-scrim"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="console-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Check a voucher"
      >
        <header className="console-modal__header">
          <h2 className="console-modal__title">Check a voucher</h2>
          <button
            type="button"
            className="console-btn console-btn--ghost console-modal__close"
            aria-label="Close"
            onClick={onClose}
          >
            ✕
          </button>
        </header>
        <div className="console-modal__body">
          {checkNote !== null ? (
            <UserMessage message={checkNote.model} severity={checkNote.severity} />
          ) : null}
          <form className="console-form" noValidate onSubmit={handleCheck}>
            <div className="console-form__row">
              <div className="console-field">
                <label htmlFor="vc-code">Code</label>
                <input
                  id="vc-code"
                  type="text"
                  value={checkCode}
                  onChange={(e) => setCheckCode(e.target.value)}
                  placeholder="WELCOME10"
                />
              </div>
              <div className="console-field">
                <label htmlFor="vc-amount">Amount</label>
                <input
                  id="vc-amount"
                  type="text"
                  inputMode="decimal"
                  value={checkAmount}
                  onChange={(e) => setCheckAmount(e.target.value)}
                  placeholder="0.00"
                />
              </div>
              <div className="console-field console-field--checkbox">
                <label htmlFor="vc-isota">
                  <input
                    id="vc-isota"
                    type="checkbox"
                    checked={checkIsOta}
                    onChange={(e) => setCheckIsOta(e.target.checked)}
                  />
                  Channel is an OTA
                </label>
              </div>
            </div>
            {checkResult !== null ? (
              <p className="recon-caption">
                {checkResult.ok ? (
                  <span className="avos-status avos-status--ok">
                    Valid — discount{" "}
                    {formatMoney(
                      checkResult.discountMinor ?? 0,
                      checkResult.currency,
                    )}
                  </span>
                ) : (
                  <span className="avos-status avos-status--critical">
                    Not applicable
                    {checkResult.reason ? ` — ${checkResult.reason}` : ""}
                  </span>
                )}
              </p>
            ) : null}
            <div className="console-modal__actions">
              <button
                type="button"
                className="console-btn console-btn--ghost"
                onClick={onClose}
              >
                Close
              </button>
              <button type="submit" className="console-btn" disabled={checking}>
                {checking ? "Checking…" : "Check voucher"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function OffersVouchersTab({
  propertyId: _propertyId,
  currency,
  onRefresh,
}: {
  readonly propertyId: string | undefined;
  readonly currency: string;
  readonly onRefresh: (message: string) => void;
}): ReactElement {
  const [rows, setRows] = useState<readonly AccountsVoucherRow[]>([]);
  const [properties, setProperties] = useState<readonly Property[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshToken, setRefreshToken] = useState<number>(0);
  const [note, setNote] = useState<Note | null>(null);

  // Overlays.
  const [createOpen, setCreateOpen] = useState<boolean>(false);
  const [editing, setEditing] = useState<AccountsVoucherRow | null>(null);
  const [duplicating, setDuplicating] = useState<boolean>(false);
  const [checkOpen, setCheckOpen] = useState<boolean>(false);
  const [historyVoucher, setHistoryVoucher] = useState<AccountsVoucherRow | null>(null);
  // Two-click inline archive confirmation (no window.confirm).
  const [confirmArchiveId, setConfirmArchiveId] = useState<string | null>(null);

  // Filters.
  const [search, setSearch] = useState<string>("");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [propFilter, setPropFilter] = useState<string>("all");
  const [validityFilter, setValidityFilter] = useState<string>("all");

  const cur = currency || FALLBACK_CURRENCY;

  // "today" for status derivation — computed once, client-side (SSR-safe: no
  // clock read at module/render top-level; useMemo runs during client render
  // and this file's tabs are client-rendered).
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const refresh = useCallback(
    (message: string): void => {
      setRefreshToken((t) => t + 1);
      onRefresh(message);
    },
    [onRefresh],
  );

  // Load properties once (for the create form + property filter).
  useEffect(() => {
    let cancelled = false;
    void (async (): Promise<void> => {
      try {
        const list = await apiAccountsProperties();
        if (!cancelled) setProperties(list);
      } catch {
        if (!cancelled) setProperties([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load rows (re-loads when refreshToken bumps).
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNote(null);
    void (async (): Promise<void> => {
      try {
        const list = await apiAccountsVouchers();
        if (!cancelled) setRows(list);
      } catch (caught) {
        if (!cancelled) {
          setNote({
            severity: "error",
            model: validationMessage(
              "Could not load offers",
              caught instanceof Error ? caught.message : "The request failed.",
              "Check your connection and try again.",
            ),
          });
          setRows([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshToken]);

  const openCreate = useCallback((): void => {
    setEditing(null);
    setDuplicating(false);
    setCreateOpen(true);
  }, []);
  const openEdit = useCallback((v: AccountsVoucherRow): void => {
    setEditing(v);
    setDuplicating(false);
    setCreateOpen(true);
  }, []);
  const openDuplicate = useCallback((v: AccountsVoucherRow): void => {
    setEditing(v);
    setDuplicating(true);
    setCreateOpen(true);
  }, []);
  const closeCreate = useCallback((): void => {
    setCreateOpen(false);
    setEditing(null);
    setDuplicating(false);
  }, []);
  const handleSaved = useCallback(
    (message: string): void => {
      closeCreate();
      refresh(message);
    },
    [closeCreate, refresh],
  );

  const toggleActive = useCallback(
    async (v: AccountsVoucherRow): Promise<void> => {
      try {
        const result = await apiAccountsSetVoucherActive({ id: v.id, active: !v.active });
        const err = asError(result);
        if (err !== null) {
          setNote({ severity: "error", model: toUserMessage(err) });
          return;
        }
        refresh(v.active ? "Offer deactivated." : "Offer activated.");
      } catch (caught) {
        setNote({
          severity: "error",
          model: validationMessage(
            "Could not update the offer",
            caught instanceof Error ? caught.message : "The request failed.",
            "Check your connection and try again.",
          ),
        });
      }
    },
    [refresh],
  );

  const archive = useCallback(
    async (v: AccountsVoucherRow): Promise<void> => {
      setConfirmArchiveId(null);
      try {
        const result = await apiAccountsArchiveVoucher({ id: v.id });
        const err = asError(result);
        if (err !== null) {
          setNote({ severity: "error", model: toUserMessage(err) });
          return;
        }
        refresh("Offer archived.");
      } catch (caught) {
        setNote({
          severity: "error",
          model: validationMessage(
            "Could not archive the offer",
            caught instanceof Error ? caught.message : "The request failed.",
            "Check your connection and try again.",
          ),
        });
      }
    },
    [refresh],
  );

  // ---- Summary figures --------------------------------------------------
  const activeCount = rows.filter((v) => {
    if (v.archived) return false;
    const s = voucherStatus(v, today).label;
    return s === "Active" || s === "Scheduled";
  }).length;
  const totalRedemptions = rows.reduce((sum, v) => sum + v.redemptions, 0);
  const discountGivenMinor = rows
    .filter((v) => v.kind === "gift")
    .reduce((sum, v) => sum + v.redeemedMinor, 0);
  const giftBalanceMinor = rows
    .filter((v) => v.kind === "gift")
    .reduce((sum, v) => sum + (v.remainingMinor ?? 0), 0);

  // ---- Filtering --------------------------------------------------------
  const filtered = rows.filter((v) => {
    const q = search.trim().toLowerCase();
    if (q !== "") {
      const hay = `${offerName(v)} ${v.code}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (typeFilter !== "all" && v.kind !== typeFilter) return false;
    if (statusFilter !== "all" && voucherStatus(v, today).label !== statusFilter) {
      return false;
    }
    if (propFilter !== "all") {
      const applies = v.propertyScope === "all" || v.propertyIds.includes(propFilter);
      if (!applies) return false;
    }
    if (validityFilter !== "all") {
      const scheduled = v.validFrom !== null && v.validFrom > today;
      const expired = v.validTo !== null && v.validTo < today;
      if (validityFilter === "valid" && (scheduled || expired)) return false;
      if (validityFilter === "scheduled" && !scheduled) return false;
      if (validityFilter === "expired" && !expired) return false;
    }
    return true;
  });

  const propertyName = (id: string): string =>
    properties.find((p) => p.id === id)?.name ?? id;

  const valueLabel = (v: AccountsVoucherRow): string => {
    if (v.kind === "gift") {
      return `${formatMoney(v.initialValueMinor ?? v.value, cur)} gift`;
    }
    return v.type === "pct" ? `${v.value}% off` : `${formatMoney(v.value, cur)} off`;
  };

  const appliesLabel = (v: AccountsVoucherRow): string => {
    const scope =
      v.propertyScope === "all"
        ? "All properties"
        : `${v.propertyIds.length} propert${v.propertyIds.length === 1 ? "y" : "ies"}`;
    if (v.kind === "promo" && v.eligibility !== "all") {
      return `${scope} · ${v.eligibility === "new" ? "New guests" : "Returning guests"}`;
    }
    return scope;
  };

  const validityLabel = (v: AccountsVoucherRow): string => {
    if (v.validFrom === null && v.validTo === null) return "Always";
    if (v.kind === "gift" && v.validFrom === null && v.validTo !== null) {
      return `Expires ${formatDate(v.validTo)}`;
    }
    return `${formatDate(v.validFrom)} – ${v.validTo !== null ? formatDate(v.validTo) : "no end"}`;
  };

  const usageLabel = (v: AccountsVoucherRow): string => {
    if (v.kind === "gift") {
      return `${formatMoney(v.remainingMinor ?? 0, cur)} remaining`;
    }
    return `${v.redemptions} / ${v.limit ?? "∞"}`;
  };

  return (
    <>
      <section className="console-panel" aria-label="Offers & Vouchers">
        <div className="accounts-panel-head">
          <div>
            <h2 className="console-panel__title">Offers &amp; Vouchers</h2>
            <p className="recon-caption">
              Create booking offers, promotional codes and gift vouchers for guests.
            </p>
          </div>
          <div className="accounts-panel-head__actions">
            <button
              type="button"
              className="console-btn console-btn--ghost"
              onClick={() => setCheckOpen(true)}
            >
              Check a promo
            </button>
            <button type="button" className="console-btn" onClick={openCreate}>
              + Create offer
            </button>
          </div>
        </div>

        {note !== null ? (
          <UserMessage message={note.model} severity={note.severity} />
        ) : null}

        {/* ---- Summary cards ---- */}
        <div className="console-card-grid accounts-offer-cards">
          <div className="console-card console-card--gold accounts-kpi accounts-kpi--hero">
            <p className="console-card__label">Active Offers</p>
            <p className="console-card__value">{activeCount}</p>
            <p className="console-card__hint">Live and scheduled</p>
          </div>
          <div className="console-card accounts-kpi">
            <p className="console-card__label">Total Redemptions</p>
            <p className="console-card__value">{totalRedemptions}</p>
            <p className="console-card__hint">Across all offers</p>
          </div>
          <div className="console-card accounts-kpi">
            <p className="console-card__label">Discount Given</p>
            <MoneyValue minor={discountGivenMinor} currency={cur} />
            <p className="console-card__hint">Value redeemed to date</p>
          </div>
          <div className="console-card accounts-kpi">
            <p className="console-card__label">Gift Voucher Balance</p>
            <MoneyValue minor={giftBalanceMinor} currency={cur} />
            <p className="console-card__hint">Outstanding gift balance</p>
          </div>
        </div>

        {/* ---- Filters ---- */}
        <div className="accounts-invcontrols">
          <div className="console-field accounts-invcontrols__search">
            <label htmlFor="off-search">Search</label>
            <input
              id="off-search"
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, promo code or voucher code"
            />
          </div>
          <div className="console-field accounts-invcontrols__status">
            <label htmlFor="off-type">Type</label>
            <select id="off-type" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
              <option value="all">All</option>
              <option value="promo">Promo Code</option>
              <option value="gift">Gift Voucher</option>
            </select>
          </div>
          <div className="console-field accounts-invcontrols__status">
            <label htmlFor="off-status">Status</label>
            <select id="off-status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">All</option>
              <option value="Active">Active</option>
              <option value="Scheduled">Scheduled</option>
              <option value="Expired">Expired</option>
              <option value="Inactive">Inactive</option>
              <option value="Fully Redeemed">Fully Redeemed</option>
            </select>
          </div>
          <div className="console-field accounts-invcontrols__status">
            <label htmlFor="off-prop">Property</label>
            <select id="off-prop" value={propFilter} onChange={(e) => setPropFilter(e.target.value)}>
              <option value="all">All properties</option>
              {properties.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <div className="console-field accounts-invcontrols__status">
            <label htmlFor="off-validity">Validity</label>
            <select id="off-validity" value={validityFilter} onChange={(e) => setValidityFilter(e.target.value)}>
              <option value="all">All</option>
              <option value="valid">Currently valid</option>
              <option value="scheduled">Scheduled</option>
              <option value="expired">Expired</option>
            </select>
          </div>
        </div>

        {loading ? (
          <p className="console-loading" role="status" aria-live="polite">
            Loading offers…
          </p>
        ) : rows.length === 0 ? (
          <div className="console-empty">
            <h3>No offers or vouchers yet</h3>
            <p>
              Create a promotional offer or gift voucher that guests can use when
              booking directly.
            </p>
            <button type="button" className="console-btn" onClick={openCreate}>
              + Create offer
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <p className="console-empty">No offers match these filters.</p>
        ) : (
          <table className="console-table">
            <caption className="console-sr-only">Offers and vouchers</caption>
            <thead>
              <tr>
                <th scope="col">Offer / Code</th>
                <th scope="col">Type</th>
                <th scope="col">Value</th>
                <th scope="col">Applies To</th>
                <th scope="col">Validity</th>
                <th scope="col">Usage</th>
                <th scope="col">Status</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((v) => {
                const status = voucherStatus(v, today);
                const actions: RowMenuAction[] = [
                  { key: "edit", label: "Edit", onClick: () => openEdit(v) },
                  { key: "duplicate", label: "Duplicate", onClick: () => openDuplicate(v) },
                  {
                    key: "toggle",
                    label: v.active ? "Deactivate" : "Activate",
                    onClick: () => void toggleActive(v),
                  },
                ];
                if (v.kind === "gift") {
                  actions.push({
                    key: "history",
                    label: "View redemption history",
                    onClick: () => setHistoryVoucher(v),
                  });
                }
                actions.push({
                  key: "archive",
                  label: confirmArchiveId === v.id ? "Click again to archive" : "Archive",
                  danger: true,
                  disabled: v.archived,
                  onClick: () => {
                    if (confirmArchiveId === v.id) {
                      void archive(v);
                    } else {
                      setConfirmArchiveId(v.id);
                    }
                  },
                });
                return (
                  <tr key={v.id}>
                    <td>
                      <strong>{offerName(v)}</strong>
                      <span className="recon-caption accounts-offer-code">{v.code}</span>
                    </td>
                    <td>{v.kind === "gift" ? "Gift Voucher" : "Promo Code"}</td>
                    <td title={voucherTerms(v, cur)}>{valueLabel(v)}</td>
                    <td>
                      {appliesLabel(v)}
                      {v.propertyScope === "selected" && v.propertyIds.length === 1 ? (
                        <span className="recon-caption accounts-offer-code">
                          {propertyName(v.propertyIds[0] as string)}
                        </span>
                      ) : null}
                    </td>
                    <td>{validityLabel(v)}</td>
                    <td className="recon-num">{usageLabel(v)}</td>
                    <td>
                      <span className={`avos-status ${status.cls}`}>{status.label}</span>
                    </td>
                    <td>
                      <div className="accounts-rowactions">
                        <button
                          type="button"
                          className="console-btn console-btn--ghost"
                          onClick={() =>
                            v.kind === "gift" ? setHistoryVoucher(v) : openEdit(v)
                          }
                        >
                          View
                        </button>
                        <RowMenu actions={actions} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      {createOpen ? (
        <CreateOfferFlow
          properties={properties}
          editing={editing}
          duplicating={duplicating}
          onClose={closeCreate}
          onSaved={handleSaved}
        />
      ) : null}
      {checkOpen ? (
        <VoucherCheckModal onClose={() => setCheckOpen(false)} />
      ) : null}
      {historyVoucher !== null ? (
        <RedemptionHistoryModal
          voucher={historyVoucher}
          currency={cur}
          onClose={() => setHistoryVoucher(null)}
        />
      ) : null}
    </>
  );
}

// ===========================================================================
// Staff & Payroll tab
// ===========================================================================

/** The Staff & Payroll internal sub-tabs, in order. */
const STAFF_SUBS = [
  { key: "team", label: "Team" },
  { key: "payroll", label: "Payroll" },
  { key: "payslips", label: "Payslips" },
] as const;

type StaffSubKey = (typeof STAFF_SUBS)[number]["key"];

/** Roles seeded into the team-member drawer's Role select. */
const STAFF_ROLES = [
  "Property Manager",
  "Cook",
  "Chef",
  "Housekeeping",
  "Security Guard",
  "Gardener",
  "Driver",
  "Reception / Operations",
  "Salesperson",
  "Accountant",
  "Other staff",
] as const;

/** Employment-type options for the team-member drawer + humanised labels. */
const EMPLOYMENT_TYPES = [
  { value: "full_time", label: "Full-time" },
  { value: "part_time", label: "Part-time" },
  { value: "contract", label: "Contract" },
  { value: "casual", label: "Casual" },
] as const;

/** Humanise a stored employment type. */
function employmentTypeLabel(value: AccountsEmploymentType): string {
  return EMPLOYMENT_TYPES.find((t) => t.value === value)?.label ?? value;
}

/** Payment-method options for staff (bank/cash/upi) + humanised labels. */
const STAFF_PAYMENT_METHODS = [
  { value: "bank", label: "Bank Transfer" },
  { value: "cash", label: "Cash" },
  { value: "upi", label: "UPI" },
] as const;

/** Humanise a staff payment method. */
function staffPaymentMethodLabel(value: AccountsStaffPaymentMethod | null): string {
  if (value === null) return "—";
  return STAFF_PAYMENT_METHODS.find((m) => m.value === value)?.label ?? value;
}

/**
 * Format a `YYYY-MM` or `YYYY-MM-01` period as "Mon YYYY" (e.g. "Mar 2026").
 * Purely string-derived (SSR-safe: no clock read).
 */
function formatPeriodMonth(period: string): string {
  const head = period.slice(0, 7);
  const [y, m] = head.split("-");
  if (y === undefined || m === undefined) return period;
  const MONTHS = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  const label = MONTHS[Number(m) - 1] ?? m;
  return `${label} ${y}`;
}

/** Map a payroll payment status to the shared status-pill markup. */
function payrollStatusPill(status: "pending" | "paid"): ReactElement {
  return status === "paid" ? (
    <span className="avos-status avos-status--ok">Paid</span>
  ) : (
    <span className="avos-status avos-status--pending">Pending</span>
  );
}

// ---------------------------------------------------------------------------
// Team member drawer (add / edit a staff person)
// ---------------------------------------------------------------------------

function TeamMemberDrawer({
  propertyId,
  properties,
  editing,
  onClose,
  onSaved,
}: {
  readonly propertyId: string | undefined;
  readonly properties: readonly Property[];
  readonly editing: AccountsPayrollRow | null;
  readonly onClose: () => void;
  readonly onSaved: (message: string) => void;
}): ReactElement {
  const isEdit = editing !== null;
  const [name, setName] = useState<string>(editing?.name ?? "");
  const [mobile, setMobile] = useState<string>(editing?.mobile ?? "");
  const [email, setEmail] = useState<string>(editing?.email ?? "");
  const [role, setRole] = useState<string>(editing?.role ?? "");
  const [prop, setProp] = useState<string>(editing?.propertyId ?? propertyId ?? "");
  const [employmentType, setEmploymentType] = useState<AccountsEmploymentType>(
    editing?.employmentType ?? "full_time",
  );
  const [joiningDate, setJoiningDate] = useState<string>(editing?.joiningDate ?? "");
  const [baseSalary, setBaseSalary] = useState<string>(
    editing !== null ? (Number(editing.baseSalaryMinor) / 100).toString() : "",
  );
  const [commissionType, setCommissionType] = useState<AccountsCommissionType>(
    editing?.commissionType ?? "none",
  );
  const [commissionPct, setCommissionPct] = useState<string>(
    editing !== null && editing.commissionType === "pct" ? String(editing.commissionPct) : "",
  );
  const [commissionValue, setCommissionValue] = useState<string>(
    editing !== null && editing.commissionType === "fixed"
      ? (Number(editing.commissionValueMinor) / 100).toString()
      : "",
  );
  const [paymentMethod, setPaymentMethod] = useState<AccountsStaffPaymentMethod>(
    editing?.paymentMethod ?? "bank",
  );
  const [accountHolder, setAccountHolder] = useState<string>(editing?.accountHolder ?? "");
  const [bankName, setBankName] = useState<string>(editing?.bankName ?? "");
  const [accountNumber, setAccountNumber] = useState<string>("");
  const [ifsc, setIfsc] = useState<string>(editing?.ifsc ?? "");
  const [upiId, setUpiId] = useState<string>(editing?.upiId ?? "");
  const [pan, setPan] = useState<string>(editing?.pan ?? "");
  const [notes, setNotes] = useState<string>(editing?.notes ?? "");
  const [active, setActive] = useState<boolean>(editing?.active ?? true);
  const [submitting, setSubmitting] = useState(false);
  const [formNote, setFormNote] = useState<Note | null>(null);

  const handleSubmit = useCallback(
    async (e: FormEvent): Promise<void> => {
      e.preventDefault();
      setFormNote(null);
      if (name.trim() === "") {
        setFormNote({
          severity: "error",
          model: validationMessage(
            "Name is required",
            "A team member needs a name.",
            "Enter the person's full name.",
          ),
        });
        return;
      }
      if (role.trim() === "") {
        setFormNote({
          severity: "error",
          model: validationMessage(
            "Role is required",
            "A team member needs a role / designation.",
            "Choose a role such as Property Manager or Cook.",
          ),
        });
        return;
      }
      if (employmentType.trim() === "") {
        setFormNote({
          severity: "error",
          model: validationMessage(
            "Employment type is required",
            "Select how this person is employed.",
            "Choose Full-time, Part-time, Contract or Casual.",
          ),
        });
        return;
      }
      if (joiningDate.trim() === "") {
        setFormNote({
          severity: "error",
          model: validationMessage(
            "Joining date is required",
            "A team member needs a joining date.",
            "Pick the date this person joined.",
          ),
        });
        return;
      }
      const baseSalaryMinor = parseMoneyToMinor(baseSalary);
      if (baseSalaryMinor === null || baseSalaryMinor < 0) {
        setFormNote({
          severity: "error",
          model: validationMessage(
            "Monthly base salary is invalid",
            "The base salary is not a valid non-negative amount.",
            "Enter a non-negative amount such as 25000.",
          ),
        });
        return;
      }
      let commissionPctNum: number | undefined;
      let commissionValueMinor: number | undefined;
      if (commissionType === "pct") {
        const c = Number(commissionPct);
        if (commissionPct.trim() === "" || !Number.isFinite(c) || c < 0) {
          setFormNote({
            severity: "error",
            model: validationMessage(
              "Commission % is invalid",
              "The commission percentage is not a valid non-negative number.",
              "Enter a percentage such as 5.",
            ),
          });
          return;
        }
        commissionPctNum = c;
      } else if (commissionType === "fixed") {
        const v = parseMoneyToMinor(commissionValue);
        if (v === null || v < 0) {
          setFormNote({
            severity: "error",
            model: validationMessage(
              "Commission value is invalid",
              "The fixed commission is not a valid non-negative amount.",
              "Enter a non-negative amount such as 2000.",
            ),
          });
          return;
        }
        commissionValueMinor = v;
      }
      setSubmitting(true);
      try {
        const result = await apiAccountsAddPayroll({
          ...(editing !== null ? { id: editing.id } : {}),
          ...(prop !== "" ? { propertyId: prop } : {}),
          name: name.trim(),
          baseSalaryMinor,
          role: role.trim(),
          employmentType,
          active,
          commissionType,
          ...(commissionPctNum !== undefined ? { commissionPct: commissionPctNum } : {}),
          ...(commissionValueMinor !== undefined ? { commissionValueMinor } : {}),
          paymentMethod,
          joiningDate,
          ...(mobile.trim() !== "" ? { mobile: mobile.trim() } : {}),
          ...(email.trim() !== "" ? { email: email.trim() } : {}),
          ...(accountHolder.trim() !== "" ? { accountHolder: accountHolder.trim() } : {}),
          ...(bankName.trim() !== "" ? { bankName: bankName.trim() } : {}),
          ...(accountNumber.trim() !== "" ? { accountNumber: accountNumber.trim() } : {}),
          ...(ifsc.trim() !== "" ? { ifsc: ifsc.trim() } : {}),
          ...(upiId.trim() !== "" ? { upiId: upiId.trim() } : {}),
          ...(pan.trim() !== "" ? { pan: pan.trim() } : {}),
          ...(notes.trim() !== "" ? { notes: notes.trim() } : {}),
        });
        const err = asError(result);
        if (err !== null) {
          setFormNote({ severity: "error", model: toUserMessage(err) });
          return;
        }
        onSaved("Team member saved.");
      } catch (caught) {
        setFormNote({
          severity: "error",
          model: validationMessage(
            "Could not save the team member",
            caught instanceof Error ? caught.message : "The request failed.",
            "Check your connection and try again.",
          ),
        });
      } finally {
        setSubmitting(false);
      }
    },
    [
      name, mobile, email, role, prop, employmentType, joiningDate, baseSalary,
      commissionType, commissionPct, commissionValue, paymentMethod,
      accountHolder, bankName, accountNumber, ifsc, upiId, pan, notes, active,
      editing, onSaved,
    ],
  );

  return (
    <div
      className="prop-drawer-scrim"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="prop-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={isEdit ? "Edit team member" : "Add team member"}
      >
        <header className="prop-drawer__header">
          <div>
            <p className="prop-drawer__eyebrow">Team member</p>
            <h2 className="prop-drawer__title">{isEdit ? "Edit team member" : "Add team member"}</h2>
          </div>
          <button
            type="button"
            className="console-btn console-btn--ghost prop-drawer__close"
            aria-label="Close"
            onClick={onClose}
          >
            ✕
          </button>
        </header>
        <div className="prop-drawer__body">
          {formNote !== null ? (
            <UserMessage message={formNote.model} severity={formNote.severity} />
          ) : null}
          <form className="console-form" noValidate onSubmit={handleSubmit}>
            <section className="prop-drawer__section">
              <h3 className="prop-drawer__section-title">Personal</h3>
              <div className="console-form__row">
                <div className="console-field">
                  <label htmlFor="staff-name">Full Name *</label>
                  <input id="staff-name" type="text" value={name} onChange={(e) => setName(e.target.value)} />
                </div>
                <div className="console-field">
                  <label htmlFor="staff-mobile">Mobile Number</label>
                  <input id="staff-mobile" type="text" value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="Optional" />
                </div>
              </div>
              <div className="console-form__row">
                <div className="console-field">
                  <label htmlFor="staff-email">Email</label>
                  <input id="staff-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Optional" />
                </div>
                <div className="console-field">
                  <label htmlFor="staff-role">Role / Designation *</label>
                  <select id="staff-role" value={role} onChange={(e) => setRole(e.target.value)}>
                    <option value="">Select a role…</option>
                    {STAFF_ROLES.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>
              </div>
            </section>

            <section className="prop-drawer__section">
              <h3 className="prop-drawer__section-title">Employment</h3>
              <div className="console-form__row">
                <div className="console-field">
                  <label htmlFor="staff-prop">Property *</label>
                  <select id="staff-prop" value={prop} onChange={(e) => setProp(e.target.value)}>
                    <option value="">Whole portfolio</option>
                    {properties.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
                <div className="console-field">
                  <label htmlFor="staff-emptype">Employment Type *</label>
                  <select
                    id="staff-emptype"
                    value={employmentType}
                    onChange={(e) => setEmploymentType(e.target.value as AccountsEmploymentType)}
                  >
                    {EMPLOYMENT_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="console-form__row">
                <div className="console-field">
                  <label htmlFor="staff-joining">Joining Date *</label>
                  <input id="staff-joining" type="date" value={joiningDate} onChange={(e) => setJoiningDate(e.target.value)} />
                </div>
                <div className="console-field">
                  <label htmlFor="staff-base">Monthly Base Salary *</label>
                  <input id="staff-base" type="text" inputMode="decimal" value={baseSalary} onChange={(e) => setBaseSalary(e.target.value)} placeholder="0.00" />
                </div>
              </div>
            </section>

            <section className="prop-drawer__section">
              <h3 className="prop-drawer__section-title">Commission</h3>
              <div className="console-form__row">
                <div className="console-field">
                  <label htmlFor="staff-commtype">Commission Type</label>
                  <select
                    id="staff-commtype"
                    value={commissionType}
                    onChange={(e) => setCommissionType(e.target.value as AccountsCommissionType)}
                  >
                    <option value="none">None</option>
                    <option value="pct">Percentage</option>
                    <option value="fixed">Fixed</option>
                  </select>
                </div>
                {commissionType === "pct" ? (
                  <div className="console-field">
                    <label htmlFor="staff-commpct">Commission %</label>
                    <input id="staff-commpct" type="number" min="0" step="0.01" value={commissionPct} onChange={(e) => setCommissionPct(e.target.value)} placeholder="e.g. 5" />
                  </div>
                ) : commissionType === "fixed" ? (
                  <div className="console-field">
                    <label htmlFor="staff-commval">Commission Value</label>
                    <input id="staff-commval" type="text" inputMode="decimal" value={commissionValue} onChange={(e) => setCommissionValue(e.target.value)} placeholder="0.00" />
                  </div>
                ) : (
                  <div className="console-field" aria-hidden="true" />
                )}
              </div>
            </section>

            <section className="prop-drawer__section">
              <h3 className="prop-drawer__section-title">Payment</h3>
              <div className="console-form__row">
                <div className="console-field">
                  <label htmlFor="staff-method">Payment Method</label>
                  <select
                    id="staff-method"
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value as AccountsStaffPaymentMethod)}
                  >
                    {STAFF_PAYMENT_METHODS.map((m) => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                </div>
                <div className="console-field">
                  <label htmlFor="staff-holder">Account Holder Name</label>
                  <input id="staff-holder" type="text" value={accountHolder} onChange={(e) => setAccountHolder(e.target.value)} placeholder="Optional" />
                </div>
              </div>
              <div className="console-form__row">
                <div className="console-field">
                  <label htmlFor="staff-bankname">Bank Name</label>
                  <input id="staff-bankname" type="text" value={bankName} onChange={(e) => setBankName(e.target.value)} placeholder="Optional" />
                </div>
                <div className="console-field">
                  <label htmlFor="staff-acctnum">Account Number</label>
                  <input id="staff-acctnum" type="text" value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)} placeholder={isEdit && editing?.accountLast4 !== null && editing?.accountLast4 !== undefined ? `•••• ${editing.accountLast4}` : "Optional"} />
                  {isEdit && editing?.accountLast4 !== null && editing?.accountLast4 !== undefined ? (
                    <p className="recon-caption">Current: •••• {editing.accountLast4}. Only the last 4 digits are stored; leave blank to keep.</p>
                  ) : (
                    <p className="recon-caption">Only the last 4 digits are stored.</p>
                  )}
                </div>
              </div>
              <div className="console-form__row">
                <div className="console-field">
                  <label htmlFor="staff-ifsc">IFSC</label>
                  <input id="staff-ifsc" type="text" value={ifsc} onChange={(e) => setIfsc(e.target.value)} placeholder="Optional" />
                </div>
                <div className="console-field">
                  <label htmlFor="staff-upi">UPI ID</label>
                  <input id="staff-upi" type="text" value={upiId} onChange={(e) => setUpiId(e.target.value)} placeholder="Optional" />
                </div>
              </div>
              <div className="console-form__row">
                <div className="console-field">
                  <label htmlFor="staff-pan">PAN</label>
                  <input id="staff-pan" type="text" value={pan} onChange={(e) => setPan(e.target.value)} placeholder="Optional" />
                </div>
                <div className="console-field">
                  <label htmlFor="staff-notes">Notes</label>
                  <input id="staff-notes" type="text" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional" />
                </div>
              </div>
              <label className="accounts-cash-denomtoggle">
                <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
                <span>Active (include in payroll)</span>
              </label>
            </section>

            <div className="prop-drawer__footer">
              <button type="button" className="console-btn console-btn--ghost" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="console-btn" disabled={submitting}>
                {submitting ? "Saving…" : isEdit ? "Save changes" : "Add team member"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Team sub-tab
// ---------------------------------------------------------------------------

function TeamSub({
  propertyId,
  currency,
  properties,
  refreshToken,
  onRefresh,
  onViewHistory,
}: {
  readonly propertyId: string | undefined;
  readonly currency: string;
  readonly properties: readonly Property[];
  readonly refreshToken: number;
  readonly onRefresh: (message: string) => void;
  readonly onViewHistory: (personId: string) => void;
}): ReactElement {
  const [summary, setSummary] = useState<AccountsStaffSummary | null>(null);
  const [people, setPeople] = useState<readonly AccountsPayrollRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [note, setNote] = useState<Note | null>(null);
  const [search, setSearch] = useState<string>("");
  const [propFilter, setPropFilter] = useState<string>("all");
  const [roleFilter, setRoleFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [drawerEditing, setDrawerEditing] = useState<AccountsPayrollRow | null>(null);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);

  const cur = summary?.currency || currency || FALLBACK_CURRENCY;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNote(null);
    void (async (): Promise<void> => {
      try {
        const [sum, rows] = await Promise.all([
          apiAccountsStaffSummary(propertyId),
          apiAccountsPayroll(propertyId),
        ]);
        if (cancelled) return;
        const err = asError(sum);
        if (err !== null) {
          setNote({ severity: "error", model: toUserMessage(err) });
          setSummary(null);
        } else {
          setSummary(sum as AccountsStaffSummary);
        }
        setPeople(rows);
      } catch (caught) {
        if (!cancelled) {
          setNote({
            severity: "error",
            model: validationMessage(
              "Could not load the team",
              caught instanceof Error ? caught.message : "The request failed.",
              "Check your connection and try again.",
            ),
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [propertyId, refreshToken]);

  const roles = useMemo(() => {
    const set = new Set<string>();
    for (const p of people) {
      if (p.role.trim() !== "") set.add(p.role);
    }
    return [...set].sort();
  }, [people]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return people.filter((p) => {
      if (q !== "") {
        const hay = `${p.name} ${p.role} ${p.mobile ?? ""} ${p.email ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (propFilter !== "all") {
        if (!(p.propertyId === propFilter || p.propertyId === null)) return false;
      }
      if (roleFilter !== "all" && p.role !== roleFilter) return false;
      if (statusFilter === "active" && !p.active) return false;
      if (statusFilter === "inactive" && p.active) return false;
      return true;
    });
  }, [people, search, propFilter, roleFilter, statusFilter]);

  const toggleActive = useCallback(
    async (row: AccountsPayrollRow): Promise<void> => {
      try {
        const result = await apiAccountsSetPayrollActive({ id: row.id, active: !row.active });
        const err = asError(result);
        if (err !== null) {
          setNote({ severity: "error", model: toUserMessage(err) });
          return;
        }
        onRefresh(row.active ? "Team member deactivated." : "Team member activated.");
      } catch (caught) {
        setNote({
          severity: "error",
          model: validationMessage(
            "Could not update the team member",
            caught instanceof Error ? caught.message : "The request failed.",
            "Check your connection and try again.",
          ),
        });
      }
    },
    [onRefresh],
  );

  const openAdd = useCallback((): void => {
    setDrawerEditing(null);
    setDrawerOpen(true);
  }, []);

  const openEdit = useCallback((row: AccountsPayrollRow): void => {
    setDrawerEditing(row);
    setDrawerOpen(true);
  }, []);

  const handleSaved = useCallback(
    (message: string): void => {
      setDrawerOpen(false);
      setDrawerEditing(null);
      onRefresh(message);
    },
    [onRefresh],
  );

  const propertyName = useCallback(
    (row: AccountsPayrollRow): string => {
      if (row.propertyId === null) return "Whole portfolio";
      return properties.find((p) => p.id === row.propertyId)?.name ?? "—";
    },
    [properties],
  );

  return (
    <>
      {note !== null ? <UserMessage message={note.model} severity={note.severity} /> : null}

      {loading ? (
        <p className="console-loading" role="status" aria-live="polite">
          Loading team…
        </p>
      ) : (
        <>
          <div className="console-card-grid accounts-staff-cards">
            <div className="console-card console-card--gold accounts-kpi accounts-kpi--hero">
              <p className="console-card__label">Total Staff</p>
              <p className="console-card__value">{summary?.totalStaff ?? 0}</p>
              <p className="console-card__hint">People on the books</p>
            </div>
            <div className="console-card accounts-kpi">
              <p className="console-card__label">Active Staff</p>
              <p className="console-card__value">{summary?.activeStaff ?? 0}</p>
              <p className="console-card__hint">Included in payroll</p>
            </div>
            <div className="console-card accounts-kpi">
              <p className="console-card__label">Monthly Salary Cost</p>
              <MoneyValue minor={Number(summary?.monthlySalaryCostMinor ?? "0")} currency={cur} />
              <p className="console-card__hint">Active base salaries</p>
            </div>
            <div className="console-card accounts-kpi">
              <p className="console-card__label">Pending Payroll</p>
              <MoneyValue minor={Number(summary?.pendingPayrollMinor ?? "0")} currency={cur} />
              <p className="console-card__hint">Generated but unpaid</p>
            </div>
          </div>

          <section className="console-panel" aria-label="Team">
            <div className="accounts-panel-head">
              <h2 className="console-panel__title">Team</h2>
              <div className="accounts-panel-head__actions">
                <button type="button" className="console-btn" onClick={openAdd}>
                  + Add Team Member
                </button>
              </div>
            </div>

            <div className="accounts-invcontrols">
              <div className="console-field accounts-invcontrols__search">
                <label htmlFor="staff-search">Search</label>
                <input
                  id="staff-search"
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Name, role, mobile or email"
                />
              </div>
              <div className="console-field">
                <label htmlFor="staff-propfilter">Property</label>
                <select id="staff-propfilter" value={propFilter} onChange={(e) => setPropFilter(e.target.value)}>
                  <option value="all">All properties</option>
                  {properties.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div className="console-field">
                <label htmlFor="staff-rolefilter">Role</label>
                <select id="staff-rolefilter" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
                  <option value="all">All roles</option>
                  {roles.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>
              <div className="console-field">
                <label htmlFor="staff-statusfilter">Status</label>
                <select id="staff-statusfilter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <option value="all">All</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>

            {people.length === 0 ? (
              <div className="console-empty">
                <p>No team members yet</p>
                <p className="recon-caption">
                  Add your property manager, cooks, housekeeping, security and other staff to run monthly payroll.
                </p>
                <button type="button" className="console-btn" onClick={openAdd}>
                  + Add Team Member
                </button>
              </div>
            ) : filtered.length === 0 ? (
              <p className="console-empty">No team members match these filters.</p>
            ) : (
              <table className="console-table">
                <caption className="console-sr-only">Team members and payroll setup</caption>
                <thead>
                  <tr>
                    <th scope="col">Employee</th>
                    <th scope="col">Role</th>
                    <th scope="col">Property</th>
                    <th scope="col">Employment Type</th>
                    <th scope="col">Monthly Salary</th>
                    <th scope="col">Additional / Commission</th>
                    <th scope="col">Payment Method</th>
                    <th scope="col">Status</th>
                    <th scope="col" aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => {
                    const additional =
                      r.commissionType === "pct"
                        ? `${r.commissionPct}% commission`
                        : r.commissionType === "fixed"
                          ? formatMoney(Number(r.commissionValueMinor), cur)
                          : "—";
                    return (
                      <tr key={r.id}>
                        <td>
                          <strong>{r.name}</strong>
                          <span className="accounts-staff-sub">
                            {r.role}
                            {r.mobile !== null && r.mobile.trim() !== "" ? ` · ${r.mobile}` : ""}
                          </span>
                        </td>
                        <td>{r.role}</td>
                        <td>{propertyName(r)}</td>
                        <td>{employmentTypeLabel(r.employmentType)}</td>
                        <td className="recon-num">{formatMoney(Number(r.baseSalaryMinor), cur)}</td>
                        <td className="recon-num">{additional}</td>
                        <td>{staffPaymentMethodLabel(r.paymentMethod)}</td>
                        <td>
                          {r.active ? (
                            <span className="avos-status avos-status--ok">Active</span>
                          ) : (
                            <span className="avos-status avos-status--pending">Inactive</span>
                          )}
                        </td>
                        <td>
                          <div className="accounts-rowactions">
                            <button type="button" className="console-btn console-btn--ghost" onClick={() => openEdit(r)}>
                              View
                            </button>
                            <RowMenu
                              actions={[
                                { key: "view", label: "View", onClick: () => openEdit(r) },
                                { key: "edit", label: "Edit", onClick: () => openEdit(r) },
                                {
                                  key: "toggle",
                                  label: r.active ? "Deactivate" : "Activate",
                                  onClick: () => { void toggleActive(r); },
                                },
                                { key: "history", label: "Payroll History", onClick: () => onViewHistory(r.id) },
                              ]}
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}

      {drawerOpen ? (
        <TeamMemberDrawer
          propertyId={propertyId}
          properties={properties}
          editing={drawerEditing}
          onClose={() => { setDrawerOpen(false); setDrawerEditing(null); }}
          onSaved={handleSaved}
        />
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// Run-payroll modal (preview eligible staff, adjust, then generate a run)
// ---------------------------------------------------------------------------

/** A per-person manual adjustment in the run-payroll preview (major-unit text). */
interface RunAdjust {
  readonly additional: string;
  readonly deductions: string;
}

function RunPayrollModal({
  periodMonth,
  propertyId,
  properties,
  onClose,
  onConfirmed,
}: {
  readonly periodMonth: string;
  readonly propertyId: string | undefined;
  readonly properties: readonly Property[];
  readonly onClose: () => void;
  readonly onConfirmed: (message: string) => void;
}): ReactElement {
  const [lines, setLines] = useState<readonly AccountsPayrollLine[]>([]);
  const [cur, setCur] = useState<string>(FALLBACK_CURRENCY);
  const [adjust, setAdjust] = useState<Record<string, RunAdjust>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState(false);
  const [formNote, setFormNote] = useState<Note | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFormNote(null);
    void (async (): Promise<void> => {
      try {
        const result = await apiAccountsPayrollPreview({
          periodMonth,
          ...(propertyId !== undefined ? { propertyId } : {}),
        });
        if (cancelled) return;
        const err = asError(result);
        if (err !== null) {
          setFormNote({ severity: "error", model: toUserMessage(err) });
          setLines([]);
          return;
        }
        const data = result as { lines: readonly AccountsPayrollLine[]; currency: string };
        setLines(data.lines);
        setCur(data.currency || FALLBACK_CURRENCY);
      } catch (caught) {
        if (!cancelled) {
          setFormNote({
            severity: "error",
            model: validationMessage(
              "Could not load the payroll preview",
              caught instanceof Error ? caught.message : "The request failed.",
              "Check your connection and try again.",
            ),
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [periodMonth, propertyId]);

  const setRow = useCallback((personId: string, patch: Partial<RunAdjust>): void => {
    setAdjust((prev) => {
      const current = prev[personId] ?? { additional: "", deductions: "" };
      return { ...prev, [personId]: { ...current, ...patch } };
    });
  }, []);

  const rowNet = useCallback(
    (line: AccountsPayrollLine): number => {
      const a = adjust[line.personId];
      const base = Number(line.baseSalaryMinor);
      const add =
        a !== undefined && a.additional.trim() !== ""
          ? (parseMoneyToMinor(a.additional) ?? 0)
          : Number(line.additionalMinor);
      const ded =
        a !== undefined && a.deductions.trim() !== ""
          ? (parseMoneyToMinor(a.deductions) ?? 0)
          : Number(line.deductionsMinor);
      return base + add - ded;
    },
    [adjust],
  );

  const handleConfirm = useCallback(async (): Promise<void> => {
    setFormNote(null);
    const adjustedLines = lines
      .map((line) => {
        const a = adjust[line.personId];
        if (a === undefined) return null;
        const addSet = a.additional.trim() !== "";
        const dedSet = a.deductions.trim() !== "";
        if (!addSet && !dedSet) return null;
        const additionalMinor = addSet ? parseMoneyToMinor(a.additional) : null;
        const deductionsMinor = dedSet ? parseMoneyToMinor(a.deductions) : null;
        return {
          personId: line.personId,
          ...(additionalMinor !== null && additionalMinor >= 0 ? { additionalMinor } : {}),
          ...(deductionsMinor !== null && deductionsMinor >= 0 ? { deductionsMinor } : {}),
        };
      })
      .filter((x): x is { personId: string; additionalMinor?: number; deductionsMinor?: number } => x !== null);
    setSubmitting(true);
    try {
      const result = await apiAccountsRunPayroll({
        periodMonth,
        ...(propertyId !== undefined ? { propertyId } : {}),
        ...(adjustedLines.length > 0 ? { lines: adjustedLines } : {}),
      });
      const err = asError(result);
      if (err !== null) {
        setFormNote({ severity: "error", model: toUserMessage(err) });
        return;
      }
      onConfirmed("Payroll generated.");
    } catch (caught) {
      setFormNote({
        severity: "error",
        model: validationMessage(
          "Could not generate payroll",
          caught instanceof Error ? caught.message : "The request failed.",
          "Check your connection and try again.",
        ),
      });
    } finally {
      setSubmitting(false);
    }
  }, [lines, adjust, periodMonth, propertyId, onConfirmed]);

  const scopeLabel =
    propertyId === undefined
      ? "Whole portfolio"
      : properties.find((p) => p.id === propertyId)?.name ?? "Selected property";

  return (
    <div
      className="console-modal-scrim"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="console-modal" role="dialog" aria-modal="true" aria-label="Run payroll">
        <header className="console-modal__header">
          <h2 className="console-modal__title">Run payroll — {formatPeriodMonth(periodMonth)}</h2>
          <button
            type="button"
            className="console-btn console-btn--ghost console-modal__close"
            aria-label="Close"
            onClick={onClose}
          >
            ✕
          </button>
        </header>
        <div className="console-modal__body">
          {formNote !== null ? (
            <UserMessage message={formNote.model} severity={formNote.severity} />
          ) : null}
          <p className="recon-caption">Scope: {scopeLabel}</p>
          {loading ? (
            <p className="console-loading" role="status" aria-live="polite">
              Loading eligible staff…
            </p>
          ) : lines.length === 0 ? (
            <p className="console-empty">No active staff are eligible for this month.</p>
          ) : (
            <table className="console-table">
              <caption className="console-sr-only">Eligible payroll lines</caption>
              <thead>
                <tr>
                  <th scope="col">Employee</th>
                  <th scope="col">Base Salary</th>
                  <th scope="col">Additional / Commission</th>
                  <th scope="col">Deductions</th>
                  <th scope="col">Net Salary</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line) => {
                  const a = adjust[line.personId] ?? { additional: "", deductions: "" };
                  return (
                    <tr key={line.personId}>
                      <td>{line.name}</td>
                      <td className="recon-num">{formatMoney(Number(line.baseSalaryMinor), cur)}</td>
                      <td>
                        <input
                          type="text"
                          inputMode="decimal"
                          aria-label={`Additional for ${line.name}`}
                          value={a.additional}
                          onChange={(e) => setRow(line.personId, { additional: e.target.value })}
                          placeholder={(Number(line.additionalMinor) / 100).toString()}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          inputMode="decimal"
                          aria-label={`Deductions for ${line.name}`}
                          value={a.deductions}
                          onChange={(e) => setRow(line.personId, { deductions: e.target.value })}
                          placeholder="0.00"
                        />
                      </td>
                      <td className="recon-num">{formatMoney(rowNet(line), cur)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          <p className="recon-caption">
            Generating payroll does not pay anyone — record payments below to mark salaries paid.
          </p>
          <div className="console-modal__actions">
            <button type="button" className="console-btn console-btn--ghost" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="console-btn"
              disabled={submitting || loading || lines.length === 0}
              onClick={() => { void handleConfirm(); }}
            >
              {submitting ? "Generating…" : "Confirm Payroll"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Record-payment modal (mark one payroll line paid)
// ---------------------------------------------------------------------------

function RecordPaymentModal({
  line,
  periodMonth,
  bankAccounts,
  onClose,
  onPaid,
}: {
  readonly line: AccountsPayrollLine;
  readonly periodMonth: string;
  readonly bankAccounts: readonly AccountsBankAccountRow[];
  readonly onClose: () => void;
  readonly onPaid: (message: string) => void;
}): ReactElement {
  const [paidOn, setPaidOn] = useState<string>("");
  const [method, setMethod] = useState<AccountsStaffPaymentMethod>("bank");
  const [bankAccountId, setBankAccountId] = useState<string>("");
  const [paidAccount, setPaidAccount] = useState<string>("Cash box");
  const [reference, setReference] = useState<string>("");
  const [note, setNote] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [formNote, setFormNote] = useState<Note | null>(null);

  const cur = line.currency || FALLBACK_CURRENCY;
  const activeBanks = bankAccounts.filter((b) => b.status === "active");

  const handleSubmit = useCallback(
    async (e: FormEvent): Promise<void> => {
      e.preventDefault();
      setFormNote(null);
      if (line.id === null) {
        setFormNote({
          severity: "error",
          model: validationMessage(
            "This line cannot be paid",
            "The payroll line has not been generated yet.",
            "Generate payroll before recording a payment.",
          ),
        });
        return;
      }
      if (paidOn.trim() === "") {
        setFormNote({
          severity: "error",
          model: validationMessage(
            "Payment date is required",
            "A salary payment needs a date.",
            "Pick the date the salary was paid.",
          ),
        });
        return;
      }
      if ((method === "bank" || method === "upi") && bankAccountId === "") {
        setFormNote({
          severity: "error",
          model: validationMessage(
            "Pay-from account is required",
            "Bank and UPI payments need an account to pay from.",
            "Choose an active bank account.",
          ),
        });
        return;
      }
      setSubmitting(true);
      try {
        const result = await apiAccountsPayPayroll({
          lineId: line.id,
          paidOn,
          method,
          ...(method === "bank" || method === "upi" ? { bankAccountId } : {}),
          ...(method === "cash" && paidAccount.trim() !== "" ? { paidAccount: paidAccount.trim() } : {}),
          ...(reference.trim() !== "" ? { reference: reference.trim() } : {}),
          ...(note.trim() !== "" ? { note: note.trim() } : {}),
        });
        const err = asError(result);
        if (err !== null) {
          setFormNote({ severity: "error", model: toUserMessage(err) });
          return;
        }
        onPaid("Salary payment recorded.");
      } catch (caught) {
        setFormNote({
          severity: "error",
          model: validationMessage(
            "Could not record the payment",
            caught instanceof Error ? caught.message : "The request failed.",
            "Check your connection and try again.",
          ),
        });
      } finally {
        setSubmitting(false);
      }
    },
    [line.id, paidOn, method, bankAccountId, paidAccount, reference, note, onPaid],
  );

  return (
    <div
      className="console-modal-scrim"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="console-modal console-modal--confirm" role="dialog" aria-modal="true" aria-label="Record salary payment">
        <header className="console-modal__header">
          <h2 className="console-modal__title">Record salary payment</h2>
          <button
            type="button"
            className="console-btn console-btn--ghost console-modal__close"
            aria-label="Close"
            onClick={onClose}
          >
            ✕
          </button>
        </header>
        <div className="console-modal__body">
          {formNote !== null ? (
            <UserMessage message={formNote.model} severity={formNote.severity} />
          ) : null}
          <dl className="accounts-invdetail__rows">
            <div className="accounts-invdetail__row">
              <dt>Employee</dt>
              <dd>{line.name}</dd>
            </div>
            <div className="accounts-invdetail__row">
              <dt>Payroll Month</dt>
              <dd>{formatPeriodMonth(periodMonth)}</dd>
            </div>
            <div className="accounts-invdetail__row accounts-invdetail__row--strong">
              <dt>Net Salary</dt>
              <dd className="recon-num">{formatMoney(Number(line.netMinor), cur)}</dd>
            </div>
          </dl>
          <form className="console-form" noValidate onSubmit={handleSubmit}>
            <div className="console-form__row">
              <div className="console-field">
                <label htmlFor="pay-date">Payment Date *</label>
                <input id="pay-date" type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} />
              </div>
              <div className="console-field">
                <label htmlFor="pay-method">Payment Method *</label>
                <select
                  id="pay-method"
                  value={method}
                  onChange={(e) => setMethod(e.target.value as AccountsStaffPaymentMethod)}
                >
                  {STAFF_PAYMENT_METHODS.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="console-form__row">
              {method === "cash" ? (
                <div className="console-field">
                  <label htmlFor="pay-cashbox">Pay From *</label>
                  <input id="pay-cashbox" type="text" value={paidAccount} onChange={(e) => setPaidAccount(e.target.value)} placeholder="Cash box" />
                </div>
              ) : (
                <div className="console-field">
                  <label htmlFor="pay-bank">Pay From Account *</label>
                  <select id="pay-bank" value={bankAccountId} onChange={(e) => setBankAccountId(e.target.value)}>
                    <option value="">Select an account…</option>
                    {activeBanks.map((b) => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="console-field">
                <label htmlFor="pay-ref">Transaction / UTR Reference</label>
                <input id="pay-ref" type="text" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Optional" />
              </div>
            </div>
            <div className="console-form__row">
              <div className="console-field" style={{ flex: "1 1 100%" }}>
                <label htmlFor="pay-note">Notes</label>
                <input id="pay-note" type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional" />
              </div>
            </div>
            <p className="recon-caption">
              This marks the salary Paid, books a Salaries &amp; wages expense, and updates Cash &amp; Banks — once.
            </p>
            <div className="console-modal__actions">
              <button type="button" className="console-btn console-btn--ghost" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="console-btn" disabled={submitting}>
                {submitting ? "Saving…" : "Record Payment"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Payroll sub-tab
// ---------------------------------------------------------------------------

function PayrollSub({
  propertyId,
  currency,
  properties,
  bankAccounts,
  defaultMonth,
  refreshToken,
  onRefresh,
}: {
  readonly propertyId: string | undefined;
  readonly currency: string;
  readonly properties: readonly Property[];
  readonly bankAccounts: readonly AccountsBankAccountRow[];
  readonly defaultMonth: string;
  readonly refreshToken: number;
  readonly onRefresh: (message: string) => void;
}): ReactElement {
  const [month, setMonth] = useState<string>(defaultMonth);
  const [scope, setScope] = useState<string>(propertyId ?? "");
  const [run, setRun] = useState<AccountsPayrollRun | null>(null);
  const [lines, setLines] = useState<readonly AccountsPayrollLine[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [note, setNote] = useState<Note | null>(null);
  const [runOpen, setRunOpen] = useState<boolean>(false);
  const [payLine, setPayLine] = useState<AccountsPayrollLine | null>(null);

  const scopeId = scope === "" ? undefined : scope;
  const cur = run?.currency || currency || FALLBACK_CURRENCY;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNote(null);
    void (async (): Promise<void> => {
      try {
        const runs = await apiAccountsPayrollRuns({
          periodMonth: month,
          ...(scopeId !== undefined ? { propertyId: scopeId } : {}),
        });
        if (cancelled) return;
        const match =
          runs.find((r) => r.periodMonth.slice(0, 7) === month && (scopeId === undefined ? r.propertyId === null : r.propertyId === scopeId)) ??
          runs.find((r) => r.periodMonth.slice(0, 7) === month) ??
          null;
        setRun(match);
        if (match !== null) {
          const rows = await apiAccountsPayrollRunLines(match.id);
          if (!cancelled) setLines(rows);
        } else {
          setLines([]);
        }
      } catch (caught) {
        if (!cancelled) {
          setNote({
            severity: "error",
            model: validationMessage(
              "Could not load payroll",
              caught instanceof Error ? caught.message : "The request failed.",
              "Check your connection and try again.",
            ),
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [month, scopeId, refreshToken]);

  const handleConfirmed = useCallback(
    (message: string): void => {
      setRunOpen(false);
      onRefresh(message);
    },
    [onRefresh],
  );

  const handlePaid = useCallback(
    (message: string): void => {
      setPayLine(null);
      onRefresh(message);
    },
    [onRefresh],
  );

  return (
    <>
      {note !== null ? <UserMessage message={note.model} severity={note.severity} /> : null}

      <div className="accounts-invcontrols">
        <div className="console-field">
          <label htmlFor="payroll-month">Payroll Month</label>
          <input id="payroll-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
        </div>
        <div className="console-field">
          <label htmlFor="payroll-scope">Property</label>
          <select id="payroll-scope" value={scope} onChange={(e) => setScope(e.target.value)}>
            <option value="">Whole portfolio</option>
            {properties.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
        <div className="console-field accounts-invcontrols__period">
          <button type="button" className="console-btn" onClick={() => setRunOpen(true)}>
            Run Payroll
          </button>
        </div>
      </div>

      {loading ? (
        <p className="console-loading" role="status" aria-live="polite">
          Loading payroll…
        </p>
      ) : run === null ? (
        <div className="console-empty">
          <p>No payroll generated for {formatPeriodMonth(month)}</p>
          <button type="button" className="console-btn" onClick={() => setRunOpen(true)}>
            Run Payroll
          </button>
        </div>
      ) : (
        <>
          <div className="console-card-grid accounts-staff-cards">
            <div className="console-card console-card--gold accounts-kpi accounts-kpi--hero">
              <p className="console-card__label">Total Payroll</p>
              <MoneyValue minor={Number(run.totalMinor)} currency={cur} />
              <p className="console-card__hint">Net salaries this run</p>
            </div>
            <div className="console-card accounts-kpi">
              <p className="console-card__label">Base Salaries</p>
              <MoneyValue minor={Number(run.baseMinor)} currency={cur} />
              <p className="console-card__hint">Before adjustments</p>
            </div>
            <div className="console-card accounts-kpi">
              <p className="console-card__label">Additional / Commission</p>
              <MoneyValue minor={Number(run.additionalMinor)} currency={cur} />
              <p className="console-card__hint">Extra earnings</p>
            </div>
            <div className="console-card accounts-kpi">
              <p className="console-card__label">Pending Payment</p>
              <MoneyValue minor={Number(run.pendingMinor)} currency={cur} />
              <p className="console-card__hint">Not yet paid</p>
            </div>
          </div>

          <section className="console-panel" aria-label="Payroll">
            <h2 className="console-panel__title">Payroll — {formatPeriodMonth(month)}</h2>
            <table className="console-table">
              <caption className="console-sr-only">Payroll lines for the selected month</caption>
              <thead>
                <tr>
                  <th scope="col">Employee</th>
                  <th scope="col">Role</th>
                  <th scope="col">Property</th>
                  <th scope="col">Base Salary</th>
                  <th scope="col">Commission / Additional</th>
                  <th scope="col">Deductions</th>
                  <th scope="col">Net Salary</th>
                  <th scope="col">Payment Status</th>
                  <th scope="col" aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {lines.map((line) => (
                  <tr key={line.id ?? line.personId}>
                    <td>{line.name}</td>
                    <td>{line.role ?? "—"}</td>
                    <td>{line.propertyName ?? "Whole portfolio"}</td>
                    <td className="recon-num">{formatMoney(Number(line.baseSalaryMinor), cur)}</td>
                    <td className="recon-num">{formatMoney(Number(line.additionalMinor), cur)}</td>
                    <td className="recon-num">{formatMoney(Number(line.deductionsMinor), cur)}</td>
                    <td className="recon-num">{formatMoney(Number(line.netMinor), cur)}</td>
                    <td>{payrollStatusPill(line.paymentStatus)}</td>
                    <td>
                      {line.paymentStatus === "pending" ? (
                        <button
                          type="button"
                          className="console-btn console-btn--ghost"
                          disabled={line.id === null}
                          onClick={() => setPayLine(line)}
                        >
                          Record Payment
                        </button>
                      ) : (
                        <span className="recon-caption">Paid {formatDate(line.paidOn)}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </>
      )}

      {runOpen ? (
        <RunPayrollModal
          periodMonth={month}
          propertyId={scopeId}
          properties={properties}
          onClose={() => setRunOpen(false)}
          onConfirmed={handleConfirmed}
        />
      ) : null}
      {payLine !== null ? (
        <RecordPaymentModal
          line={payLine}
          periodMonth={month}
          bankAccounts={bankAccounts}
          onClose={() => setPayLine(null)}
          onPaid={handlePaid}
        />
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// Payslip detail drawer
// ---------------------------------------------------------------------------

function PayslipDetailDrawer({
  lineId,
  currency,
  onClose,
}: {
  readonly lineId: string;
  readonly currency: string;
  readonly onClose: () => void;
}): ReactElement {
  const [detail, setDetail] = useState<AccountsPayslipDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [note, setNote] = useState<Note | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNote(null);
    void (async (): Promise<void> => {
      try {
        const result = await apiAccountsPayslipDetail(lineId);
        if (cancelled) return;
        const err = asError(result);
        if (err !== null) {
          setNote({ severity: "error", model: toUserMessage(err) });
          setDetail(null);
        } else {
          setDetail(result as AccountsPayslipDetail);
        }
      } catch (caught) {
        if (!cancelled) {
          setNote({
            severity: "error",
            model: validationMessage(
              "Could not load the payslip",
              caught instanceof Error ? caught.message : "The request failed.",
              "Check your connection and try again.",
            ),
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [lineId]);

  const cur = detail?.currency || currency || FALLBACK_CURRENCY;

  const handleDownload = useCallback(async (): Promise<void> => {
    setNote(null);
    try {
      const result = await apiAccountsPayslipPdf(lineId);
      const err = asError(result);
      if (err !== null) {
        setNote({ severity: "error", model: toUserMessage(err) });
        return;
      }
      openInvoiceHtml((result as { html: string }).html);
    } catch (caught) {
      setNote({
        severity: "error",
        model: validationMessage(
          "Could not open the payslip PDF",
          caught instanceof Error ? caught.message : "The request failed.",
          "Check your connection and try again.",
        ),
      });
    }
  }, [lineId]);

  return (
    <div
      className="prop-drawer-scrim"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="prop-drawer" role="dialog" aria-modal="true" aria-label="Payslip detail">
        <header className="prop-drawer__header">
          <div>
            <p className="prop-drawer__eyebrow">Payslip</p>
            <h2 className="prop-drawer__title">
              {detail !== null ? `${detail.name} · ${formatPeriodMonth(detail.periodMonth)}` : "Payslip"}
            </h2>
          </div>
          <button
            type="button"
            className="console-btn console-btn--ghost prop-drawer__close"
            aria-label="Close"
            onClick={onClose}
          >
            ✕
          </button>
        </header>
        <div className="prop-drawer__body">
          {note !== null ? <UserMessage message={note.model} severity={note.severity} /> : null}
          {loading ? (
            <p className="console-loading" role="status" aria-live="polite">
              Loading payslip…
            </p>
          ) : detail === null ? (
            <p className="console-empty">No payslip to show.</p>
          ) : (
            <>
              <p className="prop-drawer__eyebrow">AVOS Books · {detail.propertyName ?? "Whole portfolio"}</p>

              <section className="prop-drawer__section">
                <h3 className="prop-drawer__section-title">Employee</h3>
                <dl className="accounts-invdetail__rows">
                  <div className="accounts-invdetail__row">
                    <dt>Name</dt>
                    <dd>{detail.name}</dd>
                  </div>
                  <div className="accounts-invdetail__row">
                    <dt>Employee ID</dt>
                    <dd>{detail.personId.slice(0, 8)}</dd>
                  </div>
                  <div className="accounts-invdetail__row">
                    <dt>Role</dt>
                    <dd>{detail.role ?? "—"}</dd>
                  </div>
                  <div className="accounts-invdetail__row">
                    <dt>Property</dt>
                    <dd>{detail.propertyName ?? "Whole portfolio"}</dd>
                  </div>
                  <div className="accounts-invdetail__row">
                    <dt>Payroll Month</dt>
                    <dd>{formatPeriodMonth(detail.periodMonth)}</dd>
                  </div>
                </dl>
              </section>

              <section className="prop-drawer__section">
                <h3 className="prop-drawer__section-title">Earnings</h3>
                <table className="console-table accounts-invdetail__lines">
                  <thead>
                    <tr>
                      <th scope="col">Component</th>
                      <th scope="col">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Base Salary</td>
                      <td className="recon-num">{formatMoney(Number(detail.baseSalaryMinor), cur)}</td>
                    </tr>
                    <tr>
                      <td>Additional / Commission</td>
                      <td className="recon-num">{formatMoney(Number(detail.additionalMinor), cur)}</td>
                    </tr>
                  </tbody>
                </table>
              </section>

              <section className="prop-drawer__section">
                <h3 className="prop-drawer__section-title">Totals</h3>
                <dl className="accounts-invdetail__rows accounts-invdetail__rows--totals">
                  <div className="accounts-invdetail__row">
                    <dt>Gross</dt>
                    <dd className="recon-num">{formatMoney(Number(detail.grossMinor), cur)}</dd>
                  </div>
                  <div className="accounts-invdetail__row">
                    <dt>Deductions</dt>
                    <dd className="recon-num">−{formatMoney(Number(detail.deductionsMinor), cur)}</dd>
                  </div>
                  <div className="accounts-invdetail__row accounts-invdetail__row--strong">
                    <dt>Net</dt>
                    <dd className="recon-num">{formatMoney(Number(detail.netMinor), cur)}</dd>
                  </div>
                </dl>
              </section>

              <section className="prop-drawer__section">
                <h3 className="prop-drawer__section-title">Payment</h3>
                <dl className="accounts-invdetail__rows">
                  <div className="accounts-invdetail__row">
                    <dt>Payment Method</dt>
                    <dd>{staffPaymentMethodLabel(detail.paymentMethod)}</dd>
                  </div>
                  <div className="accounts-invdetail__row">
                    <dt>Payment Date</dt>
                    <dd>{formatDate(detail.paidOn)}</dd>
                  </div>
                  <div className="accounts-invdetail__row">
                    <dt>Transaction Reference</dt>
                    <dd>{detail.paymentReference ?? "—"}</dd>
                  </div>
                </dl>
              </section>
            </>
          )}
        </div>
        <div className="prop-drawer__footer">
          <button
            type="button"
            className="console-btn console-btn--ghost"
            disabled={detail === null}
            onClick={() => { void handleDownload(); }}
          >
            Download PDF
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Payslips sub-tab
// ---------------------------------------------------------------------------

function PayslipsSub({
  propertyId,
  currency,
  properties,
  refreshToken,
  personFilter,
}: {
  readonly propertyId: string | undefined;
  readonly currency: string;
  readonly properties: readonly Property[];
  readonly refreshToken: number;
  readonly personFilter: string | null;
}): ReactElement {
  const [rows, setRows] = useState<readonly AccountsPayslipRow[]>([]);
  const [staff, setStaff] = useState<readonly AccountsPayrollRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [note, setNote] = useState<Note | null>(null);
  const [month, setMonth] = useState<string>("");
  const [propFilter, setPropFilter] = useState<string>(propertyId ?? "all");
  const [person, setPerson] = useState<string>(personFilter ?? "all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [search, setSearch] = useState<string>("");
  const [detailLineId, setDetailLineId] = useState<string | null>(null);

  // Adopt an incoming person filter (e.g. from Team → Payroll History).
  useEffect(() => {
    if (personFilter !== null) setPerson(personFilter);
  }, [personFilter]);

  // Load the staff list once for the employee filter.
  useEffect(() => {
    let cancelled = false;
    void (async (): Promise<void> => {
      try {
        const list = await apiAccountsPayroll(propertyId);
        if (!cancelled) setStaff(list);
      } catch {
        if (!cancelled) setStaff([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [propertyId, refreshToken]);

  const propScope = propFilter === "all" ? undefined : propFilter;
  const personScope = person === "all" ? undefined : person;
  const statusScope: "pending" | "paid" | undefined =
    statusFilter === "pending" ? "pending" : statusFilter === "paid" ? "paid" : undefined;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNote(null);
    void (async (): Promise<void> => {
      try {
        const list = await apiAccountsPayslips({
          ...(month.trim() !== "" ? { periodMonth: month } : {}),
          ...(propScope !== undefined ? { propertyId: propScope } : {}),
          ...(personScope !== undefined ? { personId: personScope } : {}),
          ...(statusScope !== undefined ? { paymentStatus: statusScope } : {}),
        });
        if (!cancelled) setRows(list);
      } catch (caught) {
        if (!cancelled) {
          setNote({
            severity: "error",
            model: validationMessage(
              "Could not load payslips",
              caught instanceof Error ? caught.message : "The request failed.",
              "Check your connection and try again.",
            ),
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [month, propScope, personScope, statusScope, refreshToken]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q === "") return rows;
    return rows.filter((r) => `${r.name} ${r.role ?? ""}`.toLowerCase().includes(q));
  }, [rows, search]);

  const handleDownload = useCallback(async (lineId: string): Promise<void> => {
    setNote(null);
    try {
      const result = await apiAccountsPayslipPdf(lineId);
      const err = asError(result);
      if (err !== null) {
        setNote({ severity: "error", model: toUserMessage(err) });
        return;
      }
      openInvoiceHtml((result as { html: string }).html);
    } catch (caught) {
      setNote({
        severity: "error",
        model: validationMessage(
          "Could not open the payslip PDF",
          caught instanceof Error ? caught.message : "The request failed.",
          "Check your connection and try again.",
        ),
      });
    }
  }, []);

  const handleEmail = useCallback(async (lineId: string): Promise<void> => {
    setNote(null);
    try {
      const result = await apiAccountsPayslipEmail({ lineId });
      const err = asError(result);
      if (err !== null) {
        setNote({ severity: "error", model: toUserMessage(err) });
        return;
      }
      const to = (result as { to: string }).to;
      setNote({
        severity: "status",
        model: { what: `Payslip emailed to ${to}.`, why: "The payslip was sent.", next: "" },
      });
    } catch (caught) {
      setNote({
        severity: "error",
        model: validationMessage(
          "Could not email the payslip",
          caught instanceof Error ? caught.message : "The request failed.",
          "Check your connection and try again.",
        ),
      });
    }
  }, []);

  const handleWhatsApp = useCallback(async (lineId: string): Promise<void> => {
    setNote(null);
    try {
      const result = await apiAccountsPayslipWhatsApp({ lineId });
      const err = asError(result);
      if (err !== null) {
        setNote({ severity: "error", model: toUserMessage(err) });
        return;
      }
      window.open((result as { url: string }).url, "_blank");
    } catch (caught) {
      setNote({
        severity: "error",
        model: validationMessage(
          "Could not share the payslip",
          caught instanceof Error ? caught.message : "The request failed.",
          "Check your connection and try again.",
        ),
      });
    }
  }, []);

  return (
    <>
      {note !== null ? <UserMessage message={note.model} severity={note.severity} /> : null}

      <div className="accounts-invcontrols">
        <div className="console-field accounts-invcontrols__search">
          <label htmlFor="payslip-search">Search</label>
          <input id="payslip-search" type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name or role" />
        </div>
        <div className="console-field">
          <label htmlFor="payslip-month">Month</label>
          <input id="payslip-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
        </div>
        <div className="console-field">
          <label htmlFor="payslip-prop">Property</label>
          <select id="payslip-prop" value={propFilter} onChange={(e) => setPropFilter(e.target.value)}>
            <option value="all">All properties</option>
            {properties.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
        <div className="console-field">
          <label htmlFor="payslip-person">Employee</label>
          <select id="payslip-person" value={person} onChange={(e) => setPerson(e.target.value)}>
            <option value="all">All employees</option>
            {staff.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>
        <div className="console-field">
          <label htmlFor="payslip-status">Payment Status</label>
          <select id="payslip-status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="paid">Paid</option>
          </select>
        </div>
      </div>

      {loading ? (
        <p className="console-loading" role="status" aria-live="polite">
          Loading payslips…
        </p>
      ) : filtered.length === 0 ? (
        <p className="console-empty">No payslips yet.</p>
      ) : (
        <section className="console-panel" aria-label="Payslips">
          <table className="console-table">
            <caption className="console-sr-only">Payslips</caption>
            <thead>
              <tr>
                <th scope="col">Employee</th>
                <th scope="col">Month</th>
                <th scope="col">Property</th>
                <th scope="col">Gross Salary</th>
                <th scope="col">Deductions</th>
                <th scope="col">Net Salary</th>
                <th scope="col">Payment Status</th>
                <th scope="col">Paid Date</th>
                <th scope="col" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const rc = r.currency || currency || FALLBACK_CURRENCY;
                return (
                  <tr key={r.lineId}>
                    <td>
                      <strong>{r.name}</strong>
                      {r.role !== null && r.role.trim() !== "" ? (
                        <span className="accounts-staff-sub">{r.role}</span>
                      ) : null}
                    </td>
                    <td>{formatPeriodMonth(r.periodMonth)}</td>
                    <td>{r.propertyName ?? "Whole portfolio"}</td>
                    <td className="recon-num">{formatMoney(Number(r.grossMinor), rc)}</td>
                    <td className="recon-num">{formatMoney(Number(r.deductionsMinor), rc)}</td>
                    <td className="recon-num">{formatMoney(Number(r.netMinor), rc)}</td>
                    <td>{payrollStatusPill(r.paymentStatus)}</td>
                    <td>{formatDate(r.paidOn)}</td>
                    <td>
                      <div className="accounts-rowactions">
                        <button type="button" className="console-btn console-btn--ghost" onClick={() => setDetailLineId(r.lineId)}>
                          View
                        </button>
                        <RowMenu
                          actions={[
                            { key: "view", label: "View Payslip", onClick: () => setDetailLineId(r.lineId) },
                            { key: "pdf", label: "Download PDF", onClick: () => { void handleDownload(r.lineId); } },
                            { key: "email", label: "Send by Email", onClick: () => { void handleEmail(r.lineId); } },
                            { key: "whatsapp", label: "Send by WhatsApp", onClick: () => { void handleWhatsApp(r.lineId); } },
                          ]}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      )}

      {detailLineId !== null ? (
        <PayslipDetailDrawer
          lineId={detailLineId}
          currency={currency}
          onClose={() => setDetailLineId(null)}
        />
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// Staff & Payroll tab (self-loading; hosts Team / Payroll / Payslips)
// ---------------------------------------------------------------------------

function StaffPayrollTab({
  propertyId,
  currency,
  onRefresh,
}: {
  readonly propertyId: string | undefined;
  readonly currency: string;
  readonly onRefresh: (message: string) => void;
}): ReactElement {
  const [activeSub, setActiveSub] = useState<StaffSubKey>("team");
  const [refreshToken, setRefreshToken] = useState<number>(0);
  const [properties, setProperties] = useState<readonly Property[]>([]);
  const [bankAccounts, setBankAccounts] = useState<readonly AccountsBankAccountRow[]>([]);
  const [personFilter, setPersonFilter] = useState<string | null>(null);

  // Default payroll month `YYYY-MM` from the client clock (SSR-safe: useMemo).
  const now = useMemo(() => new Date(), []);
  const defaultMonth = useMemo(
    () => `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`,
    [now],
  );

  // Load properties + bank accounts once (for the modals / drawers).
  useEffect(() => {
    let cancelled = false;
    void (async (): Promise<void> => {
      try {
        const [props, banks] = await Promise.all([
          apiAccountsProperties(),
          apiAccountsBankAccounts(propertyId),
        ]);
        if (!cancelled) {
          setProperties(props);
          setBankAccounts(banks);
        }
      } catch {
        if (!cancelled) {
          setProperties([]);
          setBankAccounts([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [propertyId, refreshToken]);

  const refresh = useCallback(
    (message: string): void => {
      setRefreshToken((t) => t + 1);
      onRefresh(message);
    },
    [onRefresh],
  );

  const viewHistory = useCallback((personId: string): void => {
    setPersonFilter(personId);
    setActiveSub("payslips");
  }, []);

  return (
    <>
      <div
        className="channel-detail__tabs accounts-subtabs accounts-staff-subtabs"
        role="tablist"
        aria-label="Staff & Payroll sections"
      >
        {STAFF_SUBS.map((s) => (
          <button
            key={s.key}
            type="button"
            role="tab"
            aria-selected={activeSub === s.key}
            className={
              activeSub === s.key
                ? "channel-detail__tab channel-detail__tab--active"
                : "channel-detail__tab"
            }
            onClick={() => setActiveSub(s.key)}
          >
            {s.label}
          </button>
        ))}
      </div>

      {activeSub === "team" ? (
        <TeamSub
          propertyId={propertyId}
          currency={currency}
          properties={properties}
          refreshToken={refreshToken}
          onRefresh={refresh}
          onViewHistory={viewHistory}
        />
      ) : null}
      {activeSub === "payroll" ? (
        <PayrollSub
          propertyId={propertyId}
          currency={currency}
          properties={properties}
          bankAccounts={bankAccounts}
          defaultMonth={defaultMonth}
          refreshToken={refreshToken}
          onRefresh={refresh}
        />
      ) : null}
      {activeSub === "payslips" ? (
        <PayslipsSub
          propertyId={propertyId}
          currency={currency}
          properties={properties}
          refreshToken={refreshToken}
          personFilter={personFilter}
        />
      ) : null}
    </>
  );
}

// ===========================================================================
// Owner Statements tab
// ===========================================================================

/**
 * Title-case a raw stream/label key as a display fallback when it is not in
 * STREAM_LABEL (e.g. "rooms" → "Rooms"). Pure and SSR-safe.
 */
function titleCase(raw: string): string {
  if (raw.length === 0) return raw;
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

/** Human label for a revenue stream, falling back to a title-cased key. */
function streamLabel(stream: string): string {
  return STREAM_LABEL[stream as AccountsStream] ?? titleCase(stream);
}

/** The stable cost key used for the OTA / channel commission line. */
const CHANNEL_COST_KEY = "channel_commissions";

/**
 * OwnerStatementView — the detailed single-property statement (MODE A).
 *
 * A professional owner-ready P&L: four summary cards, a revenue + cost donut
 * pair, and a fully itemised statement with expandable drill-downs, a tax
 * position block and a prominent Owner's Share. Print + Download PDF actions
 * build a standalone branded HTML document (SSR-safe: window/document are only
 * touched inside click handlers).
 */
function OwnerStatementView({
  row,
  currency,
  period,
}: {
  readonly row: AccountsOwnerStatementRow;
  readonly currency: string;
  readonly period: PeriodState;
}): ReactElement {
  const cur = row.currency || currency || FALLBACK_CURRENCY;
  const money = (minor: string): string => formatMoney(Number(minor), cur);

  // Expanded drill-down rows. Stream keys prefixed "s:", cost keys "c:".
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const toggle = useCallback((key: string): void => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  // Split costs into the channel-commission line (shown under Channel/Booking
  // costs) and the operating expenses (everything else). No double-counting.
  const channelCost = row.costs.find((c) => c.key === CHANNEL_COST_KEY);
  const opexCosts = row.costs.filter((c) => c.key !== CHANNEL_COST_KEY);
  const opexTotalMinor = opexCosts.reduce((sum, c) => sum + Number(c.amountMinor), 0);

  const operatingNegative = Number(row.retainedResultMinor) < 0;

  // Lookup helpers for drill-down records.
  const streamRecords = (stream: string): AccountsOwnerStatementRow["streamDetails"][number]["records"] =>
    row.streamDetails.find((d) => d.stream === stream)?.records ?? [];
  const costRecords = (key: string): AccountsOwnerStatementRow["costDetails"][number]["records"] =>
    row.costDetails.find((d) => d.key === key)?.records ?? [];

  const handlePrint = useCallback((): void => {
    // SSR-safe: window is only read inside this click handler.
    window.print();
  }, []);

  const handleDownloadPdf = useCallback((): void => {
    // SSR-safe: the HTML is built and opened only inside this handler.
    openInvoiceHtml(buildStatementHtml(row, period, cur));
  }, [row, period, cur]);

  /** Render one statement money row (optionally emphasised subtotal / negative). */
  const plainRow = (
    label: string,
    amountMinor: string,
    opts?: { subtotal?: boolean; negative?: boolean },
  ): ReactElement => (
    <div
      className={
        opts?.subtotal === true
          ? "accounts-stmt__row accounts-stmt__row--subtotal"
          : "accounts-stmt__row"
      }
    >
      <span className="accounts-stmt__row-label">{label}</span>
      <span
        className={
          opts?.negative === true
            ? "accounts-stmt__row-amount accounts-stmt__neg"
            : "accounts-stmt__row-amount"
        }
      >
        {money(amountMinor)}
      </span>
    </div>
  );

  /** Render an expandable row + its drill-down when open and records exist. */
  const expandableRow = (
    rowKey: string,
    label: string,
    amountMinor: string,
    records: AccountsOwnerStatementRow["streamDetails"][number]["records"],
    opts?: { negative?: boolean },
  ): ReactElement => {
    const hasRecords = records.length > 0;
    const isOpen = expanded.has(rowKey);
    return (
      <div key={rowKey}>
        {hasRecords ? (
          <button
            type="button"
            className="accounts-stmt__row-toggle"
            aria-expanded={isOpen}
            onClick={() => toggle(rowKey)}
          >
            <span className="accounts-stmt__row-label">
              <span
                className={
                  isOpen
                    ? "accounts-stmt__caret accounts-stmt__caret--open"
                    : "accounts-stmt__caret"
                }
                aria-hidden="true"
              >
                ▸
              </span>
              {label}
            </span>
            <span
              className={
                opts?.negative === true
                  ? "accounts-stmt__row-amount accounts-stmt__neg"
                  : "accounts-stmt__row-amount"
              }
            >
              {money(amountMinor)}
            </span>
          </button>
        ) : (
          plainRow(label, amountMinor, opts)
        )}
        {hasRecords && isOpen ? (
          <div className="accounts-stmt__drill">
            {records.map((rec, i) => (
              <div className="accounts-stmt__drill-row" key={`${rowKey}-rec-${i}`}>
                <span>
                  {rec.label}
                  {rec.sublabel !== null && rec.sublabel !== "" ? (
                    <span className="accounts-stmt__drill-sub"> · {rec.sublabel}</span>
                  ) : null}
                </span>
                <span className="accounts-stmt__row-amount">{money(rec.amountMinor)}</span>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    );
  };

  const revenueDonutRows = row.streams.map((s) => ({
    label: streamLabel(s.stream),
    netMinor: s.netMinor,
  }));
  const costDonutRows = row.costs.map((c) => ({
    label: c.label,
    netMinor: c.amountMinor,
  }));

  return (
    <section
      className="console-panel accounts-owner"
      aria-label={`Owner statement — ${row.propertyName ?? "property"}`}
    >
      {/* ---- Header: property name + Print / Download actions ---- */}
      <div className="accounts-panel-head">
        <h2 className="console-panel__title">{row.propertyName ?? "Property"}</h2>
        <div className="accounts-panel-head__actions">
          <button type="button" className="console-btn console-btn--ghost" onClick={handlePrint}>
            Print statement
          </button>
          <button type="button" className="console-btn" onClick={handleDownloadPdf}>
            Download PDF
          </button>
        </div>
      </div>

      {/* ---- Four summary cards ---- */}
      <div className="console-card-grid accounts-owner-cards">
        <div className="console-card accounts-kpi">
          <p className="console-card__label">Gross revenue</p>
          <MoneyValue minor={Number(row.grossExGstMinor)} currency={cur} />
        </div>
        <div className="console-card accounts-kpi">
          <p className="console-card__label">Total costs</p>
          <MoneyValue minor={Number(row.totalCostsMinor)} currency={cur} />
        </div>
        <div
          className={
            operatingNegative
              ? "console-card accounts-kpi accounts-kpi--loss"
              : "console-card accounts-kpi"
          }
        >
          <p className="console-card__label">Operating result</p>
          <MoneyValue minor={Number(row.retainedResultMinor)} currency={cur} />
        </div>
        <div className="console-card console-card--gold accounts-kpi accounts-kpi--hero">
          <p className="console-card__label">Owner&rsquo;s share</p>
          <MoneyValue minor={Number(row.ownerShareMinor)} currency={cur} />
        </div>
      </div>

      {/* ---- Revenue + Cost breakdown donuts ---- */}
      <div className="accounts-owner-charts">
        <section className="console-panel accounts-dash-panel" aria-label="Revenue breakdown">
          <h2 className="console-panel__title">Revenue breakdown</h2>
          <RevenueStreamDonut
            rows={revenueDonutRows}
            totalLabel={formatMoney(Number(row.grossExGstMinor), cur)}
          />
        </section>
        <section className="console-panel accounts-dash-panel" aria-label="Cost breakdown">
          <h2 className="console-panel__title">Cost breakdown</h2>
          <RevenueStreamDonut
            rows={costDonutRows}
            totalLabel={formatMoney(Number(row.totalCostsMinor), cur)}
          />
        </section>
      </div>

      {/* ---- Detailed statement ---- */}
      <section className="console-panel accounts-dash-panel" aria-label="Detailed statement">
        <h2 className="console-panel__title">Detailed statement</h2>
        <div className="accounts-stmt">
          {/* REVENUE */}
          <div className="accounts-stmt__section">
            <p className="accounts-stmt__heading">Revenue</p>
            {row.streams.map((s) =>
              expandableRow(
                `s:${s.stream}`,
                streamLabel(s.stream),
                s.netMinor,
                streamRecords(s.stream),
              ),
            )}
            {plainRow("Gross revenue", row.grossExGstMinor, { subtotal: true })}
          </div>

          {/* CHANNEL / BOOKING COSTS */}
          <div className="accounts-stmt__section">
            <p className="accounts-stmt__heading">Channel / booking costs</p>
            {channelCost !== undefined ? (
              expandableRow(
                `c:${channelCost.key}`,
                "OTA / channel commission",
                channelCost.amountMinor,
                costRecords(channelCost.key),
                { negative: Number(channelCost.amountMinor) < 0 },
              )
            ) : (
              <p className="accounts-stmt__muted">No channel costs in this period.</p>
            )}
          </div>

          {/* OPERATING EXPENSES */}
          <div className="accounts-stmt__section">
            <p className="accounts-stmt__heading">Operating expenses</p>
            {opexCosts.length === 0 ? (
              <p className="accounts-stmt__muted">No operating expenses in this period.</p>
            ) : (
              opexCosts.map((c) =>
                expandableRow(
                  `c:${c.key}`,
                  c.label,
                  c.amountMinor,
                  costRecords(c.key),
                  { negative: Number(c.amountMinor) < 0 },
                ),
              )
            )}
            {plainRow("Total operating costs", String(opexTotalMinor), { subtotal: true })}
          </div>

          {/* TAX POSITION */}
          <div className="accounts-stmt__section">
            <p className="accounts-stmt__heading">Tax position</p>
            {plainRow("Output GST", row.gstCollectedMinor)}
            {plainRow("Eligible ITC", row.itcMinor)}
            {plainRow("Net GST payable", row.netGstPayableMinor, { subtotal: true })}
            <p className="accounts-stmt__caption">Uses existing GST calculations.</p>
          </div>

          {/* OPERATING RESULT */}
          <div className="accounts-stmt__section">
            <p className="accounts-stmt__heading">Operating result</p>
            {plainRow("Gross revenue", row.grossExGstMinor)}
            {plainRow(
              "Less total costs",
              String(-Number(row.totalCostsMinor)),
              { negative: true },
            )}
            {plainRow("Operating result", row.retainedResultMinor, {
              subtotal: true,
              negative: operatingNegative,
            })}
          </div>

          {/* OWNER SETTLEMENT */}
          <div className="accounts-stmt__section">
            <p className="accounts-stmt__heading">Owner settlement</p>
            {plainRow("Retained operating result", row.retainedResultMinor)}
            {plainRow(`Management fee (${row.managementFeePct}%)`, row.managementFeeMinor)}
            <div
              className={
                Number(row.ownerShareMinor) < 0
                  ? "accounts-owner-share accounts-owner-share--loss"
                  : "accounts-owner-share"
              }
            >
              <span className="accounts-owner-share__label">Owner&rsquo;s share</span>
              <span className="accounts-owner-share__value">{money(row.ownerShareMinor)}</span>
            </div>
          </div>
        </div>
      </section>
    </section>
  );
}

/**
 * Build a self-contained branded HTML owner statement for print / save-as-PDF.
 * Returns a plain string (SSR-safe: it constructs no clock value and touches no
 * DOM); the caller opens it inside a click handler via {@link openInvoiceHtml}.
 */
function buildStatementHtml(
  row: AccountsOwnerStatementRow,
  period: PeriodState,
  cur: string,
): string {
  const esc = (s: string): string =>
    s
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  const inr = (minor: string | number): string => {
    const major = Number(minor) / 100;
    try {
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: cur,
        maximumFractionDigits: 0,
      }).format(major);
    } catch {
      return `${cur} ${major.toFixed(0)}`;
    }
  };

  // Period label — append the resolved dates for a custom range.
  let periodLabel = period.label;
  if (period.preset === "custom" && period.from !== undefined && period.to !== undefined) {
    periodLabel = `${period.label} (${period.from} – ${period.to})`;
  }

  const channelCost = row.costs.find((c) => c.key === CHANNEL_COST_KEY);
  const opexCosts = row.costs.filter((c) => c.key !== CHANNEL_COST_KEY);
  const opexTotal = opexCosts.reduce((sum, c) => sum + Number(c.amountMinor), 0);

  const line = (label: string, amount: string | number, strong = false): string =>
    `<tr class="${strong ? "strong" : ""}"><td>${esc(label)}</td><td class="num">${esc(inr(amount))}</td></tr>`;

  const streamRows = row.streams
    .map((s) => line(streamLabel(s.stream), s.netMinor))
    .join("");
  const opexRows = opexCosts.map((c) => line(c.label, c.amountMinor)).join("");

  const negOwner = Number(row.ownerShareMinor) < 0;

  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8" />
<title>Owner statement — ${esc(row.propertyName ?? "Property")}</title>
<style>
  :root { --forest:#172B8A; --gold:#1D35A9; --clay:#B42318; --rule:#EAECF0; --muted:#667085; }
  * { box-sizing: border-box; }
  body { font-family: Georgia, "Times New Roman", serif; color:#1c1c19; margin:0; padding:2.5rem; background:#fff; }
  h1 { font-size:1.4rem; margin:0 0 .25rem; color:var(--forest); letter-spacing:.02em; }
  h2 { font-size:.72rem; text-transform:uppercase; letter-spacing:.14em; color:var(--muted);
       border-bottom:1px solid var(--rule); padding-bottom:.35rem; margin:1.5rem 0 .5rem; }
  .brand { font-size:.7rem; text-transform:uppercase; letter-spacing:.2em; color:var(--gold); font-weight:700; }
  .sub { color:var(--muted); margin:.15rem 0 0; font-size:.9rem; }
  table { width:100%; border-collapse:collapse; }
  td { padding:.4rem 0; border-bottom:1px solid var(--rule); font-size:.95rem; }
  td.num { text-align:right; font-variant-numeric:tabular-nums; }
  tr.strong td { font-weight:700; color:var(--forest); }
  .owner { margin-top:1.5rem; padding:1rem 1.25rem; border:1px solid var(--gold);
           box-shadow:0 0 0 1px var(--gold); border-radius:8px; display:flex;
           align-items:baseline; justify-content:space-between; }
  .owner.loss { border-color:var(--clay); box-shadow:0 0 0 1px rgba(180,35,24,.3); }
  .owner__label { font-weight:700; color:var(--forest); }
  .owner__value { font-size:1.5rem; font-weight:700; color:var(--gold); font-variant-numeric:tabular-nums; }
  .owner.loss .owner__value { color:var(--clay); }
  .caption { color:var(--muted); font-size:.8rem; margin:.35rem 0 0; }
  .noprint { margin-bottom:1.5rem; }
  @media print { .noprint { display:none; } body { padding:0; } }
  button { font:inherit; padding:.5rem 1rem; border:1px solid var(--forest); background:var(--forest);
           color:#fff; border-radius:6px; cursor:pointer; }
</style></head>
<body>
  <div class="noprint"><button type="button" onclick="window.print()">Print / Save as PDF</button></div>
  <p class="brand">AVOS Books</p>
  <h1>Owner statement</h1>
  <p class="sub">${esc(row.propertyName ?? "Property")} · ${esc(periodLabel)}</p>

  <h2>Revenue</h2>
  <table><tbody>
    ${streamRows}
    ${line("Gross revenue", row.grossExGstMinor, true)}
  </tbody></table>

  <h2>Channel / booking costs</h2>
  <table><tbody>
    ${channelCost !== undefined
      ? line("OTA / channel commission", channelCost.amountMinor)
      : `<tr><td class="caption">No channel costs in this period.</td><td></td></tr>`}
  </tbody></table>

  <h2>Operating expenses</h2>
  <table><tbody>
    ${opexRows}
    ${line("Total operating costs", opexTotal, true)}
  </tbody></table>

  <h2>Tax position</h2>
  <table><tbody>
    ${line("Output GST", row.gstCollectedMinor)}
    ${line("Eligible ITC", row.itcMinor)}
    ${line("Net GST payable", row.netGstPayableMinor, true)}
  </tbody></table>
  <p class="caption">Uses existing GST calculations.</p>

  <h2>Operating result</h2>
  <table><tbody>
    ${line("Gross revenue", row.grossExGstMinor)}
    ${line("Less total costs", -Number(row.totalCostsMinor))}
    ${line("Operating result", row.retainedResultMinor, true)}
  </tbody></table>

  <h2>Owner settlement</h2>
  <table><tbody>
    ${line("Retained operating result", row.retainedResultMinor)}
    ${line(`Management fee (${row.managementFeePct}%)`, row.managementFeeMinor)}
  </tbody></table>
  <div class="owner ${negOwner ? "loss" : ""}">
    <span class="owner__label">Owner's share</span>
    <span class="owner__value">${esc(inr(row.ownerShareMinor))}</span>
  </div>
</body></html>`;
}

/**
 * OwnersTab — self-loading Owner Statements tab.
 *
 * MODE A (a property is selected): renders the detailed OwnerStatementView.
 * MODE B (whole portfolio): four summary cards, optional aggregated donuts and
 * a per-property comparison table with a "View Statement" action that switches
 * the page-level property selector.
 *
 * SSR-safe: the period is resolved inside a mount effect; every network call
 * runs inside an effect keyed on (propertyId, period).
 */
function OwnersTab({
  propertyId,
  currency,
  properties,
  onSelectProperty,
}: {
  readonly propertyId: string | undefined;
  readonly currency: string;
  readonly properties: readonly Property[];
  readonly onSelectProperty: (id: string | undefined) => void;
}): ReactElement {
  const [rows, setRows] = useState<readonly AccountsOwnerStatementRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState<Note | null>(null);
  // Static placeholder until the mount effect resolves the real "this-month"
  // range (SSR-safe: resolvePreset reads the clock, so only in an effect).
  const [period, setPeriod] = useState<PeriodState>({
    preset: "this-month",
    from: undefined,
    to: undefined,
    label: "This month",
  });

  // Resolve the real "this month" range on mount (client-only).
  useEffect(() => {
    setPeriod(resolvePreset("this-month"));
  }, []);

  const handlePresetChange = useCallback((preset: PresetKey): void => {
    setPeriod(resolvePreset(preset));
  }, []);
  const handleCustomApply = useCallback((from: string, to: string): void => {
    setPeriod(resolvePreset("custom", from, to));
  }, []);

  // Load owner statements whenever the property scope or period changes.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNote(null);
    apiAccountsOwnerStatements(propertyId, {
      ...(period.from !== undefined ? { from: period.from } : {}),
      ...(period.to !== undefined ? { to: period.to } : {}),
    })
      .then((result) => {
        if (cancelled) return;
        setRows(result);
        setLoading(false);
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        setNote({
          severity: "error",
          model: validationMessage(
            "Could not load owner statements",
            caught instanceof Error ? caught.message : "The request failed.",
            "Check your connection and try again.",
          ),
        });
        setRows([]);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [propertyId, period]);

  const periodSelector = (
    <PeriodFilter
      period={period}
      onPresetChange={handlePresetChange}
      onCustomApply={handleCustomApply}
    />
  );

  if (loading) {
    return (
      <>
        {periodSelector}
        <p className="console-loading" role="status" aria-live="polite">
          Loading owner statements…
        </p>
      </>
    );
  }

  const noteBlock =
    note !== null ? (
      <UserMessage message={note.model} severity={note.severity} />
    ) : null;

  // ---- MODE A: a single property is selected. ----
  if (propertyId !== undefined) {
    const single = rows.find((r) => r.propertyId === propertyId) ?? rows[0];
    const selectedName =
      properties.find((p) => p.id === propertyId)?.name ?? "this property";
    if (single === undefined) {
      return (
        <>
          {periodSelector}
          {noteBlock}
          <p className="console-empty">
            No financial activity for {selectedName} in this period.
          </p>
        </>
      );
    }
    return (
      <>
        {periodSelector}
        {noteBlock}
        <OwnerStatementView row={single} currency={currency} period={period} />
      </>
    );
  }

  // ---- MODE B: whole portfolio. ----
  if (rows.length === 0) {
    return (
      <>
        {periodSelector}
        {noteBlock}
        <p className="console-empty">
          No owner statements yet. They appear once revenue, commission and
          expenses are recorded for a property.
        </p>
      </>
    );
  }

  const cur = rows[0]?.currency || currency || FALLBACK_CURRENCY;

  // Portfolio totals — simple summation of provided minor values only.
  const sum = (pick: (r: AccountsOwnerStatementRow) => string): number =>
    rows.reduce((acc, r) => acc + Number(pick(r)), 0);
  const grossTotal = sum((r) => r.grossExGstMinor);
  const costsTotal = sum((r) => r.totalCostsMinor);
  const operatingTotal = sum((r) => r.retainedResultMinor);
  const ownerTotal = sum((r) => r.ownerShareMinor);
  const operatingNegative = operatingTotal < 0;

  // Aggregate streams (by key) and costs (by label) for the portfolio donuts.
  const streamAgg = new Map<string, number>();
  const costAgg = new Map<string, number>();
  for (const r of rows) {
    for (const s of r.streams) {
      streamAgg.set(s.stream, (streamAgg.get(s.stream) ?? 0) + Number(s.netMinor));
    }
    for (const c of r.costs) {
      costAgg.set(c.label, (costAgg.get(c.label) ?? 0) + Number(c.amountMinor));
    }
  }
  const streamDonutRows = [...streamAgg.entries()].map(([stream, minor]) => ({
    label: streamLabel(stream),
    netMinor: String(minor),
  }));
  const costDonutRows = [...costAgg.entries()].map(([label, minor]) => ({
    label,
    netMinor: String(minor),
  }));

  const marginLabel = (gross: number, owner: number): string =>
    gross > 0 ? `${((owner / gross) * 100).toFixed(1)}%` : "0.0%";

  return (
    <>
      {periodSelector}
      {noteBlock}

      {/* ---- Four portfolio summary cards ---- */}
      <div className="console-card-grid accounts-owner-cards">
        <div className="console-card accounts-kpi">
          <p className="console-card__label">Gross revenue</p>
          <MoneyValue minor={grossTotal} currency={cur} />
        </div>
        <div className="console-card accounts-kpi">
          <p className="console-card__label">Total costs</p>
          <MoneyValue minor={costsTotal} currency={cur} />
        </div>
        <div
          className={
            operatingNegative
              ? "console-card accounts-kpi accounts-kpi--loss"
              : "console-card accounts-kpi"
          }
        >
          <p className="console-card__label">Operating result</p>
          <MoneyValue minor={operatingTotal} currency={cur} />
        </div>
        <div className="console-card console-card--gold accounts-kpi accounts-kpi--hero">
          <p className="console-card__label">Owner&rsquo;s share</p>
          <MoneyValue minor={ownerTotal} currency={cur} />
        </div>
      </div>

      {/* ---- Portfolio breakdown donuts ---- */}
      <div className="accounts-owner-charts">
        <section className="console-panel accounts-dash-panel" aria-label="Revenue breakdown">
          <h2 className="console-panel__title">Revenue breakdown</h2>
          <RevenueStreamDonut
            rows={streamDonutRows}
            totalLabel={formatMoney(grossTotal, cur)}
          />
        </section>
        <section className="console-panel accounts-dash-panel" aria-label="Cost breakdown">
          <h2 className="console-panel__title">Cost breakdown</h2>
          <RevenueStreamDonut
            rows={costDonutRows}
            totalLabel={formatMoney(costsTotal, cur)}
          />
        </section>
      </div>

      {/* ---- Property comparison table ---- */}
      <section className="console-panel accounts-dash-panel" aria-label="Owner statements by property">
        <h2 className="console-panel__title">Statements by property</h2>
        <table className="console-table">
          <thead>
            <tr>
              <th scope="col">Property</th>
              <th scope="col">Revenue</th>
              <th scope="col">Costs</th>
              <th scope="col">Operating result</th>
              <th scope="col">Management fee</th>
              <th scope="col">Owner&rsquo;s share</th>
              <th scope="col">Margin</th>
              <th scope="col">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const rowCur = r.currency || cur;
              const opNeg = Number(r.retainedResultMinor) < 0;
              return (
                <tr key={r.propertyId}>
                  <td>{r.propertyName ?? "Property"}</td>
                  <td className="recon-num">{formatMoney(Number(r.grossExGstMinor), rowCur)}</td>
                  <td className="recon-num">{formatMoney(Number(r.totalCostsMinor), rowCur)}</td>
                  <td className={opNeg ? "recon-num accounts-stmt__neg" : "recon-num"}>
                    {formatMoney(Number(r.retainedResultMinor), rowCur)}
                  </td>
                  <td className="recon-num">{formatMoney(Number(r.managementFeeMinor), rowCur)}</td>
                  <td className="recon-num">
                    <strong>{formatMoney(Number(r.ownerShareMinor), rowCur)}</strong>
                  </td>
                  <td className="recon-num accounts-owner-margin">
                    {marginLabel(Number(r.grossExGstMinor), Number(r.ownerShareMinor))}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="console-btn console-btn--ghost"
                      onClick={() => onSelectProperty(r.propertyId)}
                    >
                      View statement
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
    </>
  );
}

// ===========================================================================
// Accounts / Ledger tab — chart of accounts + settings
// ===========================================================================

function SettingsModal({
  propertyId,
  onClose,
  onSaved,
}: {
  readonly propertyId: string;
  readonly onClose: () => void;
  readonly onSaved: (message: string) => void;
}): ReactElement {
  const [settings, setSettings] = useState<AccountsPropertySettings | null>(null);
  const [gstSpecified, setGstSpecified] = useState<boolean>(false);
  const [managementFeePct, setManagementFeePct] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState(false);
  const [formNote, setFormNote] = useState<Note | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async (): Promise<void> => {
      try {
        const data = await apiAccountsSettings(propertyId);
        const err = asError(data);
        if (cancelled) {
          return;
        }
        if (err !== null) {
          setFormNote({ severity: "error", model: toUserMessage(err) });
        } else {
          const s = data as AccountsPropertySettings;
          setSettings(s);
          setGstSpecified(s.gstSpecified);
          setManagementFeePct(String(s.managementFeePct));
        }
      } catch (caught) {
        if (!cancelled) {
          setFormNote({
            severity: "error",
            model: validationMessage(
              "Could not load settings",
              caught instanceof Error ? caught.message : "The request failed.",
              "Check your connection and try again.",
            ),
          });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [propertyId]);

  const handleSubmit = useCallback(
    async (e: FormEvent): Promise<void> => {
      e.preventDefault();
      setFormNote(null);
      let feePct: number | undefined;
      if (managementFeePct.trim() !== "") {
        const f = Number(managementFeePct);
        if (!Number.isFinite(f) || f < 0) {
          setFormNote({
            severity: "error",
            model: validationMessage(
              "Management fee % is invalid",
              "The management fee percentage is not a valid non-negative number.",
              "Enter a percentage such as 20.",
            ),
          });
          return;
        }
        feePct = f;
      }
      setSubmitting(true);
      try {
        const result = await apiAccountsUpdateSettings({
          propertyId,
          gstSpecified,
          ...(feePct !== undefined ? { managementFeePct: feePct } : {}),
        });
        const err = asError(result);
        if (err !== null) {
          setFormNote({ severity: "error", model: toUserMessage(err) });
          return;
        }
        onSaved("Settings saved.");
      } catch (caught) {
        setFormNote({
          severity: "error",
          model: validationMessage(
            "Could not save settings",
            caught instanceof Error ? caught.message : "The request failed.",
            "Check your connection and try again.",
          ),
        });
      } finally {
        setSubmitting(false);
      }
    },
    [propertyId, gstSpecified, managementFeePct, onSaved],
  );

  return (
    <div
      className="console-modal-scrim"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="console-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Accounts settings"
      >
        <header className="console-modal__header">
          <h2 className="console-modal__title">
            Accounts settings
            {settings?.propertyName ? ` — ${settings.propertyName}` : ""}
          </h2>
          <button
            type="button"
            className="console-btn console-btn--ghost console-modal__close"
            aria-label="Close"
            onClick={onClose}
          >
            ✕
          </button>
        </header>
        <div className="console-modal__body">
          {formNote !== null ? (
            <UserMessage message={formNote.model} severity={formNote.severity} />
          ) : null}
          {loading ? (
            <p className="console-loading" role="status" aria-live="polite">
              Loading settings…
            </p>
          ) : (
            <form className="console-form" noValidate onSubmit={handleSubmit}>
              <div className="console-form__row">
                <div className="console-field console-field--checkbox">
                  <label htmlFor="set-specified">
                    <input
                      id="set-specified"
                      type="checkbox"
                      checked={gstSpecified}
                      onChange={(e) => setGstSpecified(e.target.checked)}
                    />
                    Specified premises
                  </label>
                </div>
                <div className="console-field">
                  <label htmlFor="set-fee">Management fee %</label>
                  <input
                    id="set-fee"
                    type="number"
                    min="0"
                    step="0.01"
                    value={managementFeePct}
                    onChange={(e) => setManagementFeePct(e.target.value)}
                    placeholder="e.g. 20"
                  />
                </div>
              </div>
              <p className="recon-caption">
                Specified premises charge 18% GST on rooms; management fee is
                taken from the retained operating result on owner statements.
              </p>
              <div className="console-modal__actions">
                <button
                  type="button"
                  className="console-btn console-btn--ghost"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="console-btn"
                  disabled={submitting}
                >
                  {submitting ? "Saving…" : "Save settings"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function LedgerTab({
  groups,
  loading,
  propertyId,
  onSettingsSaved,
}: {
  readonly groups: readonly AccountsChartGroup[];
  readonly loading: boolean;
  readonly propertyId: string | undefined;
  readonly onSettingsSaved: (message: string) => void;
}): ReactElement {
  const [settingsOpen, setSettingsOpen] = useState(false);

  const handleSaved = useCallback(
    (message: string): void => {
      setSettingsOpen(false);
      onSettingsSaved(message);
    },
    [onSettingsSaved],
  );

  return (
    <>
      <section className="console-panel" aria-label="Accounts settings">
        <div className="accounts-panel-head">
          <h2 className="console-panel__title">Chart of accounts</h2>
          {propertyId !== undefined ? (
            <button
              type="button"
              className="console-btn console-btn--ghost"
              onClick={() => setSettingsOpen(true)}
            >
              Settings
            </button>
          ) : null}
        </div>
        {propertyId === undefined ? (
          <p className="recon-caption">
            Select a single property in the header to edit its accounting
            settings (specified premises and management fee).
          </p>
        ) : null}
      </section>

      {loading ? (
        <p className="console-loading" role="status" aria-live="polite">
          Loading chart of accounts…
        </p>
      ) : groups.length === 0 ? (
        <p className="console-empty">
          No accounts yet. Revenue, GST, commission, expense and cash entries
          appear here as they are recorded.
        </p>
      ) : (
        groups.map((g) => (
          <section
            key={g.group}
            className="console-panel accounts-chart-group"
            aria-label={g.label}
          >
            <h2 className="console-panel__title">{g.label}</h2>
            <table className="console-table">
              <caption className="console-sr-only">{g.label} accounts</caption>
              <thead>
                <tr>
                  <th scope="col">Code</th>
                  <th scope="col">Account</th>
                  <th scope="col">Amount</th>
                </tr>
              </thead>
              <tbody>
                {g.accounts.map((a) => {
                  const cur = a.currency || FALLBACK_CURRENCY;
                  const amt = Number(a.amountMinor);
                  return (
                    <tr key={a.code}>
                      <td>{a.code}</td>
                      <td>{a.label}</td>
                      <td
                        className={
                          amt < 0 ? "recon-num accounts-ledger-neg" : "recon-num"
                        }
                      >
                        {formatMoney(amt, cur)}
                      </td>
                    </tr>
                  );
                })}
                <tr className="accounts-total-row">
                  <td colSpan={2}>Subtotal</td>
                  <td className="recon-num">
                    {formatMoney(
                      Number(g.subtotalMinor),
                      g.accounts[0]?.currency || FALLBACK_CURRENCY,
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </section>
        ))
      )}

      {settingsOpen && propertyId !== undefined ? (
        <SettingsModal
          propertyId={propertyId}
          onClose={() => setSettingsOpen(false)}
          onSaved={handleSaved}
        />
      ) : null}
    </>
  );
}

// ===========================================================================
// AccountsScreen — the eight-tab workspace
// ===========================================================================

export default function AccountsScreen(): ReactElement {
  // Property selector: undefined => whole portfolio.
  const [properties, setProperties] = useState<readonly Property[] | null>(null);
  const [propertyId, setPropertyId] = useState<string | undefined>(undefined);
  const [activeTab, setActiveTab] = useState<TabKey>("overview");
  // The shared Overview date filter (one source of truth for every widget).
  // Initialised to a static placeholder; resolved to the real "This month"
  // range on mount inside an effect so the clock is never read at render top-
  // level (SSR-safe). Custom ranges carry the user's chosen from/to.
  const [period, setPeriod] = useState<PeriodState>({
    preset: "this-month",
    from: undefined,
    to: undefined,
    label: "This month",
  });
  useEffect(() => {
    setPeriod(resolvePreset("this-month"));
  }, []);
  const handlePresetChange = useCallback((preset: PresetKey): void => {
    setPeriod(resolvePreset(preset));
  }, []);
  const handleCustomApply = useCallback((from: string, to: string): void => {
    setPeriod(resolvePreset("custom", from, to));
  }, []);

  // Per-tab data.
  const [overview, setOverview] = useState<AccountsOverviewData | null>(null);
  const [stays, setStays] = useState<readonly AccountsStayRow[]>([]);
  const [expenses, setExpenses] = useState<readonly AccountsExpenseRow[]>([]);
  const [chart, setChart] = useState<readonly AccountsChartGroup[]>([]);
  // Payroll people are shared by the invoice drawer (salesperson select) and
  // the sales-team edit modal (base salary prefill).
  const [payrollPeople, setPayrollPeople] = useState<readonly AccountsPayrollRow[]>(
    [],
  );

  const [loading, setLoading] = useState<boolean>(true);
  const [note, setNote] = useState<Note | null>(null);
  const [invoiceStay, setInvoiceStay] = useState<AccountsStayRow | null>(null);
  // Bumped to force the active tab to re-fetch after a successful mutation.
  const [refreshToken, setRefreshToken] = useState<number>(0);

  // Load the property list once (SSR-safe: inside an effect).
  useEffect(() => {
    let cancelled = false;
    void (async (): Promise<void> => {
      try {
        const list = await apiAccountsProperties();
        if (!cancelled) {
          setProperties(list);
        }
      } catch {
        if (!cancelled) {
          setProperties([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Keep the payroll people list fresh for tabs that need it (stays drawer +
  // sales team). Re-fetched on property change and after mutations.
  useEffect(() => {
    let cancelled = false;
    void (async (): Promise<void> => {
      try {
        const people = await apiAccountsPayroll(propertyId);
        if (!cancelled) {
          setPayrollPeople(people);
        }
      } catch {
        if (!cancelled) {
          setPayrollPeople([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [propertyId, refreshToken]);

  // Load the active tab's data whenever the tab, property, period, or refresh
  // changes. Effects run client-side only in this app, so reading the clock via
  // resolvePeriod here is SSR-safe.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNote(null);
    void (async (): Promise<void> => {
      try {
        switch (activeTab) {
          case "overview": {
            const periodArg =
              period.from !== undefined || period.to !== undefined
                ? {
                    ...(period.from !== undefined ? { from: period.from } : {}),
                    ...(period.to !== undefined ? { to: period.to } : {}),
                  }
                : undefined;
            const data = await apiAccountsOverview(propertyId, periodArg);
            const err = asError(data);
            if (!cancelled) {
              if (err !== null) {
                setNote({ severity: "error", model: toUserMessage(err) });
                setOverview(null);
              } else {
                setOverview(data as AccountsOverviewData);
              }
            }
            break;
          }
          case "stays": {
            const rows = await apiAccountsStays(propertyId);
            if (!cancelled) {
              setStays(rows);
            }
            break;
          }
          case "expenses": {
            const rows = await apiAccountsExpenses(propertyId);
            if (!cancelled) {
              setExpenses(rows);
            }
            break;
          }
          case "cash": {
            // The Cash & Banks tab owns its own data loading (each sub-view
            // self-loads in an effect); nothing to pre-load here.
            break;
          }
          case "vouchers": {
            // The Offers & Vouchers tab owns its own data loading (it self-loads
            // in an effect keyed on a local refresh token); nothing to pre-load
            // here.
            break;
          }
          case "salesteam": {
            // The Staff & Payroll tab owns its own data loading (each sub-view
            // self-loads in an effect keyed on a local refresh token); nothing
            // to pre-load here.
            break;
          }
          case "owners": {
            // The Owner Statements tab owns its own data loading (it self-loads
            // with its own period filter); nothing to pre-load here.
            break;
          }
          case "ledger": {
            const rows = await apiAccountsChart(propertyId);
            if (!cancelled) {
              setChart(rows);
            }
            break;
          }
          default:
            break;
        }
      } catch (caught) {
        if (!cancelled) {
          setNote({
            severity: "error",
            model: validationMessage(
              "Could not load this tab",
              caught instanceof Error ? caught.message : "The request failed.",
              "Check your connection and try again.",
            ),
          });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTab, propertyId, period, refreshToken]);

  const refreshActive = useCallback((message: string): void => {
    setNote({
      severity: "status",
      model: { what: message, why: "The books have been updated.", next: "" },
    });
    setRefreshToken((t) => t + 1);
  }, []);

  const handleInvoiceSaved = useCallback(
    (message: string): void => {
      setInvoiceStay(null);
      refreshActive(message);
    },
    [refreshActive],
  );

  const currency = overview?.currency ?? FALLBACK_CURRENCY;

  return (
    <section aria-labelledby="accounts-heading" data-console-screen="accounts">
      <div className="accounts-header">
        <div>
          <p className="console-page-eyebrow">Finance · Books</p>
          <h1 id="accounts-heading">Accounts</h1>
          <p className="console-lede">
            The financial workspace behind AVOS — invoices, expenses, cash,
            vouchers, sales-team payroll and owner statements layered on your
            reservations, ready for GST and the owner&rsquo;s payout.
          </p>
        </div>
        <div className="console-field accounts-header__selector">
          <label htmlFor="accounts-property">Property</label>
          <select
            id="accounts-property"
            value={propertyId ?? ""}
            onChange={(e) =>
              setPropertyId(e.target.value === "" ? undefined : e.target.value)
            }
          >
            <option value="">Whole portfolio</option>
            {(properties ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {note !== null ? (
        <UserMessage message={note.model} severity={note.severity} />
      ) : null}

      <div
        className="channel-detail__tabs"
        role="tablist"
        aria-label="Accounts sections"
      >
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            id={`accounts-tab-${t.key}`}
            aria-selected={activeTab === t.key}
            aria-controls="accounts-tabpanel"
            className={
              activeTab === t.key
                ? "channel-detail__tab channel-detail__tab--active"
                : "channel-detail__tab"
            }
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div
        role="tabpanel"
        id="accounts-tabpanel"
        aria-labelledby={`accounts-tab-${activeTab}`}
      >
        {activeTab === "overview" ? (
          <OverviewTab
            data={overview}
            loading={loading}
            period={period}
            onPresetChange={handlePresetChange}
            onCustomApply={handleCustomApply}
            onViewAllActivity={() => setActiveTab("ledger")}
          />
        ) : null}
        {activeTab === "stays" ? (
          <StaysTab
            rows={stays}
            loading={loading}
            onInvoice={(s) => setInvoiceStay(s)}
            onRefresh={refreshActive}
          />
        ) : null}
        {activeTab === "expenses" ? (
          <ExpensesTab
            rows={expenses}
            loading={loading}
            propertyId={propertyId}
            onCreated={refreshActive}
          />
        ) : null}
        {activeTab === "cash" ? (
          <CashTab
            propertyId={propertyId}
            currency={currency}
            onRefresh={refreshActive}
          />
        ) : null}
        {activeTab === "vouchers" ? (
          <OffersVouchersTab
            propertyId={propertyId}
            currency={currency}
            onRefresh={refreshActive}
          />
        ) : null}
        {activeTab === "salesteam" ? (
          <StaffPayrollTab
            propertyId={propertyId}
            currency={currency}
            onRefresh={refreshActive}
          />
        ) : null}
        {activeTab === "owners" ? (
          <OwnersTab
            propertyId={propertyId}
            currency={currency}
            properties={properties ?? []}
            onSelectProperty={setPropertyId}
          />
        ) : null}
        {activeTab === "ledger" ? (
          <LedgerTab
            groups={chart}
            loading={loading}
            propertyId={propertyId}
            onSettingsSaved={refreshActive}
          />
        ) : null}
      </div>

      {invoiceStay !== null ? (
        <InvoiceDrawer
          stay={invoiceStay}
          people={payrollPeople}
          onClose={() => setInvoiceStay(null)}
          onSaved={handleInvoiceSaved}
        />
      ) : null}
    </section>
  );
}
